import React, { useState, useEffect, useRef } from 'react';
import { goalEarnedTotal } from '../utils/goalPoints';
import {
  Box,
  Drawer,
  List,
  Typography,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  Paper,
  useMediaQuery,
  useTheme,
  Container,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableFooter,
  TableRow,
  TablePagination,
  TableSortLabel,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  IconButton,
  RadioGroup,
  Radio,
  Divider,
  Link
} from '@mui/material';
import RichTextEditor from '../components/RichTextEditor';
import DeleteCampaignDialog from '../components/DeleteCampaignDialog';
import CampaignDailySyncChartView from '../components/CampaignDailySyncChartView';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { formatMatchDateTime } from '../utils/matchTime';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  CardGiftcard as CardIcon,
  Campaign as CampaignIcon,
  Leaderboard as LeaderboardIcon,
  Quiz as QuizIcon,
  Assignment as BonusTaskIcon,
  CheckCircle as AcceptIcon,
  Cancel as RejectIcon,
  TrendingUp as MarketingIcon,
  Notifications as NotificationIcon,
  Email as CommunicationIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  CloudDownload as ImportIcon,
  Store as StoreIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  ShowChart as ShowChartIcon,
  ContentCopy as ContentCopyIcon,
  ArrowBack as ArrowBackIcon,
  Sync as SyncIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  PostAdd as PostAddIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getCurrencySymbol, formatCurrency, getPrimaryCurrency } from '../utils/currency';
import { getQuizPreviewHtml, quizRichContentSx, quizRichHtmlBlockSx } from '../utils/quizDisplay';
import { isTournamentCampaign, companyHasTournamentModule } from '../utils/tournamentCampaign';
import {
  formatDealerPrediction,
  hasMatchPrediction,
  pointsForPastGame,
} from '../utils/tournamentScoring';
import { downloadActiveDealersExportExcel, downloadTournamentExportExcel } from '../utils/tournamentExportExcel';
import useLeaderboards from '../hooks/useLeaderboards';
import LeaderboardsSection from '../components/LeaderboardsSection';

// Configure dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const drawerWidth = 240;
const drawerMobileWidth = 64;

/** Match server `apply_special_url_placeholder` for test emails (plain HTML string). */
function applySpecialUrlPlaceholderForTest(html, url) {
  if (!html || url == null || url === '') return html || '';
  const escAttr = (s) =>
    String(s)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  const escText = (s) =>
    String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  return html.replace(
    /\[([^\]]+)\]\{Url\}/g,
    (_, label) => `<a href="${escAttr(url)}" style="text-decoration: underline;">${escText(label)}</a>`
  );
}

const Manufacturer = () => {
  const { user, token, logout, apiCall, fetchSmodBalance, isAdminOrManager, loading, isImpersonating, originalUser, stopImpersonation, API_URL, CDN_URL } = useAuth();
  const isManager = user?.role === 'Manager';

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('manufacturerSidebarCollapsed') === 'true');
  const isNarrow = isMobile || sidebarCollapsed;
  const currentDrawerWidth = isNarrow ? drawerMobileWidth : drawerWidth;
  const [selectedTab, setSelectedTab] = useState('dashboard');

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('manufacturerSidebarCollapsed', String(next));
      return next;
    });
  };
  const [companyBrandingImage, setCompanyBrandingImage] = useState(null);
  const [companyBrandingColor, setCompanyBrandingColor] = useState(null);
  const [availableRewards, setAvailableRewards] = useState([]);
  const [rewardsByProvider, setRewardsByProvider] = useState({});
  const [companyRewardProviders, setCompanyRewardProviders] = useState([]);
  const [companyExtraModules, setCompanyExtraModules] = useState([]);
  const hasTournamentModule = companyHasTournamentModule(companyExtraModules);
  const {
    campaignsWithLeaderboards,
    leaderboardGoals,
    loadingLeaderboardGoals,
    loadingLeaderboardCampaigns,
    leaderboardSort,
    handleLeaderboardSortChange,
    getSortedLeaderboardGoals,
  } = useLeaderboards({
    apiCall,
    selectedTab,
    dealerId: null,
    enabled: hasTournamentModule,
    loading,
  });
  const [loadingRewards, setLoadingRewards] = useState(false);
  const [rewardsError, setRewardsError] = useState('');
  const [rewardsBudget, setRewardsBudget] = useState(null);
  const [loadingBudget, setLoadingBudget] = useState(false);
  const [budgetError, setBudgetError] = useState('');
  const [edenredBalance, setEdenredBalance] = useState(null);
  const [loadingEdenredBalance, setLoadingEdenredBalance] = useState(false);
  const [edenredBalanceError, setEdenredBalanceError] = useState('');
  const [edenredUrpRows, setEdenredUrpRows] = useState([]);
  const [loadingEdenredUrp, setLoadingEdenredUrp] = useState(false);
  const [edenredUrpError, setEdenredUrpError] = useState('');
  const [tremendousBalance, setTremendousBalance] = useState(null);
  const [loadingTremendousBalance, setLoadingTremendousBalance] = useState(false);
  const [tremendousBalanceError, setTremendousBalanceError] = useState('');
  const [tremendousSetupLoading, setTremendousSetupLoading] = useState(false);
  const [showTremendousCampaignDialog, setShowTremendousCampaignDialog] = useState(false);
  const [tremendousCampaignName, setTremendousCampaignName] = useState('');
  const [tremendousCampaignDescription, setTremendousCampaignDescription] = useState('');
  const [tremendousCampaignProducts, setTremendousCampaignProducts] = useState([]);
  const [creatingTremendousCampaign, setCreatingTremendousCampaign] = useState(false);
  const [tremendousProducts, setTremendousProducts] = useState([]);
  const [loadingTremendousProducts, setLoadingTremendousProducts] = useState(false);
  const [tremendousProductsError, setTremendousProductsError] = useState('');
  const [dealers, setDealers] = useState([]);
  const [loadingDealers, setLoadingDealers] = useState(false);
  const [dealersError, setDealersError] = useState('');
  const [dealersPage, setDealersPage] = useState(0);
  const [dealersRowsPerPage, setDealersRowsPerPage] = useState(25);
  const [dealersTotal, setDealersTotal] = useState(0);
  const [dealersSearch, setDealersSearch] = useState('');
  const [dealersAsmFilter, setDealersAsmFilter] = useState('');
  const [asmsForFilter, setAsmsForFilter] = useState([]);
  const [importingDealers, setImportingDealers] = useState(false);
  const [importError, setImportError] = useState('');
  const [importSuccess, setImportSuccess] = useState('');
  const [importDealersCampaign, setImportDealersCampaign] = useState('');
  const [importDealersDialogOpen, setImportDealersDialogOpen] = useState(false);
  const [updateDealersDialogOpen, setUpdateDealersDialogOpen] = useState(false);
  const [updatingDealers, setUpdatingDealers] = useState(false);
  const [updateDealersError, setUpdateDealersError] = useState('');
  const [updateDealersSuccess, setUpdateDealersSuccess] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [activationUrlsImportError, setActivationUrlsImportError] = useState('');
  const [activationUrlsImportSuccess, setActivationUrlsImportSuccess] = useState('');
  const [usersPage, setUsersPage] = useState(0);
  const [usersRowsPerPage, setUsersRowsPerPage] = useState(25);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersSearch, setUsersSearch] = useState('');
  const [usersDealerFilter, setUsersDealerFilter] = useState('');
  const [usersRoleFilter, setUsersRoleFilter] = useState('');
  const [usersAsmFilter, setUsersAsmFilter] = useState('');
  const [usersSortBy, setUsersSortBy] = useState('name');
  const [usersSortOrder, setUsersSortOrder] = useState('asc');
  const [importingActivationUrls, setImportingActivationUrls] = useState(false);
  const activationUrlsCsvInputRef = useRef(null);
  const [dealersForFilter, setDealersForFilter] = useState([]);
  const [dealersForCampaigns, setDealersForCampaigns] = useState([]);

  // Dashboard state
  const [dashboardCampaigns, setDashboardCampaigns] = useState([]);
  const [loadingDashboardCampaigns, setLoadingDashboardCampaigns] = useState(false);
  const [dashboardGoalSettings, setDashboardGoalSettings] = useState(null);
  const [dashboardMarketingItems, setDashboardMarketingItems] = useState([]);
  const [loadingDashboardMarketing, setLoadingDashboardMarketing] = useState(false);
  const [dashboardRewardsBudget, setDashboardRewardsBudget] = useState(null);
  const [loadingDashboardRewards, setLoadingDashboardRewards] = useState(false);
  const [dashboardQuizzes, setDashboardQuizzes] = useState([]);
  const [loadingDashboardQuizzes, setLoadingDashboardQuizzes] = useState(false);

  // Campaign dialog state
  const [addCampaignOpen, setAddCampaignOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [campaignFormData, setCampaignFormData] = useState({
    name: '',
    campaign_tag: '',
    description: '',
    header: '',
    type: 'POINTS',
    status: 'DRAFT',
    campaign_image: '',
    start_date: null,
    end_date: null,
    reward_ids: [],
    dealer_ids: [],
    company_extra_module_id: null,
    // Column visibility settings
    show_value_1: true,
    show_value_2: true,
    show_value_3: true,
    show_status: true,
    // Custom column labels
    value_1_label: 'Value 1',
    value_2_label: 'Value 2',
    value_3_label: 'Value 3',
    show_in_leaderboards: false
  });
  const [campaignFormErrors, setCampaignFormErrors] = useState({});
  const [submittingCampaign, setSubmittingCampaign] = useState(false);

  // Campaigns list state
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [campaignsError, setCampaignsError] = useState('');
  const [campaignsPage, setCampaignsPage] = useState(0);
  const [campaignsRowsPerPage, setCampaignsRowsPerPage] = useState(25);
  const [campaignsTotal, setCampaignsTotal] = useState(0);
  const [campaignsSearch, setCampaignsSearch] = useState('');

  // Campaign delete state
  const [deleteCampaignDialog, setDeleteCampaignDialog] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);

  // Campaign detail view state
  const [viewingCampaignDetail, setViewingCampaignDetail] = useState(false);
  const [selectedCampaignDetail, setSelectedCampaignDetail] = useState(null);
  const [loadingCampaignDetail, setLoadingCampaignDetail] = useState(false);
  const [campaignDetailError, setCampaignDetailError] = useState('');

  // Campaign sync chart view state
  const [viewingCampaignChart, setViewingCampaignChart] = useState(false);
  const [selectedCampaignChart, setSelectedCampaignChart] = useState(null);
  const [chartDays, setChartDays] = useState(28);
  const [tournamentExporting, setTournamentExporting] = useState(false);
  const [activeDealersExporting, setActiveDealersExporting] = useState(false);

  // Campaign synchronization state
  const [syncingPoints, setSyncingPoints] = useState(false);
  const [syncError, setSyncError] = useState('');
  const [syncSuccess, setSyncSuccess] = useState('');
  const [applyingLeaderboardBonuses, setApplyingLeaderboardBonuses] = useState(false);
  const [leaderboardBonusMessage, setLeaderboardBonusMessage] = useState('');
  const [leaderboardBonusError, setLeaderboardBonusError] = useState('');
  const [updatingCampaignStatus, setUpdatingCampaignStatus] = useState(false);
  const [campaignGoals, setCampaignGoals] = useState([]);
  const [goalsSortColumn, setGoalsSortColumn] = useState('points');
  const [goalsSortDirection, setGoalsSortDirection] = useState('desc');
  const [asmFilter, setAsmFilter] = useState('all');

  const [matchScoreDialogOpen, setMatchScoreDialogOpen] = useState(false);
  const [matchScoreDialogGame, setMatchScoreDialogGame] = useState(null);
  const [matchScoreEditIndex, setMatchScoreEditIndex] = useState(null);
  const [matchScoreTeamA, setMatchScoreTeamA] = useState('');
  const [matchScoreTeamB, setMatchScoreTeamB] = useState('');
  const [matchScoreSaving, setMatchScoreSaving] = useState(false);
  const [matchScoreError, setMatchScoreError] = useState('');

  const [matchPredictionsDialogOpen, setMatchPredictionsDialogOpen] = useState(false);
  const [matchPredictionsDialogGame, setMatchPredictionsDialogGame] = useState(null);
  const [matchPredictionsDialogIndex, setMatchPredictionsDialogIndex] = useState(null);

  const [manualAdjustOpen, setManualAdjustOpen] = useState(false);
  const [manualAdjustGoal, setManualAdjustGoal] = useState(null);
  const [manualAdjustValues, setManualAdjustValues] = useState({
    manual_points: '',
    manual_value_1: '',
    manual_value_2: '',
    manual_value_3: ''
  });
  const [manualAdjustSaving, setManualAdjustSaving] = useState(false);
  const [manualAdjustError, setManualAdjustError] = useState('');

  // Voucher requests state
  const [voucherRequests, setVoucherRequests] = useState([]);
  const [loadingVoucherRequests, setLoadingVoucherRequests] = useState(false);
  const [voucherRequestsError, setVoucherRequestsError] = useState('');

  // Start campaign confirmation dialog state
  const [startCampaignDialogOpen, setStartCampaignDialogOpen] = useState(false);
  const [sendStartCampaignEmails, setSendStartCampaignEmails] = useState(true);

  // New dealers email popup (Edit Campaign - RUNNING with new dealers added)
  const [newDealersEmailDialogOpen, setNewDealersEmailDialogOpen] = useState(false);
  const [sendEmailsToNewDealers, setSendEmailsToNewDealers] = useState(true);
  const [pendingCampaignSubmitData, setPendingCampaignSubmitData] = useState(null);
  const [pendingNewDealerIds, setPendingNewDealerIds] = useState([]);

  // Quiz management state
  const [quizzes, setQuizzes] = useState([]);
  const [loadingQuizzes, setLoadingQuizzes] = useState(false);
  const [quizzesError, setQuizzesError] = useState('');
  const [quizDialog, setQuizDialog] = useState(false);
  const [editingQuiz, setEditingQuiz] = useState(null);
  const [quizFormData, setQuizFormData] = useState({
    title: '',
    description: '',
    short_description: '',
    active: false,
    only_with_points: false,
    auto_on_off: false,
    auto_active_at: null,
    auto_inactive_at: null,
    reward: 0,
    reward_per_question: false,
    show_correct_answers: 'ALWAYS',
    campaign_id: null,
    questions: []
  });
  const [quizFormErrors, setQuizFormErrors] = useState({});
  const [submittingQuiz, setSubmittingQuiz] = useState(false);

  // Bonus task management state
  const [bonusTasks, setBonusTasks] = useState([]);
  const [loadingBonusTasks, setLoadingBonusTasks] = useState(false);
  const [bonusTasksError, setBonusTasksError] = useState('');
  const [bonusTaskDialog, setBonusTaskDialog] = useState(false);
  const [editingBonusTask, setEditingBonusTask] = useState(null);
  const [selectedBonusTaskDetail, setSelectedBonusTaskDetail] = useState(null);
  const [loadingBonusTaskDetail, setLoadingBonusTaskDetail] = useState(false);
  const [reviewingSubmissionId, setReviewingSubmissionId] = useState(null);
  const [bonusTaskFormData, setBonusTaskFormData] = useState({
    title: '',
    description: '',
    points: 0,
    active: false,
    campaign_id: null,
  });
  const [bonusTaskFormErrors, setBonusTaskFormErrors] = useState({});
  const [submittingBonusTask, setSubmittingBonusTask] = useState(false);

  // Campaign-specific quizzes state
  const [campaignQuizzes, setCampaignQuizzes] = useState([]);
  const [loadingCampaignQuizzes, setLoadingCampaignQuizzes] = useState(false);
  const [campaignQuizzesError, setCampaignQuizzesError] = useState('');

  // Quiz statistics state
  const [quizStatistics, setQuizStatistics] = useState({}); // keyed by quiz_id
  const [quizStatsLoading, setQuizStatsLoading] = useState({}); // keyed by quiz_id
  const [quizStatsPage, setQuizStatsPage] = useState({}); // keyed by quiz_id, default 0
  const [quizStatsRowsPerPage, setQuizStatsRowsPerPage] = useState({}); // keyed by quiz_id, default 5

  // Notification management state
  const [notificationSets, setNotificationSets] = useState([]);
  const [loadingNotificationSets, setLoadingNotificationSets] = useState(false);
  const [notificationSetsError, setNotificationSetsError] = useState('');
  const [hasGeneralSet, setHasGeneralSet] = useState(false);
  const [campaignsWithSets, setCampaignsWithSets] = useState(new Set());
  const [notificationSetDialog, setNotificationSetDialog] = useState(false);
  const [editingNotificationSet, setEditingNotificationSet] = useState(null);
  const [notificationSetFormData, setNotificationSetFormData] = useState({
    name: '',
    campaign_id: null,
    header_image: '',
    footer_image: ''
  });
  const [notificationSetFormErrors, setNotificationSetFormErrors] = useState({});
  const [submittingNotificationSet, setSubmittingNotificationSet] = useState(false);
  const [selectedNotificationSet, setSelectedNotificationSet] = useState(null);
  const [viewingTemplates, setViewingTemplates] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [loadingEmailTemplates, setLoadingEmailTemplates] = useState(false);
  const [emailTemplatesError, setEmailTemplatesError] = useState('');
  const [emailTemplateDialog, setEmailTemplateDialog] = useState(false);
  const [editingEmailTemplate, setEditingEmailTemplate] = useState(null);
  const [emailTemplateFormData, setEmailTemplateFormData] = useState({
    template_type: 'CAMPAIGN_START',
    title: '',
    subtitle: '',
    content: '',
    extra_content: '',
    custom_cta_enabled: false,
    custom_cta_title: '',
    custom_cta_url: '',
    no_cta: false
  });
  const [emailTemplateFormErrors, setEmailTemplateFormErrors] = useState({});
  const [submittingEmailTemplate, setSubmittingEmailTemplate] = useState(false);

  // Marketing management state
  const [marketingItems, setMarketingItems] = useState([]);
  const [loadingMarketingItems, setLoadingMarketingItems] = useState(false);
  const [marketingItemsError, setMarketingItemsError] = useState('');
  const [marketingItemDialog, setMarketingItemDialog] = useState(false);
  const [editingMarketingItem, setEditingMarketingItem] = useState(null);
  const [marketingItemFormData, setMarketingItemFormData] = useState({
    title: '',
    image: '',
    description: '',
    type: 'IMAGE',
    youtube_link: '',
    download_file: ''
  });
  const [marketingItemFormErrors, setMarketingItemFormErrors] = useState({});
  const [submittingMarketingItem, setSubmittingMarketingItem] = useState(false);
  const [uploadingMarketingImage, setUploadingMarketingImage] = useState(false);
  const [uploadingMarketingFile, setUploadingMarketingFile] = useState(false);

  // Email configuration state
  const [emailConfig, setEmailConfig] = useState({
    email_header_image: '',
    email_footer_image: '',
    email_footer: '',
    button_title: 'Log in',
    button_url: '',
    sender_name: 'SMOD',
    temp_password_prefix: 'Password:'
  });
  const [emailConfigLoading, setEmailConfigLoading] = useState(false);
  const [emailConfigSaving, setEmailConfigSaving] = useState(false);
  const [emailConfigError, setEmailConfigError] = useState('');
  const [emailConfigSuccess, setEmailConfigSuccess] = useState('');
  const [companyTimezone, setCompanyTimezone] = useState('UTC');
  const [dealerSupportEmail, setDealerSupportEmail] = useState('');
  const [dealerDashboardWelcome, setDealerDashboardWelcome] = useState('');

  // Formal documents state (Terms and Conditions, Privacy Policy)
  const [formalDocuments, setFormalDocuments] = useState({
    terms: { type: 'url', url: '', file: '' },
    privacy: { type: 'url', url: '', file: '' },
    extra_confirmation_text: ''
  });
  const [uploadingFormalDoc, setUploadingFormalDoc] = useState({ terms: false, privacy: false });

  // Communication module state
  const [communicationTab, setCommunicationTab] = useState('scheduled');
  const [messagingDealers, setMessagingDealers] = useState([]);
  const [loadingMessagingDealers, setLoadingMessagingDealers] = useState(false);
  const [onlyAssignedDealers, setOnlyAssignedDealers] = useState(false);
  const [selectAllDealers, setSelectAllDealers] = useState(false);
  const [selectedDealerOptions, setSelectedDealerOptions] = useState([]);
  const [communicationCampaigns, setCommunicationCampaigns] = useState([]);
  const [loadingCommunicationCampaigns, setLoadingCommunicationCampaigns] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [campaignDealers, setCampaignDealers] = useState([]);
  const [instantTitle, setInstantTitle] = useState('');
  const [instantSubtitle, setInstantSubtitle] = useState('');
  const [instantContent, setInstantContent] = useState('');
  const [instantExtraContent, setInstantExtraContent] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [sendingInstant, setSendingInstant] = useState(false);
  const [showSendConfirmDialog, setShowSendConfirmDialog] = useState(false);
  const [sendConfirmChecked, setSendConfirmChecked] = useState(false);
  const [recipientCounts, setRecipientCounts] = useState({ dealer_count: 0, user_count: 0 });
  const [specialAcceptedRecipients, setSpecialAcceptedRecipients] = useState([]);
  const [specialRejectedRows, setSpecialRejectedRows] = useState([]);
  const [loadingSpecialPreview, setLoadingSpecialPreview] = useState(false);
  const [specialTitle, setSpecialTitle] = useState('');
  const [specialSubtitle, setSpecialSubtitle] = useState('');
  const [specialContent, setSpecialContent] = useState('');
  const [specialExtraContent, setSpecialExtraContent] = useState('');
  const [specialTestEmail, setSpecialTestEmail] = useState('');
  const [sendingSpecial, setSendingSpecial] = useState(false);
  const [showSpecialSendConfirmDialog, setShowSpecialSendConfirmDialog] = useState(false);
  const [sendSpecialConfirmChecked, setSendSpecialConfirmChecked] = useState(false);
  const [communicationError, setCommunicationError] = useState('');
  const [communicationSuccess, setCommunicationSuccess] = useState('');
  const [commHistory, setCommHistory] = useState([]);
  const [commHistoryTotal, setCommHistoryTotal] = useState(0);
  const [commHistoryPage, setCommHistoryPage] = useState(0);
  const [commHistoryRowsPerPage, setCommHistoryRowsPerPage] = useState(25);
  const [loadingCommHistory, setLoadingCommHistory] = useState(false);
  const [commHistoryCampaignFilter, setCommHistoryCampaignFilter] = useState('ALL');
  const [commStatsCampaignFilter, setCommStatsCampaignFilter] = useState('ALL');
  const [commStatistics, setCommStatistics] = useState(null);
  const [loadingCommStatistics, setLoadingCommStatistics] = useState(false);

  // Communication Details Dialog state
  const [commDetailsDialogOpen, setCommDetailsDialogOpen] = useState(false);
  const [selectedCommForDetails, setSelectedCommForDetails] = useState(null);
  const [commEvents, setCommEvents] = useState([]);
  const [loadingCommEvents, setLoadingCommEvents] = useState(false);
  const [commEventsTotal, setCommEventsTotal] = useState(0);
  const [commEventsPage, setCommEventsPage] = useState(0);
  const [commEventsRowsPerPage, setCommEventsRowsPerPage] = useState(10);
  const [commEventsFilter, setCommEventsFilter] = useState('ALL');
  const [commEventsSortBy, setCommEventsSortBy] = useState('timestamp');
  const [commEventsSortOrder, setCommEventsSortOrder] = useState('desc');
  const [commEventsSearch, setCommEventsSearch] = useState('');
  const [commEventsLatestOnly, setCommEventsLatestOnly] = useState(true);


  // Scheduled Reminders state
  const [scheduledReminders, setScheduledReminders] = useState([]);
  const [loadingReminders, setLoadingReminders] = useState(false);
  const [scheduledRemindersCampaignFilter, setScheduledRemindersCampaignFilter] = useState('ALL');
  const [reminderDialog, setReminderDialog] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [reminderForm, setReminderForm] = useState({
    campaign_id: '',
    template_type: 'REMINDER_NO_POINTS',
    frequency: 'WEEKLY',
    day_of_week: 0,
    hour: 9,
    minute: 0,
    is_active: true
  });
  const [reminderError, setReminderError] = useState('');
  const [savingReminder, setSavingReminder] = useState(false);

  const menuItems = [
    { key: 'dashboard', label: 'Dashboard', icon: DashboardIcon },
    { key: 'dealers', label: 'Dealers', icon: StoreIcon },
    { key: 'users', label: 'Users', icon: PeopleIcon },
    { key: 'rewards', label: 'Rewards', icon: CardIcon },
    { key: 'campaigns', label: 'Campaigns', icon: CampaignIcon },
    ...(hasTournamentModule ? [{ key: 'leaderboards', label: 'Leaderboards', icon: LeaderboardIcon }] : []),
    { key: 'bonusTask', label: 'Bonus task', icon: BonusTaskIcon },
    { key: 'quiz', label: 'Quiz', icon: QuizIcon },
    { key: 'marketing', label: 'Marketing', icon: MarketingIcon },
    { key: 'notifications', label: 'Notifications', icon: NotificationIcon },
    { key: 'communication', label: 'Communication', icon: CommunicationIcon },
    { key: 'settings', label: 'Settings', icon: SettingsIcon },
  ].filter(item => !isManager || !['rewards', 'quiz', 'bonusTask', 'notifications', 'communication', 'settings'].includes(item.key));

  useEffect(() => {
    // Wait for auth to load before checking access
    if (loading) return;

    // Check if user is authenticated first
    if (!user || !token) {
      navigate('/login');
      return;
    }

    // Check if user has access to manufacturer panel
    if (!isAdminOrManager()) {
      navigate('/');
      return;
    }

    // Load initial data
    fetchCompanyBrandingImage();
    loadCompanyExtraModules();
    loadAvailableRewards();
    loadDealers();
  }, [isAdminOrManager, loading, navigate, user, token]);

  useEffect(() => {
    if (isManager && ['rewards', 'quiz', 'bonusTask', 'notifications', 'communication', 'settings'].includes(selectedTab)) {
      setSelectedTab('dashboard');
    }
  }, [isManager, selectedTab]);

  useEffect(() => {
    if (selectedTab === 'leaderboards' && !hasTournamentModule) {
      setSelectedTab('dashboard');
    }
  }, [selectedTab, hasTournamentModule]);

  // Load data for Communication module when opened
  useEffect(() => {
    if (selectedTab !== 'communication') return;
    loadNotificationSets();
    loadCampaigns();
    loadMessagingDealers();
    loadCommunicationCampaigns();
  }, [selectedTab]);

  useEffect(() => {
    if (selectedTab !== 'communication') return;
    loadCommunicationHistory();
  }, [selectedTab, commHistoryPage, commHistoryRowsPerPage, commHistoryCampaignFilter]);

  useEffect(() => {
    if (selectedTab !== 'communication' || communicationTab !== 'statistics') return;
    loadCommunicationStatistics();
  }, [selectedTab, communicationTab, commStatsCampaignFilter]);

  useEffect(() => {
    if (selectedTab !== 'communication' || communicationTab !== 'scheduled') return;
    loadAllScheduledReminders();
  }, [selectedTab, communicationTab, scheduledRemindersCampaignFilter]);

  // Date formatting: European (DD-MM-YYYY) when company timezone is in Europe, otherwise US (MM/DD/YYYY)
  const isEuropeanTimezone = (tz) => tz && tz.startsWith('Europe/');
  const formatDateForDisplay = (date, includeTime = false) => {
    if (!date) return 'N/A';
    const d = dayjs(date);
    if (isEuropeanTimezone(companyTimezone)) {
      return includeTime ? d.format('YYYY-MM-DD HH:mm:ss') : d.format('YYYY-MM-DD');
    }
    return includeTime ? d.format('MM/DD/YYYY HH:mm:ss') : d.toDate().toLocaleDateString('en-US');
  };
  const dateFormatForDayjs = isEuropeanTimezone(companyTimezone) ? 'YYYY-MM-DD HH:mm' : 'MM/DD/YYYY HH:mm';

  /** Server stores campaign boundaries as UTC; edit them in company timezone (same pattern as quiz auto on/off). */
  const campaignDateApiToFormValue = (date) =>
    date == null ? null : dayjs.utc(date).tz(companyTimezone || 'UTC');

  const formatCampaignUtcDateForDisplay = (date, includeTime = false) => {
    if (!date) return 'N/A';
    const d = dayjs.utc(date).tz(companyTimezone || 'UTC');
    if (isEuropeanTimezone(companyTimezone)) {
      return includeTime ? d.format('YYYY-MM-DD HH:mm:ss') : d.format('YYYY-MM-DD');
    }
    return includeTime ? d.format('MM/DD/YYYY HH:mm:ss') : d.toDate().toLocaleDateString('en-US');
  };

  // Centralized function to fetch branding image
  const fetchCompanyBrandingImage = async (force = false) => {
    if (companyBrandingImage && !force) {
      return;
    }

    if (user?.company_id && !loading && apiCall) {
      const result = await apiCall('get', `/api/companies/${user.company_id}`);

      if (result.success && result.data) {
        // Set branding image if available
        if (result.data.branding_image) {
          const imageUrl = `${CDN_URL}/${result.data.branding_image}`;
          setCompanyBrandingImage(imageUrl);
        }

        // Set branding color if available
        if (result.data.branding_color) {
          setCompanyBrandingColor(result.data.branding_color);
        }

        // Load email configuration if available
        if (result.data.email_configuration) {
          setEmailConfig({
            email_header_image: result.data.email_configuration.email_header_image || '',
            email_footer_image: result.data.email_configuration.email_footer_image || '',
            email_footer: result.data.email_configuration.email_footer || '',
            button_title: result.data.email_configuration.button_title || 'Log in',
            button_url: result.data.email_configuration.button_url || '',
            sender_name: result.data.email_configuration.sender_name || 'SMOD',
            temp_password_prefix: result.data.email_configuration.temp_password_prefix || 'Password:'
          });
        }

        // Set company timezone
        if (result.data.timezone) {
          setCompanyTimezone(result.data.timezone);
        }
        if (result.data.support_email) {
          setDealerSupportEmail(result.data.support_email);
        } else {
          setDealerSupportEmail('');
        }
        setDealerDashboardWelcome(result.data.dashboard_welcome || '');
        // Load formal documents
        if (result.data.formal_documents) {
          const fd = result.data.formal_documents;
          const terms = fd.terms || {};
          const privacy = fd.privacy || {};
          setFormalDocuments({
            terms: {
              type: terms.url ? 'url' : 'file',
              url: terms.url || '',
              file: terms.file || ''
            },
            privacy: {
              type: privacy.url ? 'url' : 'file',
              url: privacy.url || '',
              file: privacy.file || ''
            },
            extra_confirmation_text: typeof fd.extra_confirmation_text === 'string' ? fd.extra_confirmation_text : ''
          });
        }
      } else if (!result.requiresAuth) {
        console.error('Error fetching company branding:', result.error);
      }
      // If result.requiresAuth is true, user will be redirected to login automatically
    }
  };

  const loadCompanyExtraModules = async () => {
    if (!user?.company_id || !apiCall) return;
    try {
      const result = await apiCall('get', `/api/company-extra-modules?company_id=${user.company_id}`);
      if (result.success) {
        setCompanyExtraModules(result.data.company_extra_modules || []);
      }
    } catch (error) {
      console.error('Error loading company extra modules:', error);
    }
  };

  const loadAvailableRewards = async () => {
    if (!user?.company_id || !apiCall) return;

    setLoadingRewards(true);
    setRewardsError('');

    try {
      // First, get the reward providers for this company
      const providersResult = await apiCall('get', `/api/company-reward-providers?company_id=${user.company_id}`);

      if (providersResult.success) {
        const rewardProviders = providersResult.data.company_reward_providers || [];
        setCompanyRewardProviders(rewardProviders);

        if (rewardProviders.length === 0) {
          setAvailableRewards([]);
          setRewardsByProvider({});
          setEdenredUrpRows([]);
          setEdenredUrpError('');
          setLoadingRewards(false);
          return;
        }

        // Get all rewards for each provider and group by provider
        const allRewards = [];
        const groupedByProvider = {};

        for (const crp of rewardProviders) {
          if (crp.reward_provider_id && crp.reward_provider) {
            const providerName = crp.reward_provider.display_name || crp.reward_provider.name;
            const rewardsResult = await apiCall('get', `/api/rewards?provider_id=${crp.reward_provider_id}&company_id=${user.company_id}&per_page=1000`);
            if (rewardsResult.success && rewardsResult.data.rewards) {
              const providerRewards = rewardsResult.data.rewards;
              allRewards.push(...providerRewards);
              groupedByProvider[providerName] = {
                provider: crp.reward_provider,
                rewards: providerRewards,
                crp: crp
              };
            }
          }
        }

        setAvailableRewards(allRewards);
        setRewardsByProvider(groupedByProvider);

        // Load Edenred balance if Edenred_PL provider exists
        // We need to get the company's api_account - fetch company details if needed
        const edenredProvider = rewardProviders.find(crp => crp.reward_provider?.name === 'Edenred_PL');
        if (edenredProvider) {
          loadCompanyEdenredUrp();
          // Fetch company to get api_account
          const companyResult = await apiCall('get', `/api/companies/${user.company_id}`);
          if (companyResult.success && companyResult.data?.api_account) {
            loadEdenredBalance(companyResult.data.api_account);
          }
        } else {
          setEdenredUrpRows([]);
          setEdenredUrpError('');
        }
      } else if (!providersResult.requiresAuth) {
        setRewardsError(providersResult.error || 'Failed to load reward providers');
      }
    } catch (error) {
      console.error('Error loading available rewards:', error);
      setRewardsError('Failed to load rewards');
    }

    setLoadingRewards(false);
  };

  const loadEdenredBalance = async (account) => {
    if (!account || !apiCall) return;

    setLoadingEdenredBalance(true);
    setEdenredBalanceError('');

    try {
      const result = await apiCall('get', `/api/edenred-balance?account=${account}`);
      if (result.success) {
        setEdenredBalance(result.data);
      } else if (!result.requiresAuth) {
        setEdenredBalanceError(result.error || 'Failed to load Edenred balance');
      }
    } catch (error) {
      console.error('Error loading Edenred balance:', error);
      setEdenredBalanceError('Failed to load Edenred balance');
    }

    setLoadingEdenredBalance(false);
  };

  const loadCompanyEdenredUrp = async () => {
    if (!apiCall) return;
    setLoadingEdenredUrp(true);
    setEdenredUrpError('');
    try {
      const result = await apiCall('get', '/api/company-edenred-user-reward-providers');
      if (result.success) {
        setEdenredUrpRows(result.data.rows || []);
      } else if (!result.requiresAuth) {
        setEdenredUrpError(result.error || 'Failed to load Edenred assignments');
        setEdenredUrpRows([]);
      }
    } catch (error) {
      console.error('Error loading Edenred user reward providers:', error);
      setEdenredUrpError('Failed to load Edenred assignments');
      setEdenredUrpRows([]);
    }
    setLoadingEdenredUrp(false);
  };

  // Tremendous setup functions
  const handleTremendousSetup = async () => {
    if (!user?.company_id) return;
    setTremendousSetupLoading(true);
    setError('');
    try {
      const result = await apiCall('post', `/api/tremendous/setup/${user.company_id}`);
      if (result.success) {
        setSuccess('Tremendous setup completed successfully');

        // Check if setup returned the member_id - if so, we can proceed immediately
        const hasMemberId = result.data?.connected_organization_member_id;

        // Reload rewards to refresh the UI
        await loadAvailableRewards();

        // Automatically open OAuth flow after setup
        // Use a longer delay to ensure database commit is visible, or proceed immediately if we have the ID
        const delay = hasMemberId ? 100 : 1500;

        setTimeout(async () => {
          try {
            const sessionResult = await apiCall('get', `/api/tremendous/session/${user.company_id}`);
            if (sessionResult.success && sessionResult.data?.url) {
              // Redirect to Tremendous OAuth flow
              console.log('Redirecting to Tremendous OAuth flow:', sessionResult.data.url);
              window.location.href = sessionResult.data.url;
            } else if (!sessionResult.requiresAuth) {
              setError(sessionResult.error || 'Failed to get Tremendous session URL. Please try clicking "Open Tremendous OAuth Flow" manually.');
              setTremendousSetupLoading(false);
            }
          } catch (sessionError) {
            console.error('Error opening Tremendous flow after setup:', sessionError);
            setError('Setup completed, but failed to open OAuth flow. Please click "Open Tremendous OAuth Flow" manually.');
            setTremendousSetupLoading(false);
          }
        }, delay);
      } else if (!result.requiresAuth) {
        setError(result.error || 'Failed to setup Tremendous');
        setTremendousSetupLoading(false);
      }
    } catch (error) {
      console.error('Error setting up Tremendous:', error);
      setError('Failed to setup Tremendous: ' + (error.message || 'Unknown error'));
      setTremendousSetupLoading(false);
    }
    // Note: Don't set loading to false here if we're redirecting
  };

  const handleOpenTremendousFlow = async () => {
    if (!user?.company_id) return;
    setTremendousSetupLoading(true);
    setError('');
    try {
      const result = await apiCall('get', `/api/tremendous/session/${user.company_id}`);
      if (result.success && result.data?.url) {
        // Redirect to Tremendous OAuth flow
        console.log('Redirecting to Tremendous OAuth flow:', result.data.url);
        window.location.href = result.data.url;
        // Note: setTremendousSetupLoading(false) won't execute due to redirect
      } else if (!result.requiresAuth) {
        setError(result.error || 'Failed to get Tremendous session URL');
        setTremendousSetupLoading(false);
      }
    } catch (error) {
      console.error('Error opening Tremendous flow:', error);
      setError('Failed to open Tremendous flow: ' + (error.message || 'Unknown error'));
      setTremendousSetupLoading(false);
    }
  };

  const handleGetTremendousBalance = async () => {
    if (!user?.company_id) return;
    setLoadingTremendousBalance(true);
    setTremendousBalanceError('');
    try {
      const result = await apiCall('get', `/api/tremendous/balance/${user.company_id}`);
      if (result.success) {
        setTremendousBalance(result.data);
        balanceLoadAttemptedRef.current = false; // Reset on success so it can be refreshed manually
      } else if (!result.requiresAuth) {
        const errorMsg = result.error || 'Failed to get Tremendous balance';
        setTremendousBalanceError(errorMsg);
        // Don't reset the ref on error to prevent infinite retry loops
      }
    } catch (error) {
      console.error('Error getting Tremendous balance:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to get Tremendous balance';
      setTremendousBalanceError(errorMsg);
      // Don't reset the ref on error to prevent infinite retry loops
    } finally {
      setLoadingTremendousBalance(false);
    }
  };

  const handleTremendousDisconnect = async () => {
    if (!user?.company_id) return;
    if (!window.confirm('Are you sure you want to disconnect Tremendous? This will reset the integration and you will need to set it up again.')) {
      return;
    }
    setTremendousSetupLoading(true);
    setError('');
    try {
      const result = await apiCall('post', `/api/tremendous/disconnect/${user.company_id}`);
      if (result.success) {
        setSuccess(result.message || 'Tremendous disconnected successfully');
        // Reset balance state
        setTremendousBalance(null);
        setTremendousBalanceError('');
        balanceLoadAttemptedRef.current = false; // Reset ref
        // Reload rewards to refresh the UI
        await loadAvailableRewards();
      } else if (!result.requiresAuth) {
        setError(result.error || 'Failed to disconnect Tremendous');
      }
    } catch (error) {
      console.error('Error disconnecting Tremendous:', error);
      setError('Failed to disconnect Tremendous');
    } finally {
      setTremendousSetupLoading(false);
    }
  };

  const loadTremendousProducts = async () => {
    if (!user?.company_id) return;
    setLoadingTremendousProducts(true);
    setTremendousProductsError('');
    try {
      const result = await apiCall('get', `/api/tremendous/products/${user.company_id}`);
      console.log('Tremendous products result:', result);

      if (!result.success) {
        throw new Error(result.error || 'Failed to load products');
      }

      const response = result.data; // apiCall returns { success: true, data: response.data }
      console.log('Tremendous products response data:', response);

      let products = [];

      // Handle different response structures
      if (response && response.products && Array.isArray(response.products)) {
        products = response.products;
      } else if (response && Array.isArray(response)) {
        // Handle case where API returns array directly
        products = response;
      } else if (response && typeof response === 'object') {
        // Try to find products array in response
        const possibleKeys = ['products', 'data', 'items', 'results'];
        for (const key of possibleKeys) {
          if (response[key] && Array.isArray(response[key])) {
            products = response[key];
            break;
          }
        }
      }

      if (products.length > 0) {
        console.log(`Loaded ${products.length} products`);
        setTremendousProducts(products);
      } else {
        console.warn('No products found in response:', response);
        setTremendousProducts([]);
        setTremendousProductsError('No products found. You can enter product IDs manually.');
      }
    } catch (error) {
      console.error('Error loading Tremendous products:', error);
      setTremendousProducts([]);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to load products';
      setTremendousProductsError(`${errorMsg}. You can enter product IDs manually.`);
    } finally {
      setLoadingTremendousProducts(false);
    }
  };

  const handleAddVISACards = () => {
    if (tremendousProducts.length === 0) return;

    // Filter products for VISA cards only
    const visaProducts = tremendousProducts.filter(product => {
      const name = (product.name || '').toLowerCase();
      const id = (product.id || '').toLowerCase();

      // Check if product is VISA card
      return name.includes('visa') || id.includes('visa');
    });

    // Get unique product IDs
    const newProductIds = visaProducts.map(p => p.id).filter(id => id);

    // Merge with existing selected products (avoid duplicates)
    const updatedProducts = [...new Set([...tremendousCampaignProducts, ...newProductIds])];
    setTremendousCampaignProducts(updatedProducts);
  };

  const handleAddAllGiftCards = () => {
    if (tremendousProducts.length === 0) return;

    // Get all product IDs
    const allProductIds = tremendousProducts.map(p => p.id).filter(id => id);

    // Merge with existing selected products (avoid duplicates)
    const updatedProducts = [...new Set([...tremendousCampaignProducts, ...allProductIds])];
    setTremendousCampaignProducts(updatedProducts);
  };

  const handleCreateTremendousCampaign = async () => {
    if (!user?.company_id) return;
    if (!tremendousCampaignName.trim()) {
      setError('Campaign name is required');
      return;
    }

    setCreatingTremendousCampaign(true);
    setError('');
    setSuccess('');

    try {
      const products = tremendousCampaignProducts.length > 0
        ? tremendousCampaignProducts
        : null;

      // Build query parameters - FastAPI expects all parameters as query params
      const queryParams = new URLSearchParams({
        company_id: user.company_id.toString(),
        name: tremendousCampaignName.trim()
      });

      // Add optional description to query if it exists
      if (tremendousCampaignDescription.trim()) {
        queryParams.append('description', tremendousCampaignDescription.trim());
      }

      // Add products array as multiple query parameters (FastAPI will parse as List[str])
      if (products && products.length > 0) {
        products.forEach(productId => {
          queryParams.append('products', productId);
        });
      }

      // Send all parameters as query params (no body)
      const result = await apiCall('post', `/api/tremendous/campaigns/create?${queryParams.toString()}`, null);

      if (result.success) {
        setSuccess(`Campaign "${result.campaign?.name || tremendousCampaignName}" created successfully! Campaign ID: ${result.campaign_id}`);
        // Reset form
        setTremendousCampaignName('');
        setTremendousCampaignDescription('');
        setTremendousCampaignProducts([]);
        setTremendousProductsError('');
        setShowTremendousCampaignDialog(false);
        productsLoadAttemptedRef.current = false;
        // Reload rewards to refresh the UI
        await loadAvailableRewards();
      } else if (!result.requiresAuth) {
        setError(result.error || 'Failed to create Tremendous campaign');
      }
    } catch (error) {
      console.error('Error creating Tremendous campaign:', error);
      setError(error.response?.data?.detail || error.message || 'Failed to create Tremendous campaign');
    } finally {
      setCreatingTremendousCampaign(false);
    }
  };

  const getTremendousProvider = () => {
    return companyRewardProviders.find(crp =>
      crp.reward_provider?.name?.toLowerCase().includes('tremendous')
    );
  };

  const getTremendousConnectionStatus = (tremendousProvider) => {
    if (!tremendousProvider) return null;
    const config = tremendousProvider.config || {};
    const hasOAuthToken = !!config.tremendous_oauth_access_token;
    const hasMemberId = !!config.tremendous_connected_organization_member_id;
    const hasOrgId = !!config.tremendous_connected_organization_id;

    if (hasOAuthToken) return 'connected';
    if (hasMemberId && hasOrgId) return 'setup_complete';
    return 'not_setup';
  };

  // Track if we've attempted to load products for this dialog session
  const productsLoadAttemptedRef = useRef(false);
  // Track if we've attempted to load balance to prevent infinite loops
  const balanceLoadAttemptedRef = useRef(false);

  // Load Tremendous products when dialog opens (only once per dialog session)
  useEffect(() => {
    if (showTremendousCampaignDialog && !productsLoadAttemptedRef.current) {
      productsLoadAttemptedRef.current = true;
      loadTremendousProducts();
    } else if (!showTremendousCampaignDialog) {
      // Reset the ref when dialog closes so we can load again next time
      productsLoadAttemptedRef.current = false;
    }
  }, [showTremendousCampaignDialog]);

  // Communication helpers
  const loadMessagingDealers = async () => {
    if (!user?.company_id || !apiCall) return;
    setLoadingMessagingDealers(true);
    try {
      const result = await apiCall('get', `/api/dealers?page=1&per_page=1000&company_id=${user.company_id}`);
      if (result.success) {
        setMessagingDealers(result.data.dealers || []);
      }
    } catch (error) {
      console.error('Error loading dealers for messaging:', error);
    }
    setLoadingMessagingDealers(false);
  };

  const loadCommunicationCampaigns = async () => {
    if (!apiCall) return;
    setLoadingCommunicationCampaigns(true);
    try {
      const result = await apiCall('get', `/api/campaigns?page=1&per_page=1000`);
      if (result.success) {
        setCommunicationCampaigns(result.data.campaigns || []);
      } else {
        console.error('Failed to load campaigns:', result.error);
      }
    } catch (error) {
      console.error('Error loading campaigns for communication:', error);
    }
    setLoadingCommunicationCampaigns(false);
  };

  const handleCampaignSelection = async (campaignId) => {
    if (!campaignId || campaignId === '') {
      setCampaignDealers([]);
      return;
    }

    // Find the selected campaign and get its dealers
    const campaignIdNum = typeof campaignId === 'string' ? parseInt(campaignId, 10) : campaignId;
    const selectedCampaign = communicationCampaigns.find(c => c.id === campaignIdNum);

    if (selectedCampaign && selectedCampaign.dealers && selectedCampaign.dealers.length > 0) {
      setCampaignDealers(selectedCampaign.dealers || []);
    } else {
      // If dealers not loaded, fetch campaign details
      if (!apiCall) {
        return;
      }
      try {
        const result = await apiCall('get', `/api/campaigns/${campaignIdNum}`);
        if (result.success && result.data && result.data.dealers) {
          setCampaignDealers(result.data.dealers || []);
        } else {
          setCampaignDealers([]);
        }
      } catch (error) {
        console.error('Error loading campaign dealers:', error);
        setCampaignDealers([]);
      }
    }
  };

  const loadCommunicationHistory = async () => {
    if (!apiCall) return;
    setLoadingCommHistory(true);
    setCommunicationError('');
    try {
      const params = new URLSearchParams({
        page: (commHistoryPage + 1).toString(),
        per_page: commHistoryRowsPerPage.toString()
      });
      if (commHistoryCampaignFilter !== 'ALL') {
        params.append('campaign_id', commHistoryCampaignFilter);
      }
      const result = await apiCall('get', `/api/communications?${params.toString()}`);
      if (result.success) {
        setCommHistory(result.data.communications || []);
        setCommHistoryTotal(result.data.total || 0);
      } else if (!result.requiresAuth) {
        setCommunicationError(result.error || 'Failed to load communication history');
      }
    } catch (error) {
      console.error('Error loading communication history:', error);
      setCommunicationError('Failed to load communication history');
    }
    setLoadingCommHistory(false);
  };

  const loadCommunicationEvents = async (commId = null) => {
    const id = commId || selectedCommForDetails?.id;
    if (!id || !apiCall) return;

    setLoadingCommEvents(true);
    try {
      const params = new URLSearchParams({
        page: (commEventsPage + 1).toString(),
        per_page: commEventsRowsPerPage.toString(),
        sort_by: commEventsSortBy,
        sort_order: commEventsSortOrder,
        latest_only: commEventsLatestOnly.toString()
      });
      if (commEventsFilter !== 'ALL') {
        params.append('event_type', commEventsFilter.toLowerCase());
      }
      if (commEventsSearch && commEventsSearch.trim()) {
        params.append('search', commEventsSearch.trim());
      }
      const result = await apiCall('get', `/api/communications/${id}/events?${params.toString()}`);
      if (result.success) {
        setCommEvents(result.data.events || []);
        setCommEventsTotal(result.data.total || 0);
      }
    } catch (error) {
      console.error('Error loading communication events:', error);
    }
    setLoadingCommEvents(false);
  };

  const handleCommEventsSort = (column) => {
    const isAsc = commEventsSortBy === column && commEventsSortOrder === 'asc';
    setCommEventsSortOrder(isAsc ? 'desc' : 'asc');
    setCommEventsSortBy(column);
    setCommEventsPage(0);
  };

  useEffect(() => {
    if (commDetailsDialogOpen && selectedCommForDetails) {
      loadCommunicationEvents();
    }
  }, [commEventsPage, commEventsRowsPerPage, commEventsFilter, commEventsSortBy, commEventsSortOrder, commEventsSearch, commEventsLatestOnly]);

  const handleOpenCommDetails = (comm) => {
    setSelectedCommForDetails(comm);
    setCommEventsPage(0);
    setCommEventsFilter('ALL');
    setCommEventsSortBy('timestamp');
    setCommEventsSortOrder('desc');
    setCommEventsSearch('');
    setCommEventsLatestOnly(true);
    setCommDetailsDialogOpen(true);
    loadCommunicationEvents(comm.id);
  };

  const handleCommEventsChangePage = (event, newPage) => {
    setCommEventsPage(newPage);
  };

  const handleCommEventsChangeRows = (event) => {
    setCommEventsRowsPerPage(parseInt(event.target.value, 10));
    setCommEventsPage(0);
  };


  const loadCommunicationStatistics = async () => {
    if (!user?.company_id || !apiCall) return;
    setLoadingCommStatistics(true);
    try {
      const params = new URLSearchParams({
        company_id: user.company_id.toString()
      });
      if (commStatsCampaignFilter !== 'ALL') {
        params.append('campaign_id', commStatsCampaignFilter);
      }
      const result = await apiCall('get', `/api/communications/statistics?${params.toString()}`);
      if (result.success) {
        setCommStatistics(result.data);
      } else if (!result.requiresAuth) {
        console.error('Failed to load statistics:', result.error);
      }
    } catch (error) {
      console.error('Error loading communication statistics:', error);
    }
    setLoadingCommStatistics(false);
  };

  const getNextRun = (targetDow, hour, minute) => {
    let now = dayjs();
    let next = now.day(targetDow).hour(hour).minute(minute).second(0);
    if (next.isBefore(now)) {
      next = next.add(1, 'week');
    }
    return next.format('YYYY-MM-DD HH:mm');
  };

  // Reminder management functions
  const loadScheduledReminders = async (campaignId) => {
    if (!campaignId || !apiCall) return;
    setLoadingReminders(true);
    try {
      const result = await apiCall('get', `/api/campaigns/${campaignId}/reminders`);
      if (result.success) {
        return result.data.reminders || [];
      }
    } catch (error) {
      console.error('Error loading reminders:', error);
    }
    setLoadingReminders(false);
    return [];
  };

  const handleOpenReminderDialog = (campaignId, reminder = null) => {
    if (reminder) {
      setEditingReminder(reminder);
      setReminderForm({
        campaign_id: reminder.campaign_id,
        template_type: reminder.template_type,
        frequency: reminder.frequency || 'WEEKLY',
        day_of_week: reminder.day_of_week,
        hour: reminder.hour,
        minute: reminder.minute,
        is_active: reminder.is_active
      });
    } else {
      setEditingReminder(null);
      setReminderForm({
        campaign_id: campaignId,
        template_type: 'REMINDER_NO_POINTS',
        frequency: 'WEEKLY',
        day_of_week: 0,
        hour: 9,
        minute: 0,
        is_active: true
      });
    }
    setReminderError('');
    setReminderDialog(true);
  };

  const handleCloseReminderDialog = () => {
    setReminderDialog(false);
    setEditingReminder(null);
    setReminderError('');
  };

  const handleSaveReminder = async () => {
    setSavingReminder(true);
    setReminderError('');

    try {
      const method = editingReminder ? 'put' : 'post';
      const endpoint = editingReminder
        ? `/api/reminders/${editingReminder.id}`
        : `/api/campaigns/${reminderForm.campaign_id}/reminders`;

      const result = await apiCall(method, endpoint, reminderForm);

      if (result.success) {
        handleCloseReminderDialog();
        // Reload the scheduled reminders for all campaigns
        await loadAllScheduledReminders();
      } else if (!result.requiresAuth) {
        setReminderError(result.error || 'Failed to save reminder');
      }
    } catch (error) {
      console.error('Error saving reminder:', error);
      setReminderError('Failed to save reminder');
    }

    setSavingReminder(false);
  };

  const handleDeleteReminder = async (reminderId) => {
    if (!window.confirm('Are you sure you want to delete this reminder?')) return;

    try {
      const result = await apiCall('delete', `/api/reminders/${reminderId}`);
      if (result.success) {
        await loadAllScheduledReminders();
      } else if (!result.requiresAuth) {
        alert(result.error || 'Failed to delete reminder');
      }
    } catch (error) {
      console.error('Error deleting reminder:', error);
      alert('Failed to delete reminder');
    }
  };

  const handleToggleReminderActive = async (reminder) => {
    try {
      const result = await apiCall('put', `/api/reminders/${reminder.id}`, {
        is_active: !reminder.is_active
      });
      if (result.success) {
        await loadAllScheduledReminders();
      } else if (!result.requiresAuth) {
        alert(result.error || 'Failed to update reminder');
      }
    } catch (error) {
      console.error('Error updating reminder:', error);
      alert('Failed to update reminder');
    }
  };

  const loadAllScheduledReminders = async () => {
    if (!apiCall) return;
    setLoadingReminders(true);

    try {
      let campaignsToLoad = campaigns.filter(c => c.status === 'RUNNING');

      // Apply campaign filter if not "ALL"
      if (scheduledRemindersCampaignFilter !== 'ALL') {
        campaignsToLoad = campaignsToLoad.filter(c => c.id.toString() === scheduledRemindersCampaignFilter);
      }

      const allReminders = [];

      for (const campaign of campaignsToLoad) {
        const reminders = await loadScheduledReminders(campaign.id);
        allReminders.push(...reminders.map(r => ({ ...r, campaign })));
      }

      setScheduledReminders(allReminders);
    } catch (error) {
      console.error('Error loading all reminders:', error);
    }

    setLoadingReminders(false);
  };

  const handleCommHistoryChangePage = (event, newPage) => {
    setCommHistoryPage(newPage);
  };

  const handleCommHistoryChangeRows = (event) => {
    setCommHistoryRowsPerPage(parseInt(event.target.value, 10));
    setCommHistoryPage(0);
  };

  const handleOpenSendConfirm = async () => {
    // Validation
    if (!instantTitle.trim()) { setCommunicationError('Title is required'); return; }
    if (!instantContent || instantContent.trim() === '') { setCommunicationError('Content is required'); return; }
    if (!selectedCampaignId) { setCommunicationError('Please select a campaign'); return; }

    // Fetch recipient counts
    try {
      const result = await apiCall('get', `/api/campaigns/${selectedCampaignId}/recipient-count`);
      if (result.success) {
        setRecipientCounts(result.data);
        setShowSendConfirmDialog(true);
        setSendConfirmChecked(false);
      } else {
        setCommunicationError(result.error || 'Failed to load recipient count');
      }
    } catch (error) {
      setCommunicationError('Failed to load recipient count');
    }
  };

  const handleSendInstant = async () => {
    if (!user?.company_id || !apiCall) return;
    setShowSendConfirmDialog(false);
    setSendingInstant(true);
    setCommunicationError('');
    setCommunicationSuccess('');
    try {
      const payload = {
        company_id: user.company_id,
        type: 'INSTANT',
        title: instantTitle,
        subtitle: instantSubtitle,
        content: instantContent,
        extra_content: instantExtraContent || '',
        notification_set_id: null,
        template_type: null,
        campaign_id: parseInt(selectedCampaignId)
      };
      const result = await apiCall('post', '/api/communications', payload);
      if (result.success) {
        const emailCount = result.data?.email_count || 0;
        setCommunicationSuccess(`Successfully sent ${emailCount} email${emailCount !== 1 ? 's' : ''} to dealers in the campaign.`);
        setInstantTitle('');
        setInstantSubtitle('');
        setInstantContent('');
        setInstantExtraContent('');
        setSelectedCampaignId('');
        setCampaignDealers([]);
        loadCommunicationHistory();
      } else if (!result.requiresAuth) {
        setCommunicationError(result.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending instant communication:', error);
      setCommunicationError('Failed to send message');
    }
    setSendingInstant(false);
  };

  const handleOpenSpecialSendConfirm = () => {
    if (!specialTitle.trim()) { setCommunicationError('Title is required'); return; }
    if (!specialContent || specialContent.trim() === '') { setCommunicationError('Content is required'); return; }
    if (!specialAcceptedRecipients.length) { setCommunicationError('Upload a CSV and ensure at least one recipient is accepted'); return; }
    setCommunicationError('');
    setShowSpecialSendConfirmDialog(true);
    setSendSpecialConfirmChecked(false);
  };

  const handleSendSpecial = async () => {
    if (!user?.company_id || !apiCall) return;
    setShowSpecialSendConfirmDialog(false);
    setSendingSpecial(true);
    setCommunicationError('');
    setCommunicationSuccess('');
    try {
      const payload = {
        company_id: user.company_id,
        type: 'SPECIAL',
        title: specialTitle,
        subtitle: specialSubtitle,
        content: specialContent,
        extra_content: specialExtraContent || '',
        notification_set_id: null,
        template_type: null,
        special_recipients: specialAcceptedRecipients.map((r) => ({ email: r.email, url: r.url }))
      };
      const result = await apiCall('post', '/api/communications', payload);
      if (result.success) {
        const emailCount = result.data?.email_count || 0;
        setCommunicationSuccess(`Successfully sent ${emailCount} email${emailCount !== 1 ? 's' : ''}.`);
        setSpecialTitle('');
        setSpecialSubtitle('');
        setSpecialContent('');
        setSpecialExtraContent('');
        setSpecialAcceptedRecipients([]);
        setSpecialRejectedRows([]);
        setSpecialTestEmail('');
        loadCommunicationHistory();
      } else if (!result.requiresAuth) {
        setCommunicationError(result.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending special communication:', error);
      setCommunicationError('Failed to send message');
    }
    setSendingSpecial(false);
  };

  const loadRewardsBudget = async () => {
    if (!user?.company_id || !fetchSmodBalance) return;

    // Check if company has GiftMall or PerfectGift providers before calling SMOD balance
    const hasGiftMallOrPerfectGift = companyRewardProviders.some(crp => {
      const name = (crp.reward_provider?.name || '').toLowerCase();
      return name.includes('giftmall') || name.includes('perfectgift');
    });

    // Only load SMOD balance if GiftMall or PerfectGift providers are connected
    if (!hasGiftMallOrPerfectGift) {
      setLoadingBudget(false);
      return;
    }

    setLoadingBudget(true);
    setBudgetError('');

    try {
      // Fetch balance from SMOD API via backend proxy
      const balanceResult = await fetchSmodBalance(user.company_id);

      if (balanceResult.success) {
        setRewardsBudget(balanceResult.data);
      } else if (!balanceResult.requiresAuth) {
        setBudgetError(balanceResult.error || 'Failed to load rewards budget');
      }
    } catch (error) {
      console.error('Error loading rewards budget:', error);
      setBudgetError('Failed to load rewards budget');
    }

    setLoadingBudget(false);
  };

  const loadDealers = async () => {
    if (!user?.company_id || !apiCall) return;

    setLoadingDealers(true);
    setDealersError('');

    try {
      const params = new URLSearchParams({
        page: (dealersPage + 1).toString(),
        per_page: dealersRowsPerPage.toString(),
        company_id: user.company_id.toString()
      });

      if (dealersSearch.trim()) {
        params.append('search', dealersSearch.trim());
      }

      if (dealersAsmFilter) {
        params.append('asm_owner_name', dealersAsmFilter);
      }

      const result = await apiCall('get', `/api/dealers?${params.toString()}`);

      if (result.success) {
        setDealers(result.data.dealers || []);
        setDealersTotal(result.data.total || 0);
      } else if (!result.requiresAuth) {
        setDealersError(result.error || 'Failed to load dealers');
      }
    } catch (error) {
      console.error('Error loading dealers:', error);
      setDealersError('Failed to load dealers');
    }

    setLoadingDealers(false);
  };

  const loadUsers = async () => {
    if (!user?.company_id || !apiCall) return;

    setLoadingUsers(true);
    setUsersError('');

    try {
      const params = new URLSearchParams({
        page: (usersPage + 1).toString(),
        per_page: usersRowsPerPage.toString(),
        company_id: user.company_id.toString()
      });

      if (usersSearch.trim()) {
        params.append('search', usersSearch.trim());
      }

      if (usersDealerFilter) {
        params.append('dealer_id', usersDealerFilter);
      }

      if (usersRoleFilter) {
        params.append('role', usersRoleFilter);
      }

      if (usersAsmFilter) {
        params.append('asm_owner_name', usersAsmFilter);
      }

      params.append('sort_by', usersSortBy);
      params.append('sort_order', usersSortOrder);

      const result = await apiCall('get', `/api/users?${params.toString()}`);

      if (result.success) {
        setUsers(result.data.users || []);
        setUsersTotal(result.data.total || 0);
      } else if (!result.requiresAuth) {
        setUsersError(result.error || 'Failed to load users');
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setUsersError('Failed to load users');
    }

    setLoadingUsers(false);
  };

  const handleUsersSort = (column) => {
    const isAsc = usersSortBy === column && usersSortOrder === 'asc';
    setUsersSortBy(column);
    setUsersSortOrder(isAsc ? 'desc' : 'asc');
    setUsersPage(0);
  };

  const handleActivationUrlsFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (event.target) {
      event.target.value = '';
    }
    if (!file || !apiCall || !user?.company_id) return;
    setActivationUrlsImportError('');
    setActivationUrlsImportSuccess('');
    setImportingActivationUrls(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const result = await apiCall('post', '/api/users/import-activation-urls', formData);
      if (result.success && result.data) {
        const d = result.data;
        setActivationUrlsImportSuccess(
          `Updated ${d.updated}. Skipped (already logged in): ${d.skipped_logged_in}. Not found: ${d.not_found}. Invalid rows (empty URL): ${d.skipped_invalid}. Empty email rows: ${d.skipped_empty_email}.`
        );
        await loadUsers();
      } else if (!result.requiresAuth) {
        const err = result.error;
        setActivationUrlsImportError(typeof err === 'string' ? err : 'Import failed');
      }
    } catch (error) {
      setActivationUrlsImportError(error.message || 'Import failed');
    } finally {
      setImportingActivationUrls(false);
    }
  };

  const loadDealersForFilter = async () => {
    if (!user?.company_id || !apiCall) return;

    try {
      const params = new URLSearchParams({
        page: '1',
        per_page: '1000',
        company_id: user.company_id.toString()
      });

      const result = await apiCall('get', `/api/dealers?${params.toString()}`);

      if (result.success) {
        setDealersForFilter(result.data.dealers || []);
      } else if (!result.requiresAuth) {
        console.error('Failed to load dealers for filter:', result.error);
      }
    } catch (error) {
      console.error('Error loading dealers for filter:', error);
    }
  };

  const loadDealersForCampaigns = async () => {
    if (!user?.company_id || !apiCall) return;

    try {
      const params = new URLSearchParams({
        page: '1',
        per_page: '1000',
        company_id: user.company_id.toString()
      });

      const result = await apiCall('get', `/api/dealers?${params.toString()}`);

      if (result.success) {
        setDealersForCampaigns(result.data.dealers || []);
      } else if (!result.requiresAuth) {
        console.error('Failed to load dealers for campaigns:', result.error);
      }
    } catch (error) {
      console.error('Error loading dealers for campaigns:', error);
    }
  };

  const loadAsmsForFilter = async () => {
    if (!user?.company_id || !apiCall) return;

    try {
      const params = new URLSearchParams({
        page: '1',
        per_page: '1000',
        company_id: user.company_id.toString()
      });

      const result = await apiCall('get', `/api/dealers?${params.toString()}`);

      if (result.success) {
        const dealers = result.data.dealers || [];
        // Extract unique ASM owner names
        const uniqueAsms = [...new Set(
          dealers
            .map(dealer => dealer.asm_owner_name)
            .filter(asm => asm && asm.trim()) // Filter out empty/null values
        )].sort();
        setAsmsForFilter(uniqueAsms);
      } else if (!result.requiresAuth) {
        console.error('Failed to load ASMs for filter:', result.error);
      }
    } catch (error) {
      console.error('Error loading ASMs for filter:', error);
    }
  };

  const loadCampaigns = async () => {
    if (!user?.company_id || !apiCall) return;

    setLoadingCampaigns(true);
    setCampaignsError('');

    try {
      const params = new URLSearchParams({
        page: (campaignsPage + 1).toString(),
        per_page: campaignsRowsPerPage.toString()
      });

      if (campaignsSearch.trim()) {
        params.append('search', campaignsSearch.trim());
      }

      const result = await apiCall('get', `/api/campaigns?${params.toString()}`);

      if (result.success) {
        setCampaigns(result.data.campaigns || []);
        setCampaignsTotal(result.data.total || 0);
      } else if (!result.requiresAuth) {
        setCampaignsError(result.error || 'Failed to load campaigns');
      }
    } catch (error) {
      console.error('Error loading campaigns:', error);
      setCampaignsError('Failed to load campaigns');
    }

    setLoadingCampaigns(false);
  };

  // Fetch branding image when user data is available
  useEffect(() => {
    if (user?.company_id && !loading && apiCall) {
      fetchCompanyBrandingImage();
    }
  }, [user?.company_id, loading, apiCall]);

  // Load dashboard data when dashboard tab is selected, or load dashboard campaigns when Manager selects campaigns tab
  useEffect(() => {
    if (user?.company_id && !loading && apiCall) {
      if (selectedTab === 'dashboard') {
        loadDashboardData();
      } else if (isManager && selectedTab === 'campaigns') {
        loadDashboardCampaigns();
      }
    }
  }, [user?.company_id, loading, apiCall, selectedTab, isManager]);

  // Load rewards when user data is available or when rewards tab is selected
  useEffect(() => {
    if (user?.company_id && !loading && apiCall && selectedTab === 'rewards') {
      // Load rewards first, then load budget (so companyRewardProviders is available)
      loadAvailableRewards().then(() => {
        // After rewards are loaded, check if we need to load SMOD balance
        loadRewardsBudget();
      });

      // Check for Tremendous OAuth success message
      const urlParams = new URLSearchParams(window.location.search);
      const tremendousSuccess = urlParams.get('tremendous_success');
      const tremendousMessage = urlParams.get('message');

      if (tremendousSuccess === 'true') {
        if (tremendousMessage === 'already_connected') {
          setSuccess('Tremendous is already connected!');
        } else {
          setSuccess('Tremendous OAuth connection completed successfully!');
        }
        // Reload rewards to refresh the UI
        loadAvailableRewards().then(() => {
          loadRewardsBudget();
        });
        // Reset balance state to trigger auto-load
        setTremendousBalance(null);
        setTremendousBalanceError('');
        balanceLoadAttemptedRef.current = false; // Reset ref to allow reload after reconnection
        // Clean up URL
        window.history.replaceState({}, '', '/manufacturer');
      }
    }
  }, [user?.company_id, loading, apiCall, selectedTab]);

  // Auto-load Tremendous balance when connected (only once per session)
  useEffect(() => {
    if (selectedTab === 'rewards' && companyRewardProviders.length > 0 && user?.company_id) {
      const tremendousProvider = getTremendousProvider();
      const connectionStatus = getTremendousConnectionStatus(tremendousProvider);

      // Auto-load balance if connected, balance hasn't been loaded yet, not currently loading, and no error (to prevent infinite retry loops)
      if (connectionStatus === 'connected' &&
        !tremendousBalance &&
        !loadingTremendousBalance &&
        !tremendousBalanceError &&
        !balanceLoadAttemptedRef.current) {
        balanceLoadAttemptedRef.current = true;
        handleGetTremendousBalance();
      }
    }

    // Reset the ref when switching away from rewards tab or when balance is successfully loaded
    if (selectedTab !== 'rewards' || tremendousBalance) {
      balanceLoadAttemptedRef.current = false;
    }
  }, [selectedTab, companyRewardProviders, user?.company_id, tremendousBalance, loadingTremendousBalance, tremendousBalanceError]);

  // Load dealers when user data is available or when dealers tab is selected
  useEffect(() => {
    if (user?.company_id && !loading && apiCall && selectedTab === 'dealers') {
      loadDealers();
    }
  }, [user?.company_id, loading, apiCall, selectedTab, dealersPage, dealersRowsPerPage, dealersSearch, dealersAsmFilter]);

  // Load ASMs for filter when dealers tab is selected
  useEffect(() => {
    if (user?.company_id && !loading && apiCall && selectedTab === 'dealers') {
      loadAsmsForFilter();
    }
  }, [user?.company_id, loading, apiCall, selectedTab]);

  // Load campaigns when user data is available or when campaigns tab is selected
  useEffect(() => {
    if (user?.company_id && !loading && apiCall && selectedTab === 'campaigns') {
      loadCampaigns();
      loadDealersForCampaigns();
    }
  }, [user?.company_id, loading, apiCall, selectedTab, campaignsPage, campaignsRowsPerPage, campaignsSearch]);

  // Load scheduled reminders when communication tab is scheduled
  useEffect(() => {
    if (user?.company_id && !loading && apiCall && selectedTab === 'communication' && communicationTab === 'scheduled' && campaigns.length > 0) {
      loadAllScheduledReminders();
    }
  }, [user?.company_id, loading, apiCall, selectedTab, communicationTab, campaigns]);

  // Load users when user data is available or when users tab is selected
  useEffect(() => {
    if (user?.company_id && !loading && apiCall && selectedTab === 'users') {
      loadUsers();
    }
  }, [user?.company_id, loading, apiCall, selectedTab, usersPage, usersRowsPerPage, usersSearch, usersDealerFilter, usersRoleFilter, usersAsmFilter, usersSortBy, usersSortOrder]);

  // Load dealers and ASMs for filter when users tab is selected
  useEffect(() => {
    if (user?.company_id && !loading && apiCall && selectedTab === 'users') {
      loadDealersForFilter();
      loadAsmsForFilter();
    }
  }, [user?.company_id, loading, apiCall, selectedTab]);

  // Additional trigger for when user object changes (after login)
  useEffect(() => {
    if (user && user.company_id && !loading && apiCall && !companyBrandingImage) {
      const timer = setTimeout(() => {
        fetchCompanyBrandingImage();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [user, loading, apiCall, companyBrandingImage]);

  // Fallback: retry fetching branding image if it's not loaded after a few seconds
  useEffect(() => {
    if (!companyBrandingImage && user?.company_id && !loading && apiCall) {
      const retryTimer = setTimeout(() => {
        fetchCompanyBrandingImage(true);
      }, 3000);

      return () => clearTimeout(retryTimer);
    }
  }, [companyBrandingImage, user?.company_id, loading, apiCall]);

  // Load quizzes when quiz tab is selected
  useEffect(() => {
    if (user?.company_id && !loading && apiCall && selectedTab === 'quiz') {
      loadQuizzes();
      // Also load campaigns for the dropdown in quiz dialog
      if (campaigns.length === 0) {
        loadCampaigns();
      }
    }
  }, [user?.company_id, loading, apiCall, selectedTab]);

  // Load bonus tasks when bonus task tab is selected
  useEffect(() => {
    if (user?.company_id && !loading && apiCall && selectedTab === 'bonusTask') {
      loadBonusTasks();
      if (campaigns.length === 0) {
        loadCampaigns();
      }
    }
  }, [user?.company_id, loading, apiCall, selectedTab]);

  // Load notification sets when notifications tab is selected
  useEffect(() => {
    if (user?.company_id && !loading && apiCall && selectedTab === 'notifications') {
      loadNotificationSets();
      // Also load campaigns for the dropdown in notification set dialog
      if (campaigns.length === 0) {
        loadCampaigns();
      }
      // Load dealers for campaigns if not already loaded
      if (dealersForCampaigns.length === 0) {
        loadDealersForCampaigns();
      }
    }
  }, [user?.company_id, loading, apiCall, selectedTab]);

  // Load marketing items when marketing tab is selected
  useEffect(() => {
    if (user?.company_id && !loading && apiCall && selectedTab === 'marketing') {
      loadMarketingItems();
    }
  }, [user?.company_id, loading, apiCall, selectedTab]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleDealersPageChange = (event, newPage) => {
    setDealersPage(newPage);
  };

  const handleDealersRowsPerPageChange = (event) => {
    setDealersRowsPerPage(parseInt(event.target.value, 10));
    setDealersPage(0);
  };

  const handleDealersSearchChange = (event) => {
    setDealersSearch(event.target.value);
    setDealersPage(0); // Reset to first page when searching
  };

  const handleDealersAsmFilterChange = (event) => {
    setDealersAsmFilter(event.target.value);
    setDealersPage(0); // Reset to first page when filtering
  };

  const handleConfirmImportDealers = async () => {
    setImportDealersDialogOpen(false);
    setImportingDealers(true);
    setImportError('');
    setImportSuccess('');

    try {
      const payload = importDealersCampaign?.trim() ? { campaign: importDealersCampaign.trim() } : {};
      const result = await apiCall('post', '/api/import-dealers-from-salesforce', payload);

      if (result.success) {
        setImportSuccess(result.data.message);
        // Refresh dealers list
        loadDealers();
      } else if (!result.requiresAuth) {
        setImportError(result.error || 'Failed to import dealers');
      }
    } catch (error) {
      console.error('Error importing dealers:', error);
      setImportError('Failed to import dealers');
    }

    setImportingDealers(false);
  };

  const handleUpdateDealersFromSalesforce = async (mode) => {
    setUpdateDealersDialogOpen(false);
    setUpdatingDealers(true);
    setUpdateDealersError('');
    setUpdateDealersSuccess('');

    try {
      const payload = {
        mode,
        ...(importDealersCampaign?.trim() ? { campaign: importDealersCampaign.trim() } : {})
      };
      const result = await apiCall('post', '/api/update-dealers-from-salesforce', payload);

      if (result.success) {
        setUpdateDealersSuccess(result.data.message);
        loadDealers();
      } else if (!result.requiresAuth) {
        setUpdateDealersError(result.error || 'Failed to update dealers');
      }
    } catch (error) {
      console.error('Error updating dealers:', error);
      setUpdateDealersError('Failed to update dealers');
    }

    setUpdatingDealers(false);
  };

  const handleUsersPageChange = (event, newPage) => {
    setUsersPage(newPage);
  };

  const handleUsersRowsPerPageChange = (event) => {
    setUsersRowsPerPage(parseInt(event.target.value, 10));
    setUsersPage(0);
  };

  const handleUsersSearchChange = (event) => {
    setUsersSearch(event.target.value);
    setUsersPage(0); // Reset to first page when searching
  };

  const handleUsersDealerFilterChange = (event) => {
    setUsersDealerFilter(event.target.value);
    setUsersPage(0); // Reset to first page when filtering
  };

  const handleUsersRoleFilterChange = (event) => {
    setUsersRoleFilter(event.target.value);
    setUsersPage(0); // Reset to first page when filtering
  };

  const handleUsersAsmFilterChange = (event) => {
    setUsersAsmFilter(event.target.value);
    setUsersPage(0); // Reset to first page when filtering
  };

  const handleCampaignsPageChange = (event, newPage) => {
    setCampaignsPage(newPage);
  };

  const handleCampaignsRowsPerPageChange = (event) => {
    setCampaignsRowsPerPage(parseInt(event.target.value, 10));
    setCampaignsPage(0);
  };

  const handleCampaignsSearchChange = (event) => {
    setCampaignsSearch(event.target.value);
    setCampaignsPage(0); // Reset to first page when searching
  };

  // Campaign dialog handlers
  const handleAddCampaignOpen = () => {
    if (!user.company_id) {
      setImportError('You must be associated with a company to create campaigns');
      setTimeout(() => setImportError(''), 5000);
      return;
    }

    // Load dealers for campaigns if not already loaded
    if (dealersForCampaigns.length === 0) {
      loadDealersForCampaigns();
    }

    setEditingCampaign(null);
    setAddCampaignOpen(true);
    setCampaignFormData({
      name: '',
      campaign_tag: '',
      description: '',
      header: '',
      type: 'POINTS',
      status: 'DRAFT',
      campaign_image: '',
      start_date: null,
      end_date: null,
      reward_ids: [],
      dealer_ids: [],
      // Column visibility settings
      show_value_1: true,
      show_value_2: true,
      show_value_3: true,
      show_status: true,
      // Custom column labels
      value_1_label: 'Value 1',
      value_2_label: 'Value 2',
      value_3_label: 'Value 3'
    });
    setCampaignFormErrors({});
  };

  const handleEditCampaignOpen = (campaign) => {
    if (!user.company_id) {
      setImportError('You must be associated with a company to edit campaigns');
      setTimeout(() => setImportError(''), 5000);
      return;
    }

    // Load dealers for campaigns if not already loaded
    if (dealersForCampaigns.length === 0) {
      loadDealersForCampaigns();
    }

    // Load extra modules if not already loaded
    if (companyExtraModules.length === 0) {
      loadCompanyExtraModules();
    }

    setEditingCampaign(campaign);
    setAddCampaignOpen(true);
    setCampaignFormData({
      name: campaign.name || '',
      campaign_tag: campaign.campaign_tag || '',
      description: campaign.description || '',
      header: campaign.header || '',
      type: campaign.type || 'POINTS',
      status: campaign.status || 'DRAFT',
      campaign_image: campaign.campaign_image || '',
      campaign_image_url: campaign.campaign_image ? `${CDN_URL}/${campaign.campaign_image}` : '',
      start_date: campaignDateApiToFormValue(campaign.start_date),
      end_date: campaignDateApiToFormValue(campaign.end_date),
      reward_ids: campaign.rewards ? campaign.rewards.map(r => r.id) : [],
      dealer_ids: campaign.dealers ? campaign.dealers.map(d => d.id) : [],
      company_extra_module_id: campaign.company_extra_module_id !== undefined && campaign.company_extra_module_id !== null ? Number(campaign.company_extra_module_id) : null,
      // Column visibility settings
      show_value_1: campaign.show_value_1 !== undefined ? campaign.show_value_1 : true,
      show_value_2: campaign.show_value_2 !== undefined ? campaign.show_value_2 : true,
      show_value_3: campaign.show_value_3 !== undefined ? campaign.show_value_3 : true,
      show_status: campaign.show_status !== undefined ? campaign.show_status : true,
      // Custom column labels
      value_1_label: campaign.value_1_label || 'Value 1',
      value_2_label: campaign.value_2_label || 'Value 2',
      value_3_label: campaign.value_3_label || 'Value 3',
      show_in_leaderboards: campaign.show_in_leaderboards ?? false
    });
    setCampaignFormErrors({});
  };

  const handleCopyCampaignOpen = (campaign) => {
    if (!user.company_id) {
      setImportError('You must be associated with a company to copy campaigns');
      setTimeout(() => setImportError(''), 5000);
      return;
    }

    // Load dealers for campaigns if not already loaded
    if (dealersForCampaigns.length === 0) {
      loadDealersForCampaigns();
    }

    // Generate unique name with " - Copy N" suffix
    const baseName = campaign.name;
    let copyNumber = 1;
    let newName = `${baseName} - Copy ${copyNumber}`;

    while (campaigns.some(c => c.name === newName)) {
      copyNumber++;
      newName = `${baseName} - Copy ${copyNumber}`;
    }

    // Set as new campaign (editingCampaign = null)
    setEditingCampaign(null);
    setAddCampaignOpen(true);
    setCampaignFormData({
      name: newName,
      campaign_tag: campaign.campaign_tag || '',
      description: campaign.description || '',
      header: campaign.header || '',
      type: campaign.type || 'POINTS',
      status: 'DRAFT', // Always set copied campaigns to DRAFT
      campaign_image: campaign.campaign_image || '',
      campaign_image_url: campaign.campaign_image ? `${CDN_URL}/${campaign.campaign_image}` : '',
      start_date: campaignDateApiToFormValue(campaign.start_date),
      end_date: campaignDateApiToFormValue(campaign.end_date),
      reward_ids: campaign.rewards ? campaign.rewards.map(r => r.id) : [],
      dealer_ids: campaign.dealers ? campaign.dealers.map(d => d.id) : [],
      company_extra_module_id: campaign.company_extra_module_id !== undefined && campaign.company_extra_module_id !== null ? Number(campaign.company_extra_module_id) : null,
      show_value_1: campaign.show_value_1 !== undefined ? campaign.show_value_1 : true,
      show_value_2: campaign.show_value_2 !== undefined ? campaign.show_value_2 : true,
      show_value_3: campaign.show_value_3 !== undefined ? campaign.show_value_3 : true,
      show_status: campaign.show_status !== undefined ? campaign.show_status : true,
      value_1_label: campaign.value_1_label || 'Value 1',
      value_2_label: campaign.value_2_label || 'Value 2',
      value_3_label: campaign.value_3_label || 'Value 3',
      show_in_leaderboards: campaign.show_in_leaderboards ?? false
    });
    setCampaignFormErrors({});
  };

  const handleDeleteCampaignOpen = (campaign) => {
    setCampaignToDelete(campaign);
    setDeleteCampaignDialog(true);
  };

  const handleDeleteCampaignClose = () => {
    setDeleteCampaignDialog(false);
    setCampaignToDelete(null);
  };

  const handleDeleteCampaignSuccess = () => {
    setImportSuccess('Campaign deleted successfully');
    setTimeout(() => setImportSuccess(''), 5000);
    loadCampaigns();
  };

  const handleAddCampaignClose = () => {
    setAddCampaignOpen(false);
    setEditingCampaign(null);
    setCampaignFormData({
      name: '',
      campaign_tag: '',
      description: '',
      header: '',
      type: 'POINTS',
      status: 'DRAFT',
      campaign_image: '',
      start_date: null,
      end_date: null,
      reward_ids: [],
      dealer_ids: [],
      company_extra_module_id: null,
      // Column visibility settings
      show_value_1: true,
      show_value_2: true,
      show_value_3: true,
      show_status: true,
      // Custom column labels
      value_1_label: 'Value 1',
      value_2_label: 'Value 2',
      value_3_label: 'Value 3',
      show_in_leaderboards: false
    });
    setCampaignFormErrors({});
  };

  const handleCampaignFormChange = (field, value) => {
    setCampaignFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field
    if (campaignFormErrors[field]) {
      setCampaignFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateCampaignForm = () => {
    const errors = {};

    if (!campaignFormData.name.trim()) {
      errors.name = 'Campaign name is required';
    }

    if (!campaignFormData.type) {
      errors.type = 'Campaign type is required';
    }

    if (campaignFormData.type === 'EXTRA' && !campaignFormData.company_extra_module_id) {
      errors.company_extra_module_id = 'Extra Module is required for Extra Campaign type';
    }

    if (!user.company_id) {
      errors.submit = 'User must be associated with a company to create campaigns';
    }

    return errors;
  };

  const performCampaignSubmit = async (payload, emailOptions = {}) => {
    const method = editingCampaign ? 'put' : 'post';
    const endpoint = editingCampaign ? `/api/campaigns/${editingCampaign.id}` : '/api/campaigns';
    const finalPayload = { ...payload, ...emailOptions };
    const serializeCampaignInstantForApi = (v) => {
      if (v == null) return v;
      return dayjs.isDayjs(v) ? v.utc().toISOString() : dayjs.utc(v).toISOString();
    };
    if (finalPayload.start_date) {
      finalPayload.start_date = serializeCampaignInstantForApi(finalPayload.start_date);
    }
    if (finalPayload.end_date) {
      finalPayload.end_date = serializeCampaignInstantForApi(finalPayload.end_date);
    }
    const result = await apiCall(method, endpoint, finalPayload);
    return result;
  };

  const handleCampaignSubmit = async () => {
    const errors = validateCampaignForm();

    if (Object.keys(errors).length > 0) {
      setCampaignFormErrors(errors);
      return;
    }

    // Include user's company_id in campaign data
    const campaignDataWithCompany = {
      ...campaignFormData,
      company_id: user.company_id
    };

    // Remove URL field before sending to API
    delete campaignDataWithCompany.campaign_image_url;

    // Editing RUNNING campaign with new dealers added: show popup first
    if (editingCampaign && campaignFormData.status === 'RUNNING') {
      const originalDealerIds = (editingCampaign.dealers || []).map(d => d.id);
      const newDealerIds = campaignFormData.dealer_ids.filter(id => !originalDealerIds.includes(id));
      if (newDealerIds.length > 0) {
        setPendingCampaignSubmitData(campaignDataWithCompany);
        setPendingNewDealerIds(newDealerIds);
        setSendEmailsToNewDealers(true);
        setNewDealersEmailDialogOpen(true);
        return;
      }
    }

    setSubmittingCampaign(true);

    try {
      const result = await performCampaignSubmit(campaignDataWithCompany);

      if (result.success) {
        handleAddCampaignClose();
        const successMessage = editingCampaign ? 'Campaign updated successfully!' : 'Campaign created successfully!';
        setImportSuccess(successMessage);
        setTimeout(() => setImportSuccess(''), 3000);
        loadCampaigns();
      } else if (!result.requiresAuth) {
        const errorMessage = editingCampaign ? 'Failed to update campaign' : 'Failed to create campaign';
        setCampaignFormErrors({ submit: result.error || errorMessage });
      }
    } catch (error) {
      console.error('Error saving campaign:', error);
      const errorMessage = editingCampaign ? 'Failed to update campaign' : 'Failed to create campaign';
      setCampaignFormErrors({ submit: errorMessage });
    }

    setSubmittingCampaign(false);
  };

  const handleConfirmNewDealersEmailDialog = async () => {
    if (!pendingCampaignSubmitData || !apiCall) return;

    setSubmittingCampaign(true);
    setNewDealersEmailDialogOpen(false);

    const emailOptions = {
      send_start_emails: sendEmailsToNewDealers,
      send_start_emails_to_dealer_ids: sendEmailsToNewDealers ? pendingNewDealerIds : []
    };

    try {
      const result = await performCampaignSubmit(pendingCampaignSubmitData, emailOptions);

      if (result.success) {
        handleAddCampaignClose();
        setImportSuccess('Campaign updated successfully!');
        setTimeout(() => setImportSuccess(''), 3000);
        loadCampaigns();
      } else if (!result.requiresAuth) {
        setCampaignFormErrors({ submit: result.error || 'Failed to update campaign' });
      }
    } catch (error) {
      console.error('Error saving campaign:', error);
      setCampaignFormErrors({ submit: 'Failed to update campaign' });
    }

    setPendingCampaignSubmitData(null);
    setPendingNewDealerIds([]);
    setSubmittingCampaign(false);
  };

  const handleCancelNewDealersEmailDialog = () => {
    setNewDealersEmailDialogOpen(false);
    setPendingCampaignSubmitData(null);
    setPendingNewDealerIds([]);
    setSendEmailsToNewDealers(true);
  };

  // Campaign detail view handlers
  const loadCampaignDetail = async (campaignId) => {
    if (!campaignId || !apiCall) return;

    setLoadingCampaignDetail(true);
    setCampaignDetailError('');

    try {
      const result = await apiCall('get', `/api/campaigns/${campaignId}`);

      if (result.success) {
        setSelectedCampaignDetail(result.data);
        // Load campaign goals, quizzes, and voucher requests
        loadCampaignGoals(campaignId);
        loadCampaignQuizzes(campaignId);
        loadVoucherRequests(campaignId);
      } else if (!result.requiresAuth) {
        setCampaignDetailError(result.error || 'Failed to load campaign details');
      }
    } catch (error) {
      console.error('Error loading campaign details:', error);
      setCampaignDetailError('Failed to load campaign details');
    }

    setLoadingCampaignDetail(false);
  };

  const handleCampaignDetailOpen = (campaign) => {
    setViewingCampaignDetail(true);
    loadCampaignDetail(campaign.id);
  };

  const handleCampaignDetailClose = () => {
    setViewingCampaignDetail(false);
    setSelectedCampaignDetail(null);
    setCampaignDetailError('');
    setCampaignGoals([]);
    setVoucherRequests([]);
    setVoucherRequestsError('');
    setSyncError('');
    setSyncSuccess('');
    setUpdatingCampaignStatus(false);
    setMatchScoreDialogOpen(false);
    setMatchScoreDialogGame(null);
    setMatchScoreEditIndex(null);
    setMatchScoreTeamA('');
    setMatchScoreTeamB('');
    setMatchScoreError('');
  };

  const handleCampaignChartOpen = (campaign) => {
    setSelectedCampaignChart(campaign);
    setChartDays(28);
    setViewingCampaignChart(true);
  };

  const handleCampaignChartClose = () => {
    setViewingCampaignChart(false);
    setSelectedCampaignChart(null);
    setChartDays(28);
  };

  const handleCampaignDetails = (campaignId) => {
    // Switch to campaigns tab and open campaign details
    setSelectedTab('campaigns');
    setViewingCampaignDetail(true);
    loadCampaignDetail(campaignId);
  };

  const handleMenuItemClick = (tabKey) => {
    setSelectedTab(tabKey);
    // Reset campaign detail view when navigating to campaigns or any other tab
    if (viewingCampaignDetail) {
      setViewingCampaignDetail(false);
      setSelectedCampaignDetail(null);
      setCampaignDetailError('');
      setMatchScoreDialogOpen(false);
      setMatchScoreDialogGame(null);
      setMatchScoreEditIndex(null);
      setMatchScoreTeamA('');
      setMatchScoreTeamB('');
      setMatchScoreError('');
    }
    if (viewingCampaignChart) {
      setViewingCampaignChart(false);
      setSelectedCampaignChart(null);
      setChartDays(28);
    }
    // Reset notifications view when navigating away from notifications
    if (viewingTemplates && tabKey !== 'notifications') {
      setViewingTemplates(false);
      setSelectedNotificationSet(null);
      setEmailTemplates([]);
      setEmailTemplatesError('');
    }
  };

  // Campaign synchronization handlers
  const formatLeaderboardBonusStats = (stats) => {
    if (!stats) return '';
    if (stats.skipped === 'before_final') {
      return 'Leaderboard bonuses not applied: final match kickoff has not passed yet.';
    }
    const appliedLines = (stats.applied || []).map(
      (entry) => `#${entry.rank} ${entry.dealer_name || 'Dealer'}: +${entry.points} pts`
    );
    const removedCount = (stats.removed || []).length;
    if (appliedLines.length === 0 && removedCount === 0) {
      return 'Leaderboard bonuses are already up to date.';
    }
    const parts = [];
    if (appliedLines.length) parts.push(`Applied: ${appliedLines.join('; ')}`);
    if (removedCount) parts.push(`Removed: ${removedCount} outdated bonus(es)`);
    return parts.join(' ');
  };

  const handleApplyLeaderboardBonuses = async () => {
    if (!selectedCampaignDetail || !apiCall) return;

    setApplyingLeaderboardBonuses(true);
    setLeaderboardBonusError('');
    setLeaderboardBonusMessage('');

    try {
      const result = await apiCall(
        'post',
        `/api/campaigns/${selectedCampaignDetail.id}/apply-leaderboard-bonuses`
      );

      if (result.success) {
        setLeaderboardBonusMessage(formatLeaderboardBonusStats(result.data));
        await loadCampaignGoals(selectedCampaignDetail.id);
        setTimeout(() => setLeaderboardBonusMessage(''), 8000);
      } else if (!result.requiresAuth) {
        setLeaderboardBonusError(result.error || 'Failed to apply leaderboard bonuses');
      }
    } catch (error) {
      console.error('Error applying leaderboard bonuses:', error);
      setLeaderboardBonusError('Failed to apply leaderboard bonuses');
    }

    setApplyingLeaderboardBonuses(false);
  };

  const handleSynchronizePoints = async () => {
    if (!selectedCampaignDetail || !user?.company_id || !apiCall) return;

    setSyncingPoints(true);
    setSyncError('');
    setSyncSuccess('');

    try {
      const result = await apiCall('post', `/api/campaigns/${selectedCampaignDetail.id}/sync-points`);

      if (result.success) {
        const bonusNote = formatLeaderboardBonusStats(result.data?.leaderboard_bonus_stats);
        setSyncSuccess(
          bonusNote
            ? `Points synchronized successfully! ${bonusNote}`
            : 'Points synchronized successfully!'
        );
        // Reload campaign goals to ensure fresh data
        await loadCampaignGoals(selectedCampaignDetail.id);
        setTimeout(() => setSyncSuccess(''), 8000);
      } else if (!result.requiresAuth) {
        setSyncError(result.error || 'Failed to synchronize points');
      }
    } catch (error) {
      console.error('Error synchronizing points:', error);
      setSyncError('Failed to synchronize points');
    }

    setSyncingPoints(false);
  };

  const loadCampaignGoals = async (campaignId) => {
    if (!campaignId || !apiCall) return;

    try {
      const result = await apiCall('get', `/api/campaigns/${campaignId}/goals`);

      if (result.success) {
        setCampaignGoals(result.data.goals || []);
      } else if (!result.requiresAuth) {
        console.error('Failed to load campaign goals:', result.error);
      }
    } catch (error) {
      console.error('Error loading campaign goals:', error);
    }
  };

  const getOfficialMatchScoreText = (game) => {
    if (!game) return null;
    const a = game.scoreTeamA;
    const b = game.scoreTeamB;
    if (a == null || b == null) return null;
    const na = Number(a);
    const nb = Number(b);
    if (!Number.isFinite(na) || !Number.isFinite(nb)) return null;
    if (!Number.isInteger(na) || !Number.isInteger(nb) || na < 0 || nb < 0) return null;
    return `${na} : ${nb}`;
  };

  const closeMatchScoreDialog = () => {
    setMatchScoreDialogOpen(false);
    setMatchScoreDialogGame(null);
    setMatchScoreEditIndex(null);
    setMatchScoreTeamA('');
    setMatchScoreTeamB('');
    setMatchScoreError('');
  };

  const openMatchScoreDialog = (game, idx) => {
    setMatchScoreDialogGame(game);
    setMatchScoreEditIndex(idx);
    const hasA = game.scoreTeamA !== undefined && game.scoreTeamA !== null && String(game.scoreTeamA).trim() !== '';
    const hasB = game.scoreTeamB !== undefined && game.scoreTeamB !== null && String(game.scoreTeamB).trim() !== '';
    setMatchScoreTeamA(hasA ? String(game.scoreTeamA) : '');
    setMatchScoreTeamB(hasB ? String(game.scoreTeamB) : '');
    setMatchScoreError('');
    setMatchScoreDialogOpen(true);
  };

  const closeMatchPredictionsDialog = () => {
    setMatchPredictionsDialogOpen(false);
    setMatchPredictionsDialogGame(null);
    setMatchPredictionsDialogIndex(null);
  };

  const openMatchPredictionsDialog = (game, idx) => {
    setMatchPredictionsDialogGame(game);
    setMatchPredictionsDialogIndex(idx);
    setMatchPredictionsDialogOpen(true);
  };

  const persistExtraModuleGames = async (nextGames) => {
    const relationshipId = selectedCampaignDetail?.company_extra_module_id;
    if (!relationshipId || !apiCall) return false;
    const row = companyExtraModules?.find((m) => m.id === relationshipId);
    if (!row) {
      setMatchScoreError('Extra module configuration not found.');
      return false;
    }
    const existingConfig = row.config && typeof row.config === 'object' ? { ...row.config } : {};
    const nextConfig = { ...existingConfig, games: nextGames };
    const result = await apiCall('put', `/api/company-extra-modules/${relationshipId}`, {
      config: nextConfig
    });
    if (result.success) {
      await loadCompanyExtraModules();
      return true;
    }
    if (!result.requiresAuth) {
      setMatchScoreError(result.error || 'Failed to save match score');
    }
    return false;
  };

  const handleSaveMatchScore = async () => {
    if (matchScoreEditIndex == null || !selectedCampaignDetail || !apiCall) return;
    const parseGoal = (raw) => {
      const t = String(raw ?? '').trim();
      if (t === '') return Number.NaN;
      const n = Number(t);
      if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) return Number.NaN;
      return n;
    };
    const a = parseGoal(matchScoreTeamA);
    const b = parseGoal(matchScoreTeamB);
    if (Number.isNaN(a) || Number.isNaN(b)) {
      setMatchScoreError('Enter valid non-negative whole numbers for both teams.');
      return;
    }
    const moduleConfig = companyExtraModules?.find((m) => m.id === selectedCampaignDetail.company_extra_module_id)?.config || {};
    const games = [...(moduleConfig.games || [])];
    if (matchScoreEditIndex < 0 || matchScoreEditIndex >= games.length) {
      setMatchScoreError('Invalid match.');
      return;
    }
    const prev = games[matchScoreEditIndex];
    games[matchScoreEditIndex] = { ...prev, scoreTeamA: a, scoreTeamB: b };
    setMatchScoreSaving(true);
    setMatchScoreError('');
    try {
      const ok = await persistExtraModuleGames(games);
      if (ok) {
        const modRow = companyExtraModules?.find((m) => m.id === selectedCampaignDetail.company_extra_module_id);
        const isTournamentModule =
          selectedCampaignDetail?.type === 'EXTRA' &&
          modRow?.extra_module?.name &&
          String(modRow.extra_module.name).toLowerCase() === 'tournament';
        if (isTournamentModule && apiCall) {
          const syncResult = await apiCall('post', `/api/campaigns/${selectedCampaignDetail.id}/sync-points`);
          if (syncResult.success) {
            await loadCampaignGoals(selectedCampaignDetail.id);
          }
        }
        closeMatchScoreDialog();
      }
    } finally {
      setMatchScoreSaving(false);
    }
  };

  const handleDeleteMatchScore = async () => {
    if (matchScoreEditIndex == null || !selectedCampaignDetail || !apiCall) return;
    const moduleConfig = companyExtraModules?.find((m) => m.id === selectedCampaignDetail.company_extra_module_id)?.config || {};
    const games = [...(moduleConfig.games || [])];
    if (matchScoreEditIndex < 0 || matchScoreEditIndex >= games.length) {
      setMatchScoreError('Invalid match.');
      return;
    }
    const prev = games[matchScoreEditIndex];
    const { scoreTeamA: _a, scoreTeamB: _b, ...rest } = prev;
    games[matchScoreEditIndex] = rest;
    setMatchScoreSaving(true);
    setMatchScoreError('');
    try {
      const ok = await persistExtraModuleGames(games);
      if (ok) {
        const modRow = companyExtraModules?.find((m) => m.id === selectedCampaignDetail.company_extra_module_id);
        const isTournamentModule =
          selectedCampaignDetail?.type === 'EXTRA' &&
          modRow?.extra_module?.name &&
          String(modRow.extra_module.name).toLowerCase() === 'tournament';
        if (isTournamentModule && apiCall) {
          const syncResult = await apiCall('post', `/api/campaigns/${selectedCampaignDetail.id}/sync-points`);
          if (syncResult.success) {
            await loadCampaignGoals(selectedCampaignDetail.id);
          }
        }
        closeMatchScoreDialog();
      }
    } finally {
      setMatchScoreSaving(false);
    }
  };

  const openManualAdjustDialog = (goal) => {
    setManualAdjustGoal(goal);
    setManualAdjustValues({
      manual_points: String(Number(goal.manual_points ?? 0)),
      manual_value_1: String(Number(goal.manual_value_1 ?? 0)),
      manual_value_2: String(Number(goal.manual_value_2 ?? 0)),
      manual_value_3: String(Number(goal.manual_value_3 ?? 0))
    });
    setManualAdjustError('');
    setManualAdjustOpen(true);
  };

  const closeManualAdjustDialog = () => {
    setManualAdjustOpen(false);
    setManualAdjustGoal(null);
    setManualAdjustError('');
  };

  const handleManualAdjustValueChange = (field, value) => {
    setManualAdjustValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmitManualAdjust = async () => {
    if (!manualAdjustGoal || !selectedCampaignDetail || !apiCall) return;

    const parseManual = (raw) => {
      const t = String(raw ?? '').trim();
      if (t === '') return 0;
      const n = Number(t);
      if (!Number.isFinite(n) || n < 0) return NaN;
      return n;
    };

    const manual_points = parseManual(manualAdjustValues.manual_points);
    const manual_value_1 = parseManual(manualAdjustValues.manual_value_1);
    const manual_value_2 = parseManual(manualAdjustValues.manual_value_2);
    const manual_value_3 = parseManual(manualAdjustValues.manual_value_3);

    if (Number.isNaN(manual_points)) {
      setManualAdjustError('Enter a valid non-negative number for manual points.');
      return;
    }
    if (selectedCampaignDetail.show_value_1 && Number.isNaN(manual_value_1)) {
      setManualAdjustError(`Enter a valid non-negative number for ${selectedCampaignDetail.value_1_label || 'Value 1'}.`);
      return;
    }
    if (selectedCampaignDetail.show_value_2 && Number.isNaN(manual_value_2)) {
      setManualAdjustError(`Enter a valid non-negative number for ${selectedCampaignDetail.value_2_label || 'Value 2'}.`);
      return;
    }
    if (selectedCampaignDetail.show_value_3 && Number.isNaN(manual_value_3)) {
      setManualAdjustError(`Enter a valid non-negative number for ${selectedCampaignDetail.value_3_label || 'Value 3'}.`);
      return;
    }

    const body = { manual_points };
    if (selectedCampaignDetail.show_value_1) body.manual_value_1 = manual_value_1;
    if (selectedCampaignDetail.show_value_2) body.manual_value_2 = manual_value_2;
    if (selectedCampaignDetail.show_value_3) body.manual_value_3 = manual_value_3;

    setManualAdjustSaving(true);
    setManualAdjustError('');

    try {
      const result = await apiCall(
        'patch',
        `/api/goals/${manualAdjustGoal.id}/manual-adjustments`,
        body
      );

      if (result.success) {
        await loadCampaignGoals(selectedCampaignDetail.id);
        loadDashboardCampaigns();
        closeManualAdjustDialog();
      } else if (!result.requiresAuth) {
        setManualAdjustError(result.error || 'Failed to save adjustments');
      }
    } catch (e) {
      console.error('Manual goal adjust error:', e);
      setManualAdjustError('Failed to save adjustments');
    } finally {
      setManualAdjustSaving(false);
    }
  };

  const goalHasManualExtras = (g) =>
    (Number(g?.manual_points) || 0) > 0 ||
    (Number(g?.manual_value_1) || 0) > 0 ||
    (Number(g?.manual_value_2) || 0) > 0 ||
    (Number(g?.manual_value_3) || 0) > 0;

  const loadCampaignQuizzes = async (campaignId) => {
    if (!campaignId || !apiCall) return;

    setLoadingCampaignQuizzes(true);
    setCampaignQuizzesError('');

    try {
      const result = await apiCall('get', `/api/quizzes?campaign_id=${campaignId}&per_page=1000`);

      if (result.success) {
        const quizzes = result.data.quizzes || [];
        console.log(`Loaded ${quizzes.length} quizzes for campaign ${campaignId}:`, quizzes);
        setCampaignQuizzes(quizzes);

        // Load statistics for each quiz
        quizzes.forEach(quiz => {
          loadQuizStatistics(quiz.id, 1, 5);
        });
      } else if (!result.requiresAuth) {
        setCampaignQuizzesError(result.error || 'Failed to load campaign quizzes');
      }
    } catch (error) {
      console.error('Error loading campaign quizzes:', error);
      setCampaignQuizzesError('Error loading quizzes');
    } finally {
      setLoadingCampaignQuizzes(false);
    }
  };

  const loadQuizStatistics = async (quizId, page = 1, perPage = 5) => {
    if (!quizId || !apiCall) return;

    // Set loading state for this specific quiz
    setQuizStatsLoading(prev => ({ ...prev, [quizId]: true }));

    try {
      const result = await apiCall('get', `/api/quizzes/${quizId}/statistics?page=${page}&per_page=${perPage}`);

      if (result.success) {
        setQuizStatistics(prev => ({ ...prev, [quizId]: result.data }));

        // Initialize pagination state if not set
        if (!quizStatsPage[quizId]) {
          setQuizStatsPage(prev => ({ ...prev, [quizId]: page - 1 }));
        }
        if (!quizStatsRowsPerPage[quizId]) {
          setQuizStatsRowsPerPage(prev => ({ ...prev, [quizId]: perPage }));
        }
      }
    } catch (error) {
      console.error(`Error loading statistics for quiz ${quizId}:`, error);
    } finally {
      setQuizStatsLoading(prev => ({ ...prev, [quizId]: false }));
    }
  };

  const loadVoucherRequests = async (campaignId) => {
    if (!campaignId || !apiCall) return;

    setLoadingVoucherRequests(true);
    setVoucherRequestsError('');

    try {
      const result = await apiCall('get', `/api/vouchers/requests?campaign_id=${campaignId}`);

      if (result.success) {
        setVoucherRequests(result.data.voucher_requests || []);
      } else if (!result.requiresAuth) {
        setVoucherRequestsError(result.error || 'Failed to load voucher requests');
      }
    } catch (error) {
      console.error('Error loading voucher requests:', error);
      setVoucherRequestsError('Failed to load voucher requests');
    }

    setLoadingVoucherRequests(false);
  };

  const handleGoalsSortChange = (column) => {
    if (goalsSortColumn === column) {
      // Toggle direction if clicking the same column
      setGoalsSortDirection(goalsSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to descending
      setGoalsSortColumn(column);
      setGoalsSortDirection('desc');
    }
  };

  // Get unique ASM owners from campaign goals
  const getUniqueAsmOwners = () => {
    if (!campaignGoals || campaignGoals.length === 0) return [];

    const connectedDealerIds = selectedCampaignDetail?.dealers?.map(d => d.id) || [];
    const filteredGoals = campaignGoals.filter(goal =>
      connectedDealerIds.includes(goal.dealer_id)
    );

    const asmOwners = new Set();
    filteredGoals.forEach(goal => {
      if (goal.dealer?.asm_owner_name) {
        asmOwners.add(goal.dealer.asm_owner_name);
      }
    });

    return Array.from(asmOwners).sort();
  };

  const getSortedGoals = () => {
    if (!campaignGoals || campaignGoals.length === 0) return [];

    // Filter goals to only show dealers currently connected to the campaign
    const connectedDealerIds = selectedCampaignDetail?.dealers?.map(d => d.id) || [];
    let filteredGoals = campaignGoals.filter(goal =>
      connectedDealerIds.includes(goal.dealer_id)
    );

    // Apply ASM filter if not 'all'
    if (asmFilter && asmFilter !== 'all') {
      filteredGoals = filteredGoals.filter(goal =>
        goal.dealer?.asm_owner_name === asmFilter
      );
    }

    const sorted = [...filteredGoals].sort((a, b) => {
      let aValue, bValue;

      switch (goalsSortColumn) {
        case 'dealer':
          aValue = (a.dealer?.name || '').toLowerCase();
          bValue = (b.dealer?.name || '').toLowerCase();
          break;
        case 'asm_owner':
          aValue = (a.dealer?.asm_owner_name || '').toLowerCase();
          bValue = (b.dealer?.asm_owner_name || '').toLowerCase();
          break;
        case 'points':
          aValue = goalEarnedTotal(a);
          bValue = goalEarnedTotal(b);
          break;
        case 'used':
          aValue = parseFloat(a.used_points) || 0;
          bValue = parseFloat(b.used_points) || 0;
          break;
        case 'value_1':
          aValue = parseFloat(a.value_1) || 0;
          bValue = parseFloat(b.value_1) || 0;
          break;
        case 'value_2':
          aValue = parseFloat(a.value_2) || 0;
          bValue = parseFloat(b.value_2) || 0;
          break;
        case 'value_3':
          aValue = parseFloat(a.value_3) || 0;
          bValue = parseFloat(b.value_3) || 0;
          break;
        case 'status':
          // Count completed statuses for sorting
          aValue = (a.done_1 ? 1 : 0) + (a.done_2 ? 1 : 0) + (a.done_3 ? 1 : 0);
          bValue = (b.done_1 ? 1 : 0) + (b.done_2 ? 1 : 0) + (b.done_3 ? 1 : 0);
          break;
        case 'last_login':
          aValue = a.last_login ? new Date(a.last_login).getTime() : 0;
          bValue = b.last_login ? new Date(b.last_login).getTime() : 0;
          break;
        case 'last_points_gain_at':
          aValue = a.last_points_gain_at ? new Date(a.last_points_gain_at).getTime() : 0;
          bValue = b.last_points_gain_at ? new Date(b.last_points_gain_at).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string') {
        return goalsSortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return goalsSortDirection === 'asc'
          ? aValue - bValue
          : bValue - aValue;
      }
    });

    return sorted;
  };

  const handleTournamentExportExcel = async () => {
    if (!selectedCampaignDetail?.id || !apiCall) return;

    setTournamentExporting(true);
    setError('');
    try {
      const result = await apiCall(
        'get',
        `/api/campaigns/${selectedCampaignDetail.id}/tournament-export-stats`
      );
      if (result.success) {
        downloadTournamentExportExcel(result.data);
        setSuccess('Tournament campaign stats exported to Excel.');
      } else if (!result.requiresAuth) {
        setError(result.error || 'Failed to export tournament stats');
      }
    } catch (err) {
      console.error('Tournament export failed:', err);
      setError('Failed to export tournament stats');
    } finally {
      setTournamentExporting(false);
    }
  };

  const handleActiveDealersExportExcel = async () => {
    if (!selectedCampaignDetail?.id || !apiCall) return;

    setActiveDealersExporting(true);
    setError('');
    try {
      const result = await apiCall(
        'get',
        `/api/campaigns/${selectedCampaignDetail.id}/tournament-active-dealers`
      );
      if (result.success) {
        downloadActiveDealersExportExcel(result.data);
        setSuccess('Active dealers exported to Excel.');
      } else if (!result.requiresAuth) {
        setError(result.error || 'Failed to export active dealers');
      }
    } catch (err) {
      console.error('Active dealers export failed:', err);
      setError('Failed to export active dealers');
    } finally {
      setActiveDealersExporting(false);
    }
  };

  const getDealerPerformanceFooterStats = (rows) => {
    const list = rows || [];
    return {
      sumPoints: list.reduce((s, g) => s + goalEarnedTotal(g), 0),
      sumUsed: list.reduce((s, g) => s + (parseFloat(g.used_points) || 0), 0),
      sumValue1: list.reduce((s, g) => s + (parseFloat(g.value_1) || 0), 0),
      sumValue2: list.reduce((s, g) => s + (parseFloat(g.value_2) || 0), 0),
      sumValue3: list.reduce((s, g) => s + (parseFloat(g.value_3) || 0), 0),
      statusDone1: list.filter((g) => g.done_1).length,
      statusDone2: list.filter((g) => g.done_2).length,
      statusDone3: list.filter((g) => g.done_3).length,
      loggedInCount: list.filter((g) => Boolean(g.last_login)).length,
      dealerCount: list.length
    };
  };

  // Campaign status management handlers
  const handleStartCampaign = () => {
    // Open the confirmation dialog instead of directly starting
    setStartCampaignDialogOpen(true);
  };

  const handleConfirmStartCampaign = async () => {
    if (!selectedCampaignDetail || !apiCall) return;

    setUpdatingCampaignStatus(true);
    setSyncError('');
    setSyncSuccess('');
    setStartCampaignDialogOpen(false);

    try {
      // Update campaign status to RUNNING
      const updateResult = await apiCall('put', `/api/campaigns/${selectedCampaignDetail.id}`, {
        status: 'RUNNING',
        send_start_emails: sendStartCampaignEmails
      });

      if (updateResult.success) {
        // Update the local campaign detail
        setSelectedCampaignDetail(prev => ({
          ...prev,
          status: 'RUNNING'
        }));

        // Also synchronize points after starting the campaign
        setSyncingPoints(true);
        const syncResult = await apiCall('post', `/api/campaigns/${selectedCampaignDetail.id}/sync-points`);

        if (syncResult.success) {
          const emailMessage = sendStartCampaignEmails ? ' and start emails sent to dealers!' : ' and points synchronized!';
          setSyncSuccess('Campaign started successfully' + emailMessage);
          // Reload campaign goals to ensure fresh data
          await loadCampaignGoals(selectedCampaignDetail.id);
        } else if (!syncResult.requiresAuth) {
          setSyncSuccess('Campaign started successfully, but failed to synchronize points');
          setSyncError(syncResult.error || 'Failed to synchronize points');
        }
        setSyncingPoints(false);

        setTimeout(() => setSyncSuccess(''), 5000);
      } else if (!updateResult.requiresAuth) {
        setSyncError(updateResult.error || 'Failed to start campaign');
      }
    } catch (error) {
      console.error('Error starting campaign:', error);
      setSyncError('Failed to start campaign');
      setSyncingPoints(false);
    }

    setUpdatingCampaignStatus(false);
  };

  const handleCancelStartCampaign = () => {
    setStartCampaignDialogOpen(false);
    setSendStartCampaignEmails(true); // Reset to default
  };

  const handleStopCampaign = async () => {
    if (!selectedCampaignDetail || !apiCall) return;

    if (!window.confirm('Are you sure you want to stop this campaign? This will change the status back to DRAFT.')) {
      return;
    }

    setUpdatingCampaignStatus(true);
    setSyncError('');
    setSyncSuccess('');

    try {
      // Update campaign status to DRAFT
      const result = await apiCall('put', `/api/campaigns/${selectedCampaignDetail.id}`, {
        status: 'DRAFT'
      });

      if (result.success) {
        // Update the local campaign detail
        setSelectedCampaignDetail(prev => ({
          ...prev,
          status: 'DRAFT'
        }));

        setSyncSuccess('Campaign stopped successfully!');
        setTimeout(() => setSyncSuccess(''), 3000);
      } else if (!result.requiresAuth) {
        setSyncError(result.error || 'Failed to stop campaign');
      }
    } catch (error) {
      console.error('Error stopping campaign:', error);
      setSyncError('Failed to stop campaign');
    }

    setUpdatingCampaignStatus(false);
  };

  // Quiz API functions
  const loadQuizzes = async () => {
    if (!user?.company_id || !apiCall) return;

    setLoadingQuizzes(true);
    setQuizzesError('');

    try {
      const result = await apiCall('get', '/api/quizzes');

      if (result.success) {
        setQuizzes(result.data.quizzes || []);
      } else if (!result.requiresAuth) {
        setQuizzesError(result.error || 'Failed to load quizzes');
      }
    } catch (error) {
      console.error('Error loading quizzes:', error);
      setQuizzesError('Failed to load quizzes');
    }

    setLoadingQuizzes(false);
  };

  const handleQuizSubmit = async () => {
    setQuizFormErrors({});

    if (!quizFormData.title.trim()) {
      setQuizFormErrors({ title: 'Quiz title is required' });
      return;
    }

    if (!quizFormData.campaign_id) {
      setQuizFormErrors({ campaign_id: 'Campaign is required' });
      return;
    }

    for (let qi = 0; qi < quizFormData.questions.length; qi++) {
      const q = quizFormData.questions[qi];
      const a = (q.answer_a || '').trim();
      const b = (q.answer_b || '').trim();
      const c = (q.answer_c || '').trim();
      const d = (q.answer_d || '').trim();
      if (!a || !b) {
        setQuizFormErrors({ submit: `Question ${qi + 1}: answers A and B are required` });
        return;
      }
      if (d && !c) {
        setQuizFormErrors({ submit: `Question ${qi + 1}: answer C is required when D is provided` });
        return;
      }
      const allowed = new Set(['A', 'B']);
      if (c) allowed.add('C');
      if (d) allowed.add('D');
      if (!allowed.has(q.correct_answer)) {
        setQuizFormErrors({ submit: `Question ${qi + 1}: correct answer must match a filled option` });
        return;
      }
    }

    if (quizFormData.auto_on_off) {
      if (!quizFormData.auto_active_at || !quizFormData.auto_inactive_at) {
        setQuizFormErrors({ submit: 'Auto On/Off requires both Active from and Active until' });
        return;
      }
      if (!quizFormData.auto_active_at.isBefore(quizFormData.auto_inactive_at)) {
        setQuizFormErrors({ submit: 'Active until must be after Active from' });
        return;
      }
    }

    setSubmittingQuiz(true);

    try {
      const quizData = {
        title: quizFormData.title,
        description: quizFormData.description,
        short_description: quizFormData.short_description || null,
        active: quizFormData.active,
        only_with_points: quizFormData.only_with_points,
        reward_per_question: quizFormData.reward_per_question,
        reward: quizFormData.reward,
        show_correct_answers: quizFormData.show_correct_answers,
        campaign_id: quizFormData.campaign_id,
        company_id: user.company_id
      };
      if (quizFormData.auto_on_off) {
        quizData.auto_on_off = true;
        quizData.auto_active_at = quizFormData.auto_active_at.utc().toISOString();
        quizData.auto_inactive_at = quizFormData.auto_inactive_at.utc().toISOString();
      } else {
        quizData.auto_on_off = false;
        quizData.auto_active_at = null;
        quizData.auto_inactive_at = null;
      }

      const method = editingQuiz ? 'put' : 'post';
      const endpoint = editingQuiz ? `/api/quizzes/${editingQuiz.id}` : '/api/quizzes';

      const result = await apiCall(method, endpoint, quizData);

      if (result.success) {
        const quiz = result.data;

        // When editing, track original question IDs to detect deletions
        const originalQuestionIds = editingQuiz && editingQuiz.questions
          ? editingQuiz.questions.map(q => q.id).filter(id => id !== undefined)
          : [];

        // Handle questions if any
        const currentQuestionIds = [];
        if (quizFormData.questions.length > 0) {
          for (let i = 0; i < quizFormData.questions.length; i++) {
            const question = quizFormData.questions[i];
            const questionData = {
              quiz_id: quiz.id,
              question: question.question,
              answer_a: question.answer_a,
              answer_b: question.answer_b,
              answer_c: (question.answer_c || '').trim() || null,
              answer_d: (question.answer_d || '').trim() || null,
              correct_answer: question.correct_answer,
              order_position: i
            };

            if (question.id) {
              await apiCall('put', `/api/quiz-questions/${question.id}`, questionData);
              currentQuestionIds.push(question.id);
            } else {
              const createResult = await apiCall('post', '/api/quiz-questions', questionData);
              if (createResult.success && createResult.data?.id) {
                currentQuestionIds.push(createResult.data.id);
              }
            }
          }
        }

        // Delete questions that were removed from the form
        if (editingQuiz && originalQuestionIds.length > 0) {
          const questionsToDelete = originalQuestionIds.filter(id => !currentQuestionIds.includes(id));
          for (const questionId of questionsToDelete) {
            await apiCall('delete', `/api/quiz-questions/${questionId}`);
          }
        }

        setQuizDialog(false);
        setEditingQuiz(null);
        setQuizFormData({ title: '', description: '', short_description: '', active: false, only_with_points: false, auto_on_off: false, auto_active_at: null, auto_inactive_at: null, reward: 0, reward_per_question: false, show_correct_answers: 'ALWAYS', campaign_id: null, questions: [] });

        // Show success message
        const successMessage = editingQuiz ? 'Quiz updated successfully!' : 'Quiz created successfully!';
        setImportSuccess(successMessage);
        setTimeout(() => setImportSuccess(''), 3000);

        // Reload quizzes
        loadQuizzes();

        // Also reload campaign quizzes if we're viewing a campaign detail
        if (selectedCampaignDetail && quizFormData.campaign_id === selectedCampaignDetail.id) {
          loadCampaignQuizzes(selectedCampaignDetail.id);
        }
      } else if (!result.requiresAuth) {
        setQuizFormErrors({ submit: result.error || 'Failed to save quiz' });
      }
    } catch (error) {
      console.error('Error saving quiz:', error);
      setQuizFormErrors({ submit: 'Failed to save quiz' });
    }

    setSubmittingQuiz(false);
  };

  const handleDeleteQuiz = async (quizId) => {
    if (!window.confirm('Are you sure you want to delete this quiz? This will also delete all its questions.')) {
      return;
    }

    try {
      const result = await apiCall('delete', `/api/quizzes/${quizId}`);

      if (result.success) {
        setImportSuccess('Quiz deleted successfully!');
        setTimeout(() => setImportSuccess(''), 3000);
        loadQuizzes();
      } else if (!result.requiresAuth) {
        setQuizzesError(result.error || 'Failed to delete quiz');
      }
    } catch (error) {
      console.error('Error deleting quiz:', error);
      setQuizzesError('Failed to delete quiz');
    }
  };

  const emptyBonusTaskForm = {
    title: '',
    description: '',
    points: 0,
    active: false,
    campaign_id: null,
  };

  const loadBonusTasks = async () => {
    if (!user?.company_id || !apiCall) return;

    setLoadingBonusTasks(true);
    setBonusTasksError('');

    try {
      const result = await apiCall('get', '/api/bonus-tasks');
      if (result.success) {
        setBonusTasks(result.data.bonus_tasks || []);
      } else if (!result.requiresAuth) {
        setBonusTasksError(result.error || 'Failed to load bonus tasks');
      }
    } catch (error) {
      console.error('Error loading bonus tasks:', error);
      setBonusTasksError('Failed to load bonus tasks');
    }

    setLoadingBonusTasks(false);
  };

  const loadBonusTaskDetail = async (taskId) => {
    if (!apiCall) return;
    setLoadingBonusTaskDetail(true);
    setBonusTasksError('');
    setSelectedBonusTaskDetail((prev) =>
      prev && prev.id === taskId ? prev : { id: taskId, title: '…', submissions: [] }
    );
    try {
      const result = await apiCall('get', `/api/bonus-tasks/${taskId}`);
      if (result.success) {
        setSelectedBonusTaskDetail(result.data);
      } else if (!result.requiresAuth) {
        setBonusTasksError(result.error || 'Failed to load bonus task');
        setSelectedBonusTaskDetail(null);
      }
    } catch (error) {
      console.error('Error loading bonus task detail:', error);
      setBonusTasksError('Failed to load bonus task');
      setSelectedBonusTaskDetail(null);
    }
    setLoadingBonusTaskDetail(false);
  };

  const handleAddBonusTask = () => {
    setEditingBonusTask(null);
    setBonusTaskFormData({ ...emptyBonusTaskForm });
    setBonusTaskFormErrors({});
    setBonusTaskDialog(true);
  };

  const handleEditBonusTask = (task) => {
    setEditingBonusTask(task);
    setBonusTaskFormData({
      title: task.title || '',
      description: task.description || '',
      points: Number(task.points) || 0,
      active: Boolean(task.active),
      campaign_id: task.campaign_id || null,
    });
    setBonusTaskFormErrors({});
    setBonusTaskDialog(true);
  };

  const handleBonusTaskSubmit = async () => {
    setBonusTaskFormErrors({});
    if (!bonusTaskFormData.title.trim()) {
      setBonusTaskFormErrors({ title: 'Title is required' });
      return;
    }
    if (!bonusTaskFormData.campaign_id) {
      setBonusTaskFormErrors({ campaign_id: 'Campaign is required' });
      return;
    }
    if (bonusTaskFormData.points === '' || Number(bonusTaskFormData.points) < 0) {
      setBonusTaskFormErrors({ points: 'Points must be 0 or greater' });
      return;
    }

    setSubmittingBonusTask(true);
    try {
      const payload = {
        title: bonusTaskFormData.title.trim(),
        description: bonusTaskFormData.description || null,
        points: Number(bonusTaskFormData.points) || 0,
        active: Boolean(bonusTaskFormData.active),
        campaign_id: bonusTaskFormData.campaign_id,
        company_id: user.company_id,
      };
      const method = editingBonusTask ? 'put' : 'post';
      const endpoint = editingBonusTask
        ? `/api/bonus-tasks/${editingBonusTask.id}`
        : '/api/bonus-tasks';
      const result = await apiCall(method, endpoint, payload);
      if (result.success) {
        setBonusTaskDialog(false);
        setEditingBonusTask(null);
        setBonusTaskFormData({ ...emptyBonusTaskForm });
        setImportSuccess(editingBonusTask ? 'Bonus task updated successfully!' : 'Bonus task created successfully!');
        setTimeout(() => setImportSuccess(''), 3000);
        loadBonusTasks();
        if (selectedBonusTaskDetail && editingBonusTask && selectedBonusTaskDetail.id === editingBonusTask.id) {
          loadBonusTaskDetail(editingBonusTask.id);
        }
      } else if (!result.requiresAuth) {
        setBonusTaskFormErrors({ submit: result.error || 'Failed to save bonus task' });
      }
    } catch (error) {
      console.error('Error saving bonus task:', error);
      setBonusTaskFormErrors({ submit: 'Failed to save bonus task' });
    }
    setSubmittingBonusTask(false);
  };

  const handleDeleteBonusTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this bonus task? Submissions will also be deleted.')) {
      return;
    }
    try {
      const result = await apiCall('delete', `/api/bonus-tasks/${taskId}`);
      if (result.success) {
        setImportSuccess('Bonus task deleted successfully!');
        setTimeout(() => setImportSuccess(''), 3000);
        if (selectedBonusTaskDetail?.id === taskId) {
          setSelectedBonusTaskDetail(null);
        }
        loadBonusTasks();
      } else if (!result.requiresAuth) {
        setBonusTasksError(result.error || 'Failed to delete bonus task');
      }
    } catch (error) {
      console.error('Error deleting bonus task:', error);
      setBonusTasksError('Failed to delete bonus task');
    }
  };

  const handleReviewBonusTaskSubmission = async (submissionId, action) => {
    setReviewingSubmissionId(submissionId);
    setBonusTasksError('');
    try {
      const result = await apiCall('post', `/api/bonus-task-submissions/${submissionId}/${action}`);
      if (result.success) {
        setImportSuccess(action === 'accept' ? 'Submission accepted.' : 'Submission rejected.');
        setTimeout(() => setImportSuccess(''), 3000);
        if (selectedBonusTaskDetail?.id) {
          await loadBonusTaskDetail(selectedBonusTaskDetail.id);
        }
      } else if (!result.requiresAuth) {
        setBonusTasksError(result.error || `Failed to ${action} submission`);
      }
    } catch (error) {
      console.error(`Error ${action}ing submission:`, error);
      setBonusTasksError(`Failed to ${action} submission`);
    }
    setReviewingSubmissionId(null);
  };

  const normalizeQuizQuestionsForForm = (questions) =>
    (questions || []).map((q) => ({
      ...q,
      answer_c: q.answer_c ?? '',
      answer_d: q.answer_d ?? ''
    }));

  const handleEditQuiz = (quiz) => {
    setEditingQuiz(quiz);
    setQuizFormData({
      title: quiz.title,
      description: quiz.description || '',
      short_description: quiz.short_description || '',
      active: quiz.active,
      only_with_points: Boolean(quiz.only_with_points),
      auto_on_off: Boolean(quiz.auto_on_off),
      auto_active_at: quiz.auto_active_at
        ? dayjs.utc(quiz.auto_active_at).tz(companyTimezone)
        : null,
      auto_inactive_at: quiz.auto_inactive_at
        ? dayjs.utc(quiz.auto_inactive_at).tz(companyTimezone)
        : null,
      reward: quiz.reward || 0,
      reward_per_question: Boolean(quiz.reward_per_question),
      show_correct_answers: quiz.show_correct_answers || 'ALWAYS',
      campaign_id: quiz.campaign_id || null,
      questions: normalizeQuizQuestionsForForm(quiz.questions)
    });
    setQuizFormErrors({});
    setQuizDialog(true);
  };

  const handleEditCampaignQuiz = (quiz) => {
    setEditingQuiz(quiz);
    setQuizFormData({
      title: quiz.title,
      description: quiz.description || '',
      short_description: quiz.short_description || '',
      active: quiz.active,
      only_with_points: Boolean(quiz.only_with_points),
      auto_on_off: Boolean(quiz.auto_on_off),
      auto_active_at: quiz.auto_active_at
        ? dayjs.utc(quiz.auto_active_at).tz(companyTimezone)
        : null,
      auto_inactive_at: quiz.auto_inactive_at
        ? dayjs.utc(quiz.auto_inactive_at).tz(companyTimezone)
        : null,
      reward: quiz.reward || 0,
      reward_per_question: Boolean(quiz.reward_per_question),
      show_correct_answers: quiz.show_correct_answers || 'ALWAYS',
      campaign_id: quiz.campaign_id || null,
      questions: normalizeQuizQuestionsForForm(quiz.questions)
    });
    setQuizFormErrors({});
    setQuizDialog(true);
  };

  const handleAddQuiz = () => {
    setEditingQuiz(null);
    setQuizFormData({ title: '', description: '', short_description: '', active: false, only_with_points: false, auto_on_off: false, auto_active_at: null, auto_inactive_at: null, reward: 0, reward_per_question: false, show_correct_answers: 'ALWAYS', campaign_id: null, questions: [] });
    setQuizFormErrors({});
    setQuizDialog(true);
  };

  const handleAddCampaignQuiz = () => {
    if (!selectedCampaignDetail) return;

    setEditingQuiz(null);
    setQuizFormData({
      title: '',
      description: '',
      short_description: '',
      active: false,
      only_with_points: false,
      auto_on_off: false,
      auto_active_at: null,
      auto_inactive_at: null,
      reward: 0,
      show_correct_answers: 'ALWAYS',
      campaign_id: selectedCampaignDetail.id,
      questions: []
    });
    setQuizFormErrors({});
    setQuizDialog(true);
  };

  const handleToggleQuizActive = async (quiz) => {
    if (!quiz || !apiCall) return;

    try {
      const result = await apiCall('put', `/api/quizzes/${quiz.id}`, {
        active: !quiz.active
      });

      if (result.success) {
        // Reload campaign quizzes
        if (selectedCampaignDetail) {
          loadCampaignQuizzes(selectedCampaignDetail.id);
        }
        setImportSuccess(`Quiz ${!quiz.active ? 'activated' : 'deactivated'} successfully!`);
        setTimeout(() => setImportSuccess(''), 3000);
      } else if (!result.requiresAuth) {
        setCampaignQuizzesError(result.error || 'Failed to update quiz');
      }
    } catch (error) {
      console.error('Error toggling quiz active status:', error);
      setCampaignQuizzesError('Error updating quiz');
    }
  };

  const handleDeleteCampaignQuiz = async (quizId) => {
    if (!window.confirm('Are you sure you want to delete this quiz? This will also delete all its questions.')) {
      return;
    }

    try {
      const result = await apiCall('delete', `/api/quizzes/${quizId}`);

      if (result.success) {
        setImportSuccess('Quiz deleted successfully!');
        setTimeout(() => setImportSuccess(''), 3000);
        // Reload campaign quizzes
        if (selectedCampaignDetail) {
          loadCampaignQuizzes(selectedCampaignDetail.id);
        }
      } else if (!result.requiresAuth) {
        setCampaignQuizzesError(result.error || 'Failed to delete quiz');
      }
    } catch (error) {
      console.error('Error deleting quiz:', error);
      setCampaignQuizzesError('Failed to delete quiz');
    }
  };

  const addQuestion = () => {
    const newQuestion = {
      question: '',
      answer_a: '',
      answer_b: '',
      answer_c: '',
      answer_d: '',
      correct_answer: 'A'
    };
    setQuizFormData(prev => ({
      ...prev,
      questions: [...prev.questions, newQuestion]
    }));
  };

  const updateQuestion = (index, field, value) => {
    setQuizFormData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => {
        if (i !== index) return q;
        let next = { ...q, [field]: value };
        if (field === 'answer_c') {
          const v = String(value || '').trim();
          if (!v) {
            next.answer_c = '';
            next.answer_d = '';
            if (q.correct_answer === 'C' || q.correct_answer === 'D') {
              next.correct_answer = 'A';
            }
          }
        }
        if (field === 'answer_d') {
          const v = String(value || '').trim();
          if (!v && q.correct_answer === 'D') {
            next.correct_answer = 'A';
          }
        }
        return next;
      })
    }));
  };

  const removeQuestion = (index) => {
    setQuizFormData(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  const moveQuestionUp = (index) => {
    if (index === 0) return; // Can't move first question up

    setQuizFormData(prev => {
      const newQuestions = [...prev.questions];
      [newQuestions[index - 1], newQuestions[index]] = [newQuestions[index], newQuestions[index - 1]];
      return {
        ...prev,
        questions: newQuestions
      };
    });
  };

  const moveQuestionDown = (index) => {
    setQuizFormData(prev => {
      if (index === prev.questions.length - 1) return prev; // Can't move last question down

      const newQuestions = [...prev.questions];
      [newQuestions[index], newQuestions[index + 1]] = [newQuestions[index + 1], newQuestions[index]];
      return {
        ...prev,
        questions: newQuestions
      };
    });
  };

  // Notification Set API functions
  const loadNotificationSets = async () => {
    if (!user?.company_id || !apiCall) return;

    setLoadingNotificationSets(true);
    setNotificationSetsError('');

    try {
      const result = await apiCall('get', `/api/notification-sets?company_id=${user.company_id}`);

      if (result.success) {
        const sets = result.data.notification_sets || [];
        setNotificationSets(sets);

        // Check if there's already a General set
        const generalSet = sets.find(set => set.campaign_id === null);
        setHasGeneralSet(!!generalSet);

        // Track which campaigns already have notification sets
        const campaignIds = new Set();
        sets.forEach(set => {
          if (set.campaign_id) {
            campaignIds.add(set.campaign_id);
          }
        });
        setCampaignsWithSets(campaignIds);
      } else if (!result.requiresAuth) {
        setNotificationSetsError(result.error || 'Failed to load notification sets');
      }
    } catch (error) {
      console.error('Error loading notification sets:', error);
      setNotificationSetsError('Failed to load notification sets');
    }

    setLoadingNotificationSets(false);
  };

  const loadEmailTemplates = async (notificationSetId) => {
    if (!notificationSetId || !apiCall) return;

    setLoadingEmailTemplates(true);
    setEmailTemplatesError('');

    try {
      const result = await apiCall('get', `/api/email-templates?notification_set_id=${notificationSetId}`);

      if (result.success) {
        setEmailTemplates(result.data.email_templates || []);
      } else if (!result.requiresAuth) {
        setEmailTemplatesError(result.error || 'Failed to load email templates');
      }
    } catch (error) {
      console.error('Error loading email templates:', error);
      setEmailTemplatesError('Failed to load email templates');
    }

    setLoadingEmailTemplates(false);
  };

  const handleNotificationSetSubmit = async () => {
    setNotificationSetFormErrors({});

    if (!notificationSetFormData.name.trim()) {
      setNotificationSetFormErrors({ name: 'Notification set name is required' });
      return;
    }

    setSubmittingNotificationSet(true);

    try {
      const notificationSetData = {
        name: notificationSetFormData.name,
        campaign_id: notificationSetFormData.campaign_id || null,
        company_id: user.company_id,
        header_image: notificationSetFormData.header_image || null,
        footer_image: notificationSetFormData.footer_image || null
      };

      const method = editingNotificationSet ? 'put' : 'post';
      const endpoint = editingNotificationSet
        ? `/api/notification-sets/${editingNotificationSet.id}`
        : '/api/notification-sets';

      const result = await apiCall(method, endpoint, notificationSetData);

      if (result.success) {
        setNotificationSetDialog(false);
        setEditingNotificationSet(null);
        setNotificationSetFormData({ name: '', campaign_id: null, header_image: '', footer_image: '' });

        // Show success message
        const successMessage = editingNotificationSet
          ? 'Notification set updated successfully!'
          : 'Notification set created successfully!';
        setImportSuccess(successMessage);
        setTimeout(() => setImportSuccess(''), 3000);

        // Reload notification sets (this will also update hasGeneralSet)
        loadNotificationSets();
      } else if (!result.requiresAuth) {
        setNotificationSetFormErrors({ submit: result.error || 'Failed to save notification set' });
      }
    } catch (error) {
      console.error('Error saving notification set:', error);
      setNotificationSetFormErrors({ submit: 'Failed to save notification set' });
    }

    setSubmittingNotificationSet(false);
  };

  const handleDeleteNotificationSet = async (notificationSetId) => {
    if (!window.confirm('Are you sure you want to delete this notification set? This will also delete all its email templates.')) {
      return;
    }

    try {
      const result = await apiCall('delete', `/api/notification-sets/${notificationSetId}`);

      if (result.success) {
        setImportSuccess('Notification set deleted successfully!');
        setTimeout(() => setImportSuccess(''), 3000);
        loadNotificationSets(); // This will also update hasGeneralSet
      } else if (!result.requiresAuth) {
        setNotificationSetsError(result.error || 'Failed to delete notification set');
      }
    } catch (error) {
      console.error('Error deleting notification set:', error);
      setNotificationSetsError('Failed to delete notification set');
    }
  };

  const handleEditNotificationSet = (notificationSet) => {
    setEditingNotificationSet(notificationSet);
    setNotificationSetFormData({
      name: notificationSet.name,
      campaign_id: notificationSet.campaign_id || null,
      header_image: notificationSet.header_image || '',
      footer_image: notificationSet.footer_image || ''
    });
    setNotificationSetFormErrors({});
    setNotificationSetDialog(true);
  };

  const handleAddNotificationSet = () => {
    setEditingNotificationSet(null);
    setNotificationSetFormData({
      name: '',
      campaign_id: null,
      header_image: '',
      footer_image: ''
    });
    setNotificationSetFormErrors({});
    setNotificationSetDialog(true);
  };

  const handleNotificationSetHeaderImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setNotificationSetFormErrors({ submit: 'Please upload an image file' });
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch(`${API_URL}/api/upload/branding-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      if (response.ok) {
        const result = await response.json();
        setNotificationSetFormData(prev => ({ ...prev, header_image: result.filename }));
        setNotificationSetFormErrors(prev => ({ ...prev, submit: undefined }));
      } else {
        const errorData = await response.json();
        setNotificationSetFormErrors({ submit: errorData.detail || 'Failed to upload header image' });
      }
    } catch (error) {
      setNotificationSetFormErrors({ submit: 'Failed to upload header image' });
    }
    event.target.value = '';
  };

  const handleNotificationSetFooterImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setNotificationSetFormErrors({ submit: 'Please upload an image file' });
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch(`${API_URL}/api/upload/branding-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      if (response.ok) {
        const result = await response.json();
        setNotificationSetFormData(prev => ({ ...prev, footer_image: result.filename }));
        setNotificationSetFormErrors(prev => ({ ...prev, submit: undefined }));
      } else {
        const errorData = await response.json();
        setNotificationSetFormErrors({ submit: errorData.detail || 'Failed to upload footer image' });
      }
    } catch (error) {
      setNotificationSetFormErrors({ submit: 'Failed to upload footer image' });
    }
    event.target.value = '';
  };

  const handleViewTemplates = (notificationSet) => {
    setSelectedNotificationSet(notificationSet);
    setViewingTemplates(true);
    loadEmailTemplates(notificationSet.id);
  };

  const handleBackToNotificationSets = () => {
    setViewingTemplates(false);
    setSelectedNotificationSet(null);
    setEmailTemplates([]);
    setEmailTemplatesError('');
  };

  const handleEmailTemplateSubmit = async () => {
    setEmailTemplateFormErrors({});

    if (!emailTemplateFormData.title.trim()) {
      setEmailTemplateFormErrors({ title: 'Template title is required' });
      return;
    }

    if (!emailTemplateFormData.content.trim()) {
      setEmailTemplateFormErrors({ content: 'Template content is required' });
      return;
    }

    setSubmittingEmailTemplate(true);

    try {
      const emailTemplateData = {
        notification_set_id: selectedNotificationSet.id,
        template_type: emailTemplateFormData.template_type,
        title: emailTemplateFormData.title,
        subtitle: emailTemplateFormData.subtitle,
        content: emailTemplateFormData.content,
        extra_content: emailTemplateFormData.extra_content,
        custom_cta_enabled: emailTemplateFormData.custom_cta_enabled,
        custom_cta_title: emailTemplateFormData.custom_cta_title,
        custom_cta_url: emailTemplateFormData.custom_cta_url,
        no_cta: emailTemplateFormData.no_cta
      };

      const method = editingEmailTemplate ? 'put' : 'post';
      const endpoint = editingEmailTemplate
        ? `/api/email-templates/${editingEmailTemplate.id}`
        : '/api/email-templates';

      const result = await apiCall(method, endpoint, emailTemplateData);

      if (result.success) {
        setEmailTemplateDialog(false);
        setEditingEmailTemplate(null);
        setEmailTemplateFormData({
          template_type: 'CAMPAIGN_START',
          title: '',
          subtitle: '',
          content: ''
        });

        // Show success message
        const successMessage = editingEmailTemplate
          ? 'Email template updated successfully!'
          : 'Email template created successfully!';
        setImportSuccess(successMessage);
        setTimeout(() => setImportSuccess(''), 3000);

        // Reload email templates
        loadEmailTemplates(selectedNotificationSet.id);
      } else if (!result.requiresAuth) {
        setEmailTemplateFormErrors({ submit: result.error || 'Failed to save email template' });
      }
    } catch (error) {
      console.error('Error saving email template:', error);
      setEmailTemplateFormErrors({ submit: 'Failed to save email template' });
    }

    setSubmittingEmailTemplate(false);
  };

  const handleDeleteEmailTemplate = async (emailTemplateId) => {
    if (!window.confirm('Are you sure you want to delete this email template?')) {
      return;
    }

    try {
      const result = await apiCall('delete', `/api/email-templates/${emailTemplateId}`);

      if (result.success) {
        setImportSuccess('Email template deleted successfully!');
        setTimeout(() => setImportSuccess(''), 3000);
        loadEmailTemplates(selectedNotificationSet.id);
      } else if (!result.requiresAuth) {
        setEmailTemplatesError(result.error || 'Failed to delete email template');
      }
    } catch (error) {
      console.error('Error deleting email template:', error);
      setEmailTemplatesError('Failed to delete email template');
    }
  };

  const handleEditEmailTemplate = (emailTemplate) => {
    setEditingEmailTemplate(emailTemplate);
    setEmailTemplateFormData({
      template_type: emailTemplate.template_type,
      title: emailTemplate.title,
      subtitle: emailTemplate.subtitle || '',
      content: emailTemplate.content,
      extra_content: emailTemplate.extra_content || '',
      custom_cta_enabled: emailTemplate.custom_cta_enabled || false,
      custom_cta_title: emailTemplate.custom_cta_title || '',
      custom_cta_url: emailTemplate.custom_cta_url || '',
      no_cta: emailTemplate.no_cta || false
    });
    setEmailTemplateFormErrors({});
    setEmailTemplateDialog(true);
  };

  const handleAddEmailTemplate = () => {
    setEditingEmailTemplate(null);
    setEmailTemplateFormData({
      template_type: 'CAMPAIGN_START',
      title: '',
      subtitle: '',
      content: '',
      extra_content: '',
      custom_cta_enabled: false,
      custom_cta_title: '',
      custom_cta_url: '',
      no_cta: false
    });
    setEmailTemplateFormErrors({});
    setEmailTemplateDialog(true);
  };

  const getTemplateTypeLabel = (type) => {
    const labels = {
      'CAMPAIGN_START': 'Campaign Start',
      'CAMPAIGN_START_ACTIVATION_LINK': 'Campaign Start with Activation Link',
      'REMINDER_NO_POINTS': 'Reminder (No Points)',
      'REMINDER_WITH_POINTS': 'Reminder (With Points)',
      'REMINDER_NEVER_LOGGED_IN': 'Reminder (Never logged-in)',
      'REMINDER_LOGGED_IN': 'Reminder (Logged-in)',
      'REMINDER_LOGGED_IN_2': 'Reminder (Logged-in 2)',
      'REMINDER_TARGET_ACHIEVED': 'Reminder (Target Achieved)',
      'REMINDER_TARGET_NOT_ACHIEVED': 'Reminder (Target NOT Achieved)',
      'REMINDER_ALL': 'Reminder (ALL)',
      'VOUCHER': 'Voucher',
      'TEMP_PASSWORD': 'Temporary Password',
      'RESET_PASSWORD': 'Reset Password'
    };
    return labels[type] || type;
  };

  const getReminderFrequencyLabel = (freq) => {
    const labels = {
      ONCE: 'Once',
      WEEKLY: 'Weekly',
      BIWEEKLY: 'Biweekly',
      MONTHLY: 'Monthly'
    };
    return labels[freq] || 'Weekly';
  };

  // Marketing Item API functions
  const loadMarketingItems = async () => {
    if (!user?.company_id || !apiCall) return;

    setLoadingMarketingItems(true);
    setMarketingItemsError('');

    try {
      const result = await apiCall('get', '/api/marketing-items');

      if (result.success) {
        setMarketingItems(result.data.marketing_items || []);
      } else if (!result.requiresAuth) {
        setMarketingItemsError(result.error || 'Failed to load marketing items');
      }
    } catch (error) {
      console.error('Error loading marketing items:', error);
      setMarketingItemsError('Failed to load marketing items');
    }

    setLoadingMarketingItems(false);
  };

  // Dashboard loading functions
  const loadDashboardData = async () => {
    if (!user?.company_id || !apiCall) return;

    // Load company reward providers first (needed for checking connected providers)
    // This must complete before loadDashboardRewards runs
    if (companyRewardProviders.length === 0) {
      try {
        const providersResult = await apiCall('get', `/api/company-reward-providers?company_id=${user.company_id}`);
        if (providersResult.success) {
          setCompanyRewardProviders(providersResult.data.company_reward_providers || []);
          // Wait a tick for state to update before proceeding
          await new Promise(resolve => setTimeout(resolve, 0));
        }
      } catch (error) {
        console.error('Error loading company reward providers for dashboard:', error);
      }
    }

    // Load all dashboard data in parallel (now that providers are loaded)
    await Promise.all([
      loadDashboardCampaigns(),
      loadDashboardMarketing(),
      loadDashboardRewards(),
      loadDashboardQuizzes()
    ]);
  };

  const loadDashboardCampaigns = async () => {
    if (!user?.company_id || !apiCall) return;

    setLoadingDashboardCampaigns(true);

    try {
      // Load running campaigns with dealer counts and value sums
      const campaignsResult = await apiCall('get', `/api/campaigns?status=RUNNING&per_page=50`);

      if (campaignsResult.success) {
        const campaigns = campaignsResult.data.campaigns || [];

        // For each campaign, get goal sums and use existing dealer data
        const campaignsWithData = await Promise.all(
          campaigns.map(async (campaign) => {
            try {
              // Get campaign goals summary (value sums)
              const goalsResult = await apiCall('get', `/api/campaigns/${campaign.id}/goals-summary`);
              const goalsSummary = goalsResult.success ? goalsResult.data : {};

              return {
                ...campaign,
                dealer_count: campaign.dealers ? campaign.dealers.length : 0,
                value_1_sum: goalsSummary.value_1_sum || 0,
                value_2_sum: goalsSummary.value_2_sum || 0,
                value_3_sum: goalsSummary.value_3_sum || 0
              };
            } catch (error) {
              console.error(`Error loading data for campaign ${campaign.id}:`, error);
              return {
                ...campaign,
                dealer_count: campaign.dealers ? campaign.dealers.length : 0,
                value_1_sum: 0,
                value_2_sum: 0,
                value_3_sum: 0
              };
            }
          })
        );

        setDashboardCampaigns(campaignsWithData);

        // Check if any campaign shows value columns to determine if we should show them
        if (campaigns.length > 0) {
          const hasValue1 = campaigns.some(c => c.show_value_1);
          const hasValue2 = campaigns.some(c => c.show_value_2);
          const hasValue3 = campaigns.some(c => c.show_value_3);

          setDashboardGoalSettings({
            show_value_1: hasValue1,
            show_value_2: hasValue2,
            show_value_3: hasValue3
          });
        }
      }
    } catch (error) {
      console.error('Error loading dashboard campaigns:', error);
    }

    setLoadingDashboardCampaigns(false);
  };

  const loadDashboardMarketing = async () => {
    if (!user?.company_id || !apiCall) return;

    setLoadingDashboardMarketing(true);

    try {
      const result = await apiCall('get', '/api/marketing-items?per_page=10');

      if (result.success) {
        setDashboardMarketingItems(result.data.marketing_items || []);
      }
    } catch (error) {
      console.error('Error loading dashboard marketing items:', error);
    }

    setLoadingDashboardMarketing(false);
  };

  const loadDashboardRewards = async () => {
    if (!user?.company_id || !fetchSmodBalance || !apiCall) return;

    setLoadingDashboardRewards(true);

    try {
      // Ensure company reward providers are loaded (in case they weren't loaded yet)
      let providers = companyRewardProviders;
      if (providers.length === 0) {
        try {
          const providersResult = await apiCall('get', `/api/company-reward-providers?company_id=${user.company_id}`);
          if (providersResult.success) {
            providers = providersResult.data.company_reward_providers || [];
            setCompanyRewardProviders(providers);
          }
        } catch (error) {
          console.error('Error loading company reward providers in loadDashboardRewards:', error);
        }
      }

      // Check if company has GiftMall or PerfectGift providers before calling SMOD balance
      const hasGiftMallOrPerfectGift = providers.some(crp => {
        const name = (crp.reward_provider?.name || '').toLowerCase();
        return name.includes('giftmall') || name.includes('perfectgift');
      });

      // Load SMOD balance only if GiftMall or PerfectGift providers are connected
      if (hasGiftMallOrPerfectGift) {
        const balanceResult = await fetchSmodBalance(user.company_id);
        if (balanceResult.success) {
          setDashboardRewardsBudget(balanceResult.data);
        }
      }

      // Load Tremendous balance if Tremendous is connected
      const tremendousProvider = providers.find(crp =>
        crp.reward_provider?.name?.toLowerCase().includes('tremendous')
      );
      if (tremendousProvider) {
        const connectionStatus = getTremendousConnectionStatus(tremendousProvider);
        if (connectionStatus === 'connected') {
          setLoadingTremendousBalance(true);
          setTremendousBalanceError('');
          try {
            const result = await apiCall('get', `/api/tremendous/balance/${user.company_id}`);
            if (result.success) {
              setTremendousBalance(result.data);
            } else if (!result.requiresAuth) {
              setTremendousBalanceError(result.error || 'Failed to load Tremendous balance');
            }
          } catch (error) {
            console.error('Error loading Tremendous balance for dashboard:', error);
            const errorMsg = error.response?.data?.detail || error.message || 'Failed to load Tremendous balance';
            setTremendousBalanceError(errorMsg);
          } finally {
            setLoadingTremendousBalance(false);
          }
        }
      }

      // Load Edenred balance if Edenred_PL provider exists
      const edenredProvider = providers.find(crp =>
        crp.reward_provider?.name === 'Edenred_PL'
      );
      if (edenredProvider) {
        // Fetch company to get api_account
        const companyResult = await apiCall('get', `/api/companies/${user.company_id}`);
        if (companyResult.success && companyResult.data?.api_account) {
          try {
            const result = await apiCall('get', `/api/edenred-balance?account=${companyResult.data.api_account}`);
            if (result.success) {
              setEdenredBalance(result.data);
            }
          } catch (error) {
            console.error('Error loading Edenred balance for dashboard:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error loading dashboard rewards budget:', error);
    }

    setLoadingDashboardRewards(false);
  };

  const loadDashboardQuizzes = async () => {
    if (!user?.company_id || !apiCall) return;

    setLoadingDashboardQuizzes(true);

    try {
      const result = await apiCall('get', '/api/quizzes?per_page=10');

      if (result.success) {
        // Filter to show only active quizzes
        const activeQuizzes = (result.data.quizzes || []).filter(quiz => quiz.active);
        setDashboardQuizzes(activeQuizzes);

        // Load statistics for each quiz
        activeQuizzes.forEach(quiz => {
          loadQuizStatistics(quiz.id, 1, 5);
        });
      }
    } catch (error) {
      console.error('Error loading dashboard quizzes:', error);
    }

    setLoadingDashboardQuizzes(false);
  };

  const handleMarketingItemSubmit = async () => {
    setMarketingItemFormErrors({});

    if (!marketingItemFormData.title.trim()) {
      setMarketingItemFormErrors({ title: 'Title is required' });
      return;
    }

    if (marketingItemFormData.type === 'YOUTUBE' && !marketingItemFormData.youtube_link.trim()) {
      setMarketingItemFormErrors({ youtube_link: 'YouTube link is required for YouTube type' });
      return;
    }

    if (marketingItemFormData.type === 'DOWNLOAD' && !marketingItemFormData.download_file) {
      setMarketingItemFormErrors({ download_file: 'Download file is required for Download type' });
      return;
    }

    setSubmittingMarketingItem(true);

    try {
      const marketingItemData = {
        title: marketingItemFormData.title,
        image: marketingItemFormData.image,
        description: marketingItemFormData.description,
        type: marketingItemFormData.type,
        youtube_link: marketingItemFormData.youtube_link || null,
        download_file: marketingItemFormData.download_file || null,
        company_id: user.company_id
      };

      const method = editingMarketingItem ? 'put' : 'post';
      const endpoint = editingMarketingItem
        ? `/api/marketing-items/${editingMarketingItem.id}`
        : '/api/marketing-items';

      const result = await apiCall(method, endpoint, marketingItemData);

      if (result.success) {
        setMarketingItemDialog(false);
        setEditingMarketingItem(null);
        setMarketingItemFormData({
          title: '',
          image: '',
          description: '',
          type: 'IMAGE',
          youtube_link: '',
          download_file: ''
        });

        // Show success message
        const successMessage = editingMarketingItem
          ? 'Marketing item updated successfully!'
          : 'Marketing item created successfully!';
        setImportSuccess(successMessage);
        setTimeout(() => setImportSuccess(''), 3000);

        // Reload marketing items
        loadMarketingItems();
      } else if (!result.requiresAuth) {
        setMarketingItemFormErrors({ submit: result.error || 'Failed to save marketing item' });
      }
    } catch (error) {
      console.error('Error saving marketing item:', error);
      setMarketingItemFormErrors({ submit: 'Failed to save marketing item' });
    }

    setSubmittingMarketingItem(false);
  };

  const handleDeleteMarketingItem = async (marketingItemId) => {
    if (!window.confirm('Are you sure you want to delete this marketing item?')) {
      return;
    }

    try {
      const result = await apiCall('delete', `/api/marketing-items/${marketingItemId}`);

      if (result.success) {
        setImportSuccess('Marketing item deleted successfully!');
        setTimeout(() => setImportSuccess(''), 3000);
        loadMarketingItems();
      } else if (!result.requiresAuth) {
        setMarketingItemsError(result.error || 'Failed to delete marketing item');
      }
    } catch (error) {
      console.error('Error deleting marketing item:', error);
      setMarketingItemsError('Failed to delete marketing item');
    }
  };

  const handleEditMarketingItem = (marketingItem) => {
    setEditingMarketingItem(marketingItem);
    setMarketingItemFormData({
      title: marketingItem.title,
      image: marketingItem.image || '',
      description: marketingItem.description || '',
      type: marketingItem.type,
      youtube_link: marketingItem.youtube_link || '',
      download_file: marketingItem.download_file || ''
    });
    setMarketingItemFormErrors({});
    setMarketingItemDialog(true);
  };

  const handleAddMarketingItem = () => {
    setEditingMarketingItem(null);
    setMarketingItemFormData({
      title: '',
      image: '',
      description: '',
      type: 'IMAGE',
      youtube_link: '',
      download_file: ''
    });
    setMarketingItemFormErrors({});
    setMarketingItemDialog(true);
  };

  const handleMarketingImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'marketing');

    setUploadingMarketingImage(true);

    try {
      const result = await apiCall('post', '/api/upload', formData);

      if (result.success) {
        setMarketingItemFormData(prev => ({
          ...prev,
          image: result.data.file_path
        }));
      } else if (!result.requiresAuth) {
        setMarketingItemFormErrors({ image: result.error || 'Failed to upload image' });
      }
    } catch (error) {
      console.error('Error uploading marketing image:', error);
      setMarketingItemFormErrors({ image: 'Failed to upload image' });
    }

    setUploadingMarketingImage(false);
  };

  const handleMarketingFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'marketing');

    setUploadingMarketingFile(true);

    try {
      const result = await apiCall('post', '/api/upload', formData);

      if (result.success) {
        setMarketingItemFormData(prev => ({
          ...prev,
          download_file: result.data.file_path
        }));
      } else if (!result.requiresAuth) {
        setMarketingItemFormErrors({ download_file: result.error || 'Failed to upload file' });
      }
    } catch (error) {
      console.error('Error uploading marketing file:', error);
      setMarketingItemFormErrors({ download_file: 'Failed to upload file' });
    }

    setUploadingMarketingFile(false);
  };

  const getMarketingTypeLabel = (type) => {
    const labels = {
      'IMAGE': 'Image',
      'YOUTUBE': 'YouTube',
      'DOWNLOAD': 'Download'
    };
    return labels[type] || type;
  };

  // Email Configuration Handlers
  const handleEmailHeaderImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setEmailConfigError('Please upload an image file');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setEmailConfigLoading(true);
    setEmailConfigError('');

    try {
      const response = await fetch(`${API_URL}/api/upload/branding-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        setEmailConfig(prev => ({
          ...prev,
          email_header_image: result.filename
        }));
        setEmailConfigSuccess('Header image uploaded successfully!');
        setTimeout(() => setEmailConfigSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setEmailConfigError(errorData.detail || 'Failed to upload header image');
      }
    } catch (error) {
      console.error('Error uploading header image:', error);
      setEmailConfigError('Failed to upload header image');
    }

    setEmailConfigLoading(false);
  };

  const handleEmailFooterImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setEmailConfigError('Please upload an image file');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setEmailConfigLoading(true);
    setEmailConfigError('');

    try {
      const response = await fetch(`${API_URL}/api/upload/branding-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        setEmailConfig(prev => ({
          ...prev,
          email_footer_image: result.filename
        }));
        setEmailConfigSuccess('Footer image uploaded successfully!');
        setTimeout(() => setEmailConfigSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setEmailConfigError(errorData.detail || 'Failed to upload footer image');
      }
    } catch (error) {
      console.error('Error uploading footer image:', error);
      setEmailConfigError('Failed to upload footer image');
    }

    setEmailConfigLoading(false);
  };

  const handleFormalDocumentUpload = async (docType, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain', 'text/html'];
    if (!allowedTypes.includes(file.type)) {
      setEmailConfigError('Please upload PDF, DOC, DOCX, TXT, or HTML');
      return;
    }
    setUploadingFormalDoc(prev => ({ ...prev, [docType]: true }));
    setEmailConfigError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_URL}/api/upload/formal-document`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (response.ok) {
        const result = await response.json();
        setFormalDocuments(prev => ({
          ...prev,
          [docType]: { type: 'file', url: '', file: result.filename }
        }));
        setEmailConfigSuccess(`${docType === 'terms' ? 'Terms' : 'Privacy Policy'} uploaded successfully!`);
        setTimeout(() => setEmailConfigSuccess(''), 3000);
      } else {
        const err = await response.json();
        setEmailConfigError(err.detail || 'Upload failed');
      }
    } catch (e) {
      setEmailConfigError('Failed to upload document');
    }
    setUploadingFormalDoc(prev => ({ ...prev, [docType]: false }));
    event.target.value = '';
  };

  const handleSaveEmailConfig = async () => {
    if (!user?.company_id) {
      setEmailConfigError('Company ID not found');
      return;
    }

    setEmailConfigSaving(true);
    setEmailConfigError('');
    setEmailConfigSuccess('');

    try {
      const formalDocsPayload = {};
      if (formalDocuments.terms.url || formalDocuments.terms.file) {
        formalDocsPayload.terms = formalDocuments.terms.url
          ? { url: formalDocuments.terms.url }
          : { file: formalDocuments.terms.file };
      }
      if (formalDocuments.privacy.url || formalDocuments.privacy.file) {
        formalDocsPayload.privacy = formalDocuments.privacy.url
          ? { url: formalDocuments.privacy.url }
          : { file: formalDocuments.privacy.file };
      }
      const extraTrim = (formalDocuments.extra_confirmation_text || '').trim();
      if (extraTrim) {
        formalDocsPayload.extra_confirmation_text = extraTrim;
      }
      const result = await apiCall('put', `/api/companies/${user.company_id}`, {
        email_configuration: {
          email_header_image: emailConfig.email_header_image,
          email_footer_image: emailConfig.email_footer_image,
          email_footer: emailConfig.email_footer,
          button_title: emailConfig.button_title,
          button_url: emailConfig.button_url,
          sender_name: emailConfig.sender_name,
          temp_password_prefix: emailConfig.temp_password_prefix
        },
        formal_documents: Object.keys(formalDocsPayload).length > 0 ? formalDocsPayload : null,
        timezone: companyTimezone,
        support_email: dealerSupportEmail.trim() || null,
        dashboard_welcome: dealerDashboardWelcome.trim() || null
      });

      if (result.success) {
        setEmailConfigSuccess('Email configuration saved successfully!');
        setTimeout(() => setEmailConfigSuccess(''), 3000);
        // Refresh company data
        fetchCompanyBrandingImage(true);
      } else if (!result.requiresAuth) {
        setEmailConfigError(result.error || 'Failed to save email configuration');
      }
    } catch (error) {
      console.error('Error saving email configuration:', error);
      setEmailConfigError('Failed to save email configuration');
    }

    setEmailConfigSaving(false);
  };

  const renderContent = () => {
    switch (selectedTab) {
      case 'dashboard':
        return (
          <Container maxWidth="lg" sx={{ py: 4, pl: 0.5, pr: 4 }}>
            <Paper
              sx={{
                p: 4,
                textAlign: 'center',
                backgroundColor: companyBrandingColor || '#1976d2',
                color: 'white',
                mb: 4
              }}
            >
              <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
                Welcome to SMOD
              </Typography>
              <Typography variant="h6" sx={{ mb: 2, opacity: 0.9 }}>
                Help your dealers to sell more by motivating them!
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.8 }}>
                See the running campaigns below or add new campaign!
              </Typography>
            </Paper>

            {/* Running Campaigns */}
            <Paper sx={{ p: 3, mb: 3, backgroundColor: '#ffffff' }}>
              <Typography variant="h5" gutterBottom>
                Running Campaigns
              </Typography>
              {loadingDashboardCampaigns ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : dashboardCampaigns.length === 0 ? (
                <Alert severity="info">
                  No running campaigns found.
                </Alert>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Image</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Start Date</TableCell>
                        <TableCell>End Date</TableCell>
                        <TableCell>Dealers</TableCell>
                        {dashboardGoalSettings?.show_value_1 && (
                          <TableCell>Sales</TableCell>
                        )}
                        {dashboardGoalSettings?.show_value_2 && (
                          <TableCell></TableCell>
                        )}
                        {dashboardGoalSettings?.show_value_3 && (
                          <TableCell></TableCell>
                        )}
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dashboardCampaigns.map((campaign) => (
                        <TableRow key={campaign.id}>
                          <TableCell>
                            {campaign.campaign_image ? (
                              <Box sx={{ width: 50, height: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                                <img
                                  src={`${CDN_URL}/${campaign.campaign_image}`}
                                  alt={campaign.name}
                                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: '4px' }}
                                />
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  width: 50,
                                  height: 50,
                                  backgroundColor: '#f5f5f5',
                                  borderRadius: '4px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <Typography variant="caption" color="text.secondary">
                                  No Image
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {campaign.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {formatCampaignUtcDateForDisplay(campaign.start_date)}
                          </TableCell>
                          <TableCell>
                            {formatCampaignUtcDateForDisplay(campaign.end_date)}
                          </TableCell>
                          <TableCell>
                            <Chip label={campaign.dealer_count || 0} size="small" color="primary" />
                          </TableCell>
                          {dashboardGoalSettings?.show_value_1 && (
                            <TableCell>
                              {campaign.show_value_1 ? (
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {campaign.value_1_sum || 0}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {campaign.value_1_label || 'Value 1'}
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.disabled">
                                  -
                                </Typography>
                              )}
                            </TableCell>
                          )}
                          {dashboardGoalSettings?.show_value_2 && (
                            <TableCell>
                              {campaign.show_value_2 ? (
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {campaign.value_2_sum || 0}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {campaign.value_2_label || 'Value 2'}
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.disabled">
                                  -
                                </Typography>
                              )}
                            </TableCell>
                          )}
                          {dashboardGoalSettings?.show_value_3 && (
                            <TableCell>
                              {campaign.show_value_3 ? (
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {campaign.value_3_sum || 0}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {campaign.value_3_label || 'Value 3'}
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.disabled">
                                  -
                                </Typography>
                              )}
                            </TableCell>
                          )}
                          <TableCell>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => {
                                setSelectedTab('campaigns');
                                handleCampaignDetails(campaign.id);
                              }}
                              sx={{
                                color: companyBrandingColor || '#1976d2',
                                borderColor: companyBrandingColor || '#1976d2',
                                '&:hover': {
                                  borderColor: companyBrandingColor || '#1565c0',
                                  backgroundColor: companyBrandingColor ? 'rgba(255, 255, 255, 0.1)' : 'rgba(25, 118, 210, 0.04)',
                                }
                              }}
                            >
                              View Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>

            {/* Marketing Items */}
            <Paper sx={{ p: 3, mb: 3, backgroundColor: '#ffffff' }}>
              <Typography variant="h5" gutterBottom>
                Marketing Items
              </Typography>
              {loadingDashboardMarketing ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : dashboardMarketingItems.length === 0 ? (
                <Alert severity="info">
                  No marketing items found.
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  {dashboardMarketingItems.slice(0, 3).map((item) => (
                    <Grid item xs={12} sm={6} md={4} key={item.id}>
                      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        {item.type === 'YOUTUBE' && item.youtube_link ? (
                          <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
                            <iframe
                              src={item.youtube_link.replace('watch?v=', 'embed/')}
                              title={item.title}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                              }}
                            />
                          </Box>
                        ) : item.image ? (
                          <CardMedia
                            component="img"
                            height="200"
                            image={`${CDN_URL}/${item.image}`}
                            alt={item.title}
                            sx={{ objectFit: 'contain', backgroundColor: '#f5f5f5' }}
                          />
                        ) : null}

                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" component="h3" gutterBottom>
                            {item.title}
                          </Typography>

                          {item.description && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              dangerouslySetInnerHTML={{ __html: item.description }}
                            />
                          )}

                          {item.type === 'DOWNLOAD' && item.download_file && (
                            <Box sx={{ mt: 2 }}>
                              <Button
                                variant="contained"
                                startIcon={<DownloadIcon />}
                                href={`${CDN_URL}/${item.download_file}`}
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  backgroundColor: companyBrandingColor || '#1976d2',
                                  '&:hover': {
                                    backgroundColor: companyBrandingColor ?
                                      `${companyBrandingColor}cc` : '#1565c0'
                                  }
                                }}
                              >
                                Download
                              </Button>
                            </Box>
                          )}

                          {item.type === 'YOUTUBE' && item.youtube_link && (
                            <Box sx={{ mt: 2 }}>
                              <Button
                                variant="outlined"
                                href={item.youtube_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  color: companyBrandingColor || '#1976d2',
                                  borderColor: companyBrandingColor || '#1976d2',
                                  '&:hover': {
                                    borderColor: companyBrandingColor || '#1565c0',
                                    backgroundColor: companyBrandingColor ?
                                      'rgba(255, 255, 255, 0.1)' : 'rgba(25, 118, 210, 0.04)',
                                  }
                                }}
                              >
                                Watch on YouTube
                              </Button>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
              {dashboardMarketingItems.length > 3 && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={() => setSelectedTab('marketing')}
                    sx={{
                      color: companyBrandingColor || '#1976d2',
                      borderColor: companyBrandingColor || '#1976d2',
                    }}
                  >
                    View All Marketing Materials
                  </Button>
                </Box>
              )}
            </Paper>

            {/* Rewards Budget - Show budgets for connected providers */}
            {!isManager && (
              <Paper sx={{ p: 3, mb: 3, backgroundColor: '#ffffff' }}>
                <Typography variant="h5" gutterBottom>
                  Rewards Budget
                </Typography>
                {loadingDashboardRewards ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box>
                    {/* Tremendous Budget */}
                    {(() => {
                      const tremendousProvider = companyRewardProviders.find(crp =>
                        crp.reward_provider?.name?.toLowerCase().includes('tremendous')
                      );
                      const tremendousConnected = tremendousProvider && getTremendousConnectionStatus(tremendousProvider) === 'connected';

                      if (tremendousConnected) {
                        return (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Tremendous
                            </Typography>
                            {loadingTremendousBalance ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                                <CircularProgress size={20} />
                              </Box>
                            ) : tremendousBalance ? (
                              <Paper sx={{ p: 2, backgroundColor: 'success.main', color: 'white' }}>
                                <Typography variant="h5" component="div">
                                  {tremendousBalance.funding_sources && tremendousBalance.funding_sources.length > 0 ? (
                                    tremendousBalance.funding_sources
                                      .filter(fs => fs.method === 'balance' && fs.meta?.available_cents)
                                      .map((fs, idx) => (
                                        <span key={idx}>
                                          {formatCurrency(fs.meta.available_cents / 100, 'USD')}
                                        </span>
                                      ))
                                  ) : (
                                    'N/A'
                                  )}
                                </Typography>
                                <Typography variant="body2">
                                  Available Budget
                                </Typography>
                                {tremendousBalance.currency_conversions && Object.keys(tremendousBalance.currency_conversions).length > 0 && (
                                  <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                                    {Object.entries(tremendousBalance.currency_conversions).map(([curr, val]) => formatCurrency(val, curr)).join(' · ')}
                                  </Typography>
                                )}
                              </Paper>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Loading...
                              </Typography>
                            )}
                          </Box>
                        );
                      }
                      return null;
                    })()}

                    {/* Edenred Budget */}
                    {(() => {
                      const edenredProvider = companyRewardProviders.find(crp =>
                        crp.reward_provider?.name === 'Edenred_PL'
                      );

                      if (edenredProvider && edenredBalance) {
                        return (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Edenred_PL
                            </Typography>
                            <Paper sx={{ p: 2, backgroundColor: 'success.main', color: 'white' }}>
                              <Typography variant="h5" component="div">
                                {(() => {
                                  const balance = edenredBalance.balance || edenredBalance.amount || 0;
                                  const currency = edenredBalance.currency || 'PLN';
                                  return formatCurrency(balance, currency);
                                })()}
                              </Typography>
                              <Typography variant="body2">
                                Available Budget
                              </Typography>
                            </Paper>
                          </Box>
                        );
                      }
                      return null;
                    })()}

                    {/* Regular Providers Budget (PerfectGift, GiftMall, etc.) */}
                    {(() => {
                      // Check if there are any GiftMall or PerfectGift providers (not Tremendous, not Edenred_PL)
                      const regularProviders = companyRewardProviders.filter(crp => {
                        const name = crp.reward_provider?.name?.toLowerCase() || '';
                        return (name.includes('giftmall') || name.includes('perfectgift')) &&
                          !name.includes('tremendous') &&
                          name !== 'edenred_pl';
                      });

                      if (regularProviders.length > 0 && dashboardRewardsBudget) {
                        return (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {regularProviders.map(crp => crp.reward_provider?.display_name || crp.reward_provider?.name).filter(Boolean).join(', ')}
                            </Typography>
                            <Paper sx={{ p: 2, backgroundColor: 'success.main', color: 'white' }}>
                              <Typography variant="h5" component="div">
                                {(() => {
                                  const primaryCurrency = availableRewards.length > 0
                                    ? getPrimaryCurrency(availableRewards) || 'USD'
                                    : 'USD';
                                  return formatCurrency(dashboardRewardsBudget.balance || 0, primaryCurrency);
                                })()}
                              </Typography>
                              <Typography variant="body2">
                                Available Budget
                              </Typography>
                            </Paper>
                          </Box>
                        );
                      }
                      return null;
                    })()}

                    {/* Show message if no connected providers */}
                    {(() => {
                      const tremendousProvider = companyRewardProviders.find(crp =>
                        crp.reward_provider?.name?.toLowerCase().includes('tremendous')
                      );
                      const tremendousConnected = tremendousProvider && getTremendousConnectionStatus(tremendousProvider) === 'connected';
                      const edenredProvider = companyRewardProviders.find(crp =>
                        crp.reward_provider?.name === 'Edenred_PL'
                      );
                      const hasEdenred = edenredProvider && edenredBalance;
                      const regularProviders = companyRewardProviders.filter(crp => {
                        const name = crp.reward_provider?.name?.toLowerCase() || '';
                        return !name.includes('tremendous') && name !== 'edenred_pl';
                      });
                      const hasRegular = regularProviders.length > 0 && dashboardRewardsBudget;

                      if (!tremendousConnected && !hasEdenred && !hasRegular) {
                        return (
                          <Alert severity="info">
                            No budget information available for connected reward providers.
                          </Alert>
                        );
                      }
                      return null;
                    })()}
                  </Box>
                )}
              </Paper>
            )}

            {/* Active Quizzes */}
            {!isManager && (
              <Paper sx={{ p: 3, backgroundColor: '#ffffff' }}>
                <Typography variant="h5" gutterBottom>
                  Active Quizzes
                </Typography>
                {loadingDashboardQuizzes ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : dashboardQuizzes.length === 0 ? (
                  <Alert severity="info">
                    No active quizzes found.
                  </Alert>
                ) : (
                  <Grid container spacing={2}>
                    {dashboardQuizzes.map((quiz) => (
                      <Grid item xs={12} key={quiz.id}>
                        <Card>
                          <CardContent sx={{ pb: 1 }}>
                            <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                              {quiz.title}
                            </Typography>
                            {getQuizPreviewHtml(quiz) && (
                              <Box
                                sx={quizRichContentSx}
                                dangerouslySetInnerHTML={{ __html: getQuizPreviewHtml(quiz) }}
                              />
                            )}
                          </CardContent>
                          <Divider />
                          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
                            <Box sx={{
                              width: { xs: '100%', md: '33.333%' },
                              display: 'flex',
                              flexDirection: 'column',
                              borderRight: { md: 1 },
                              borderColor: { md: 'divider' }
                            }}>
                              <CardContent sx={{ flexGrow: 1 }}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                  <Chip
                                    label={quiz.active ? 'Active' : 'Inactive'}
                                    size="small"
                                    color={quiz.active ? 'success' : 'default'}
                                  />
                                  <Chip
                                    label={`${quiz.questions?.length || 0} questions`}
                                    size="small"
                                    variant="outlined"
                                  />
                                </Box>
                              </CardContent>
                            </Box>

                            {/* Right side - Statistics */}
                            <Box sx={{
                              width: { xs: '100%', md: '66.666%' },
                              display: 'flex',
                              flexDirection: 'column'
                            }}>
                              <CardContent>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                  Statistics
                                </Typography>

                                {quizStatsLoading[quiz.id] ? (
                                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                    <CircularProgress size={30} />
                                  </Box>
                                ) : quizStatistics[quiz.id] ? (
                                  <>
                                    {/* Summary Metrics */}
                                    <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                                      <Chip
                                        label={`${quizStatistics[quiz.id].total_attempts} Attempts`}
                                        color="primary"
                                        variant="outlined"
                                      />
                                      <Chip
                                        label={`${quizStatistics[quiz.id].unique_users} Users`}
                                        color="primary"
                                        variant="outlined"
                                      />
                                      <Chip
                                        label={`${quizStatistics[quiz.id].pass_rate}% Pass Rate`}
                                        color={
                                          quizStatistics[quiz.id].pass_rate >= 70 ? 'success' :
                                            quizStatistics[quiz.id].pass_rate >= 40 ? 'warning' :
                                              'error'
                                        }
                                        variant="filled"
                                      />
                                    </Box>

                                    {/* User Completion Table */}
                                    {quizStatistics[quiz.id].user_completions && quizStatistics[quiz.id].user_completions.length > 0 ? (
                                      <Box>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                          User Completions
                                        </Typography>
                                        <TableContainer sx={{ maxHeight: 300 }}>
                                          <Table size="small" stickyHeader>
                                            <TableHead>
                                              <TableRow>
                                                <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Dealer</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Score</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {quizStatistics[quiz.id].user_completions.map((completion) => (
                                                <TableRow key={completion.user_id}>
                                                  <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                      {completion.user_name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                      {completion.user_email}
                                                    </Typography>
                                                  </TableCell>
                                                  <TableCell>
                                                    <Typography variant="body2">
                                                      {completion.dealer_name || 'N/A'}
                                                    </Typography>
                                                    {completion.dealer_city ? (
                                                      <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{ display: 'block', mt: 0.25, lineHeight: 1.2 }}
                                                      >
                                                        {completion.dealer_city}
                                                      </Typography>
                                                    ) : null}
                                                  </TableCell>
                                                  <TableCell>
                                                    <Typography variant="body2">
                                                      {completion.score}/{completion.total_questions}
                                                    </Typography>
                                                  </TableCell>
                                                  <TableCell>
                                                    <Chip
                                                      label={completion.passed ? 'Passed' : 'Failed'}
                                                      size="small"
                                                      color={completion.passed ? 'success' : 'error'}
                                                      variant="filled"
                                                    />
                                                  </TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        </TableContainer>

                                        {/* Pagination */}
                                        <TablePagination
                                          component="div"
                                          count={quizStatistics[quiz.id].total_users || 0}
                                          page={quizStatsPage[quiz.id] || 0}
                                          onPageChange={(event, newPage) => {
                                            setQuizStatsPage(prev => ({ ...prev, [quiz.id]: newPage }));
                                            loadQuizStatistics(quiz.id, newPage + 1, quizStatsRowsPerPage[quiz.id] || 5);
                                          }}
                                          rowsPerPage={quizStatsRowsPerPage[quiz.id] || 5}
                                          onRowsPerPageChange={(event) => {
                                            const newRowsPerPage = parseInt(event.target.value, 10);
                                            setQuizStatsRowsPerPage(prev => ({ ...prev, [quiz.id]: newRowsPerPage }));
                                            setQuizStatsPage(prev => ({ ...prev, [quiz.id]: 0 }));
                                            loadQuizStatistics(quiz.id, 1, newRowsPerPage);
                                          }}
                                          rowsPerPageOptions={[5, 10, 25]}
                                          sx={{ borderTop: 1, borderColor: 'divider' }}
                                        />
                                      </Box>
                                    ) : (
                                      <Alert severity="info" sx={{ mt: 1 }}>
                                        No one has completed this quiz yet.
                                      </Alert>
                                    )}
                                  </>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    Unable to load statistics
                                  </Typography>
                                )}
                              </CardContent>
                            </Box>
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
                {dashboardQuizzes.length > 0 && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Button
                      variant="outlined"
                      onClick={() => setSelectedTab('quiz')}
                      sx={{
                        color: companyBrandingColor || '#1976d2',
                        borderColor: companyBrandingColor || '#1976d2',
                      }}
                    >
                      Manage Quizzes
                    </Button>
                  </Box>
                )}
              </Paper>
            )}
          </Container>
        );

      case 'dealers':
        return (
          <Container maxWidth="lg" sx={{ py: 4, pl: 0.5, pr: 4 }}>
            <Paper sx={{ p: 3, mb: 3, backgroundColor: '#ffffff' }}>
              <Typography variant="h5" gutterBottom>
                Dealers Management
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Manage and view dealers connected to your company.
              </Typography>

              {/* Import Success/Error Messages */}
              {importSuccess && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setImportSuccess('')}>
                  {importSuccess}
                </Alert>
              )}

              {importError && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setImportError('')}>
                  {importError}
                </Alert>
              )}

              {updateDealersSuccess && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setUpdateDealersSuccess('')}>
                  {updateDealersSuccess}
                </Alert>
              )}

              {updateDealersError && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setUpdateDealersError('')}>
                  {updateDealersError}
                </Alert>
              )}

              {/* Search and Filter Controls */}
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Search Dealers"
                  placeholder="Search by name, SF ID, city, or ASM owner..."
                  variant="outlined"
                  size="small"
                  value={dealersSearch}
                  onChange={handleDealersSearchChange}
                  sx={{ minWidth: 300 }}
                />
                <TextField
                  label="Filter by ASM"
                  select
                  variant="outlined"
                  size="small"
                  value={dealersAsmFilter}
                  onChange={handleDealersAsmFilterChange}
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="">All ASMs</MenuItem>
                  {asmsForFilter.map((asm) => (
                    <MenuItem key={asm} value={asm}>
                      {asm}
                    </MenuItem>
                  ))}
                </TextField>
                {(dealersSearch || dealersAsmFilter) && (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setDealersSearch('');
                      setDealersAsmFilter('');
                      setDealersPage(0);
                    }}
                    size="small"
                  >
                    Clear Filters
                  </Button>
                )}
                {!isManager && (
                  <>
                    <TextField
                      label="Campaign (optional)"
                      placeholder="e.g. 2026Y1"
                      variant="outlined"
                      size="small"
                      value={importDealersCampaign}
                      onChange={(e) => setImportDealersCampaign(e.target.value)}
                      sx={{ minWidth: 160 }}
                      helperText="Filter dealers by campaign"
                    />
                    <Button
                      variant="contained"
                      startIcon={importingDealers ? <CircularProgress size={20} /> : <ImportIcon />}
                      onClick={() => setImportDealersDialogOpen(true)}
                      disabled={importingDealers || updatingDealers}
                      sx={{
                        minWidth: 200,
                        height: 40
                      }}
                    >
                      {importingDealers ? 'Importing...' : 'Import Dealers from Salesforce'}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={updatingDealers ? <CircularProgress size={20} /> : <SyncIcon />}
                      onClick={() => setUpdateDealersDialogOpen(true)}
                      disabled={importingDealers || updatingDealers}
                      sx={{
                        minWidth: 120,
                        height: 40
                      }}
                    >
                      {updatingDealers ? 'Updating...' : 'Update'}
                    </Button>
                  </>
                )}
              </Box>

              {dealersError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {dealersError}
                </Alert>
              )}

              {loadingDealers ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : dealers.length === 0 ? (
                <Alert severity="info">
                  {dealersSearch || dealersAsmFilter ? 'No dealers found matching your filter criteria.' : 'No dealers found for your company.'}
                </Alert>
              ) : (
                <>
                  <TableContainer component={Paper} sx={{ mb: 3, backgroundColor: '#ffffff' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>CRM ID</TableCell>
                          <TableCell>City</TableCell>
                          <TableCell>Segmentation</TableCell>
                          <TableCell>ASM Owner</TableCell>
                          <TableCell>Campaigns</TableCell>
                          <TableCell>Created</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dealers.map((dealer) => (
                          <TableRow key={dealer.id}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {dealer.name}
                              </Typography>
                            </TableCell>
                            <TableCell>{dealer.sf_id || 'N/A'}</TableCell>
                            <TableCell>{dealer.city || 'N/A'}</TableCell>
                            <TableCell>
                              {dealer.segmentation ? (
                                <Chip label={dealer.segmentation} size="small" color="primary" />
                              ) : 'N/A'}
                            </TableCell>
                            <TableCell>{dealer.asm_owner_name || 'N/A'}</TableCell>
                            <TableCell>
                              {dealer.campaigns && dealer.campaigns.length > 0 ? (
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                  {dealer.campaigns.map((campaign, index) => (
                                    <Chip
                                      key={index}
                                      label={campaign}
                                      size="small"
                                      variant="outlined"
                                      color="secondary"
                                    />
                                  ))}
                                </Box>
                              ) : (
                                'N/A'
                              )}
                            </TableCell>
                            <TableCell>
                              {formatDateForDisplay(dealer.created)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={dealersTotal}
                    rowsPerPage={dealersRowsPerPage}
                    page={dealersPage}
                    onPageChange={handleDealersPageChange}
                    onRowsPerPageChange={handleDealersRowsPerPageChange}
                  />
                </>
              )}
            </Paper>
          </Container>
        );

      case 'users':
        return (
          <Container maxWidth="lg" sx={{ py: 4, pl: 0.5, pr: 4 }}>
            <Paper sx={{ p: 3, mb: 3, backgroundColor: '#ffffff' }}>
              <Typography variant="h5" gutterBottom>
                Users Management
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Manage and view users from your company.
              </Typography>

              {/* Search and Filter Controls */}
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Search Users"
                  placeholder="Search by name or email..."
                  variant="outlined"
                  size="small"
                  value={usersSearch}
                  onChange={handleUsersSearchChange}
                  sx={{ minWidth: 300 }}
                />
                <TextField
                  label="Filter by Dealer"
                  select
                  variant="outlined"
                  size="small"
                  value={usersDealerFilter}
                  onChange={handleUsersDealerFilterChange}
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="">All Dealers</MenuItem>
                  {dealersForFilter.map((dealer) => (
                    <MenuItem key={dealer.id} value={dealer.id}>
                      {dealer.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Filter by Role"
                  select
                  variant="outlined"
                  size="small"
                  value={usersRoleFilter}
                  onChange={handleUsersRoleFilterChange}
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="">All Roles</MenuItem>
                  <MenuItem value="Admin">Admin</MenuItem>
                  <MenuItem value="Manager">Manager</MenuItem>
                  <MenuItem value="Dealer">Dealer</MenuItem>
                </TextField>
                <TextField
                  label="Filter by ASM"
                  select
                  variant="outlined"
                  size="small"
                  value={usersAsmFilter}
                  onChange={handleUsersAsmFilterChange}
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="">All ASMs</MenuItem>
                  {asmsForFilter.map((asm) => (
                    <MenuItem key={asm} value={asm}>
                      {asm}
                    </MenuItem>
                  ))}
                </TextField>
                {(usersSearch || usersDealerFilter || usersRoleFilter || usersAsmFilter) && (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setUsersSearch('');
                      setUsersDealerFilter('');
                      setUsersRoleFilter('');
                      setUsersAsmFilter('');
                      setUsersPage(0);
                    }}
                    size="small"
                  >
                    Clear Filters
                  </Button>
                )}
                <input
                  accept=".csv,text/csv"
                  style={{ display: 'none' }}
                  ref={activationUrlsCsvInputRef}
                  type="file"
                  onChange={handleActivationUrlsFileChange}
                />
                <Button
                  variant="contained"
                  onClick={() => activationUrlsCsvInputRef.current?.click()}
                  disabled={importingActivationUrls || !user?.company_id}
                  sx={{ ml: 'auto' }}
                >
                  {importingActivationUrls ? <CircularProgress size={22} color="inherit" /> : 'Import Activation Urls'}
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                CSV columns: Email, Url. Rows for users who have already logged in are skipped (activation URL unchanged).
              </Typography>

              {usersError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {usersError}
                </Alert>
              )}
              {activationUrlsImportError && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setActivationUrlsImportError('')}>
                  {activationUrlsImportError}
                </Alert>
              )}
              {activationUrlsImportSuccess && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setActivationUrlsImportSuccess('')}>
                  {activationUrlsImportSuccess}
                </Alert>
              )}

              {loadingUsers ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : users.length === 0 ? (
                <Alert severity="info">
                  {usersSearch || usersDealerFilter || usersRoleFilter || usersAsmFilter
                    ? 'No users found matching your filter criteria.'
                    : 'No users found for your company.'}
                </Alert>
              ) : (
                <>
                  <TableContainer component={Paper} sx={{ mb: 3, backgroundColor: '#ffffff' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <TableSortLabel
                              active={usersSortBy === 'name'}
                              direction={usersSortBy === 'name' ? usersSortOrder : 'asc'}
                              onClick={() => handleUsersSort('name')}
                            >
                              Name
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={usersSortBy === 'last_name'}
                              direction={usersSortBy === 'last_name' ? usersSortOrder : 'asc'}
                              onClick={() => handleUsersSort('last_name')}
                            >
                              Last Name
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={usersSortBy === 'email'}
                              direction={usersSortBy === 'email' ? usersSortOrder : 'asc'}
                              onClick={() => handleUsersSort('email')}
                            >
                              Email
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={usersSortBy === 'role'}
                              direction={usersSortBy === 'role' ? usersSortOrder : 'asc'}
                              onClick={() => handleUsersSort('role')}
                            >
                              Role
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={usersSortBy === 'dealer'}
                              direction={usersSortBy === 'dealer' ? usersSortOrder : 'asc'}
                              onClick={() => handleUsersSort('dealer')}
                            >
                              Dealer
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={usersSortBy === 'password_tmp'}
                              direction={usersSortBy === 'password_tmp' ? usersSortOrder : 'asc'}
                              onClick={() => handleUsersSort('password_tmp')}
                            >
                              Temp Password
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={usersSortBy === 'last_login'}
                              direction={usersSortBy === 'last_login' ? usersSortOrder : 'asc'}
                              onClick={() => handleUsersSort('last_login')}
                            >
                              Last Login
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={usersSortBy === 'formal_document_accepted_at'}
                              direction={usersSortBy === 'formal_document_accepted_at' ? usersSortOrder : 'asc'}
                              onClick={() => handleUsersSort('formal_document_accepted_at')}
                            >
                              TnC Accepted
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={usersSortBy === 'created'}
                              direction={usersSortBy === 'created' ? usersSortOrder : 'asc'}
                              onClick={() => handleUsersSort('created')}
                            >
                              Created
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={usersSortBy === 'activation_url'}
                              direction={usersSortBy === 'activation_url' ? usersSortOrder : 'asc'}
                              onClick={() => handleUsersSort('activation_url')}
                            >
                              Activation URL
                            </TableSortLabel>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {users.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {user.name}
                              </Typography>
                            </TableCell>
                            <TableCell>{user.last_name || 'N/A'}</TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>
                              <Chip
                                label={user.role}
                                color={user.role === 'Admin' ? 'error' : user.role === 'Manager' ? 'warning' : 'primary'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {user.dealer ? user.dealer.name : 'N/A'}
                            </TableCell>
                            <TableCell>
                              {user.password_tmp === 'YES' ? (
                                <Chip label="Yes" color="warning" size="small" />
                              ) : (
                                <Chip label="No" color="success" size="small" />
                              )}
                            </TableCell>
                            <TableCell>
                              {user.last_login ? formatDateForDisplay(user.last_login, true) : '-'}
                            </TableCell>
                            <TableCell>
                              {user.formal_document_accepted_at ? formatDateForDisplay(user.formal_document_accepted_at, true) : '-'}
                            </TableCell>
                            <TableCell>
                              {formatDateForDisplay(user.created)}
                            </TableCell>
                            <TableCell>
                              {user.activation_url && !user.last_login ? (
                                <Link
                                  href={user.activation_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  variant="body2"
                                  title={user.activation_url}
                                >
                                  link
                                </Link>
                              ) : (
                                <Typography variant="body2" color="text.secondary">—</Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={usersTotal}
                    rowsPerPage={usersRowsPerPage}
                    page={usersPage}
                    onPageChange={handleUsersPageChange}
                    onRowsPerPageChange={handleUsersRowsPerPageChange}
                  />
                </>
              )}
            </Paper>
          </Container>
        );

      case 'rewards':
        return (
          <Container maxWidth="lg" sx={{ py: 4, pl: 0.5, pr: 4 }}>
            <Paper sx={{ p: 3, mb: 3, backgroundColor: '#ffffff' }}>
              <Typography variant="h5" gutterBottom>
                Available Rewards
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                These are the rewards available from your assigned reward providers.
              </Typography>

              {rewardsError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {rewardsError}
                </Alert>
              )}

              {budgetError && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  Budget Error: {budgetError}
                </Alert>
              )}

              {loadingRewards ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : availableRewards.length === 0 ? (
                <Alert severity="info">
                  No rewards available. Contact your administrator to assign reward providers to your company.
                </Alert>
              ) : (
                <>
                  {/* Success/Error Messages */}
                  {success && (
                    <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
                      {success}
                    </Alert>
                  )}
                  {error && (
                    <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  )}

                  {/* Rewards by Provider */}
                  {Object.entries(rewardsByProvider).map(([providerName, providerData]) => {
                    // Special handling for Tremendous
                    if (providerData.provider?.name?.toLowerCase().includes('tremendous')) {
                      const tremendousProvider = getTremendousProvider();
                      const connectionStatus = getTremendousConnectionStatus(tremendousProvider);

                      return (
                        <Box key={providerName} sx={{ mb: 4 }}>
                          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                            {providerName}
                          </Typography>

                          {/* Rewards Budget for Tremendous */}
                          {loadingTremendousBalance ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, mb: 2 }}>
                              <CircularProgress />
                            </Box>
                          ) : tremendousBalanceError ? (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                              {tremendousBalanceError}
                            </Alert>
                          ) : tremendousBalance ? (
                            <Paper sx={{ p: 2, backgroundColor: 'success.main', color: 'white', mb: 2 }}>
                              <Typography variant="h5" component="div">
                                {tremendousBalance.funding_sources && tremendousBalance.funding_sources.length > 0 ? (
                                  tremendousBalance.funding_sources
                                    .filter(fs => fs.method === 'balance' && fs.meta?.available_cents)
                                    .map((fs, idx) => (
                                      <span key={idx}>
                                        {formatCurrency(fs.meta.available_cents / 100, 'USD')}
                                      </span>
                                    ))
                                ) : (
                                  'N/A'
                                )}
                              </Typography>
                              <Typography variant="body1">
                                Rewards Budget
                              </Typography>
                              {tremendousBalance.currency_conversions && Object.keys(tremendousBalance.currency_conversions).length > 0 && (
                                <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                                  {Object.entries(tremendousBalance.currency_conversions).map(([curr, val]) => formatCurrency(val, curr)).join(' · ')}
                                </Typography>
                              )}
                            </Paper>
                          ) : (
                            <Paper sx={{ p: 2, backgroundColor: 'success.main', color: 'white', mb: 2 }}>
                              <Typography variant="h5" component="div">
                                {loadingBudget ? (
                                  <CircularProgress size={24} sx={{ color: 'white' }} />
                                ) : budgetError ? (
                                  '---'
                                ) : rewardsBudget ? (
                                  (() => {
                                    const primaryCurrency = getPrimaryCurrency(providerData.rewards) || 'USD';
                                    return formatCurrency(rewardsBudget.balance || 0, primaryCurrency);
                                  })()
                                ) : (
                                  '---'
                                )}
                              </Typography>
                              <Typography variant="body1">
                                Rewards Budget
                              </Typography>
                            </Paper>
                          )}

                          {/* Tremendous Integration Section */}
                          <Box sx={{ mb: 3, border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                              Tremendous Integration
                            </Typography>

                            {connectionStatus === 'not_setup' && (
                              <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                  Tremendous is added but not yet set up. Click below to initialize the connection.
                                </Typography>
                                {error && connectionStatus === 'not_setup' && (
                                  <Alert severity="error" sx={{ mb: 2 }}>
                                    {error}
                                  </Alert>
                                )}
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleTremendousSetup}
                                    disabled={tremendousSetupLoading}
                                    startIcon={tremendousSetupLoading ? <CircularProgress size={20} /> : <SettingsIcon />}
                                    size="large"
                                  >
                                    {tremendousSetupLoading ? 'Setting up...' : 'Set up on Tremendous'}
                                  </Button>
                                  {tremendousProvider && (
                                    <Button
                                      variant="outlined"
                                      color="error"
                                      onClick={handleTremendousDisconnect}
                                      disabled={tremendousSetupLoading}
                                      size="large"
                                    >
                                      Reset Configuration
                                    </Button>
                                  )}
                                </Box>
                              </Box>
                            )}

                            {connectionStatus === 'setup_complete' && (
                              <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                  Tremendous is set up. Click the button below to complete the OAuth flow and connect your Tremendous account. You will be redirected to Tremendous to authorize the connection.
                                </Typography>
                                {error && connectionStatus === 'setup_complete' && (
                                  <Alert severity="error" sx={{ mb: 2 }}>
                                    {error}
                                  </Alert>
                                )}
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleOpenTremendousFlow}
                                    disabled={tremendousSetupLoading}
                                    startIcon={tremendousSetupLoading ? <CircularProgress size={20} /> : <SettingsIcon />}
                                    size="large"
                                  >
                                    {tremendousSetupLoading ? 'Opening Tremendous...' : 'Open Tremendous OAuth Flow'}
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={handleTremendousDisconnect}
                                    disabled={tremendousSetupLoading}
                                    size="large"
                                  >
                                    Disconnect & Reset
                                  </Button>
                                </Box>
                              </Box>
                            )}

                            {connectionStatus === 'connected' && (
                              <Box>
                                <Chip
                                  label="Connected"
                                  color="success"
                                  sx={{ mb: 2 }}
                                />

                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                  <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => {
                                      balanceLoadAttemptedRef.current = false; // Reset ref to allow manual refresh
                                      handleGetTremendousBalance();
                                    }}
                                    disabled={loadingTremendousBalance}
                                    startIcon={loadingTremendousBalance ? <CircularProgress size={20} /> : <RefreshIcon />}
                                  >
                                    {tremendousBalance ? 'Refresh balance' : 'Available balance'}
                                  </Button>
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => setShowTremendousCampaignDialog(true)}
                                    disabled={tremendousSetupLoading}
                                  >
                                    Campaign setup
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => window.open(process.env.REACT_APP_TREMENDOUS_PANEL_URL || 'https://testflight.tremendous.com', '_blank')}
                                    startIcon={<OpenInNewIcon />}
                                  >
                                    Open Tremendous Panel
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={handleTremendousDisconnect}
                                    disabled={tremendousSetupLoading}
                                  >
                                    Disconnect
                                  </Button>
                                </Box>
                              </Box>
                            )}
                          </Box>

                          {/* Tremendous Rewards Grid */}
                          <Grid container spacing={3}>
                            {providerData.rewards.map((reward) => (
                              <Grid item xs={12} sm={6} md={4} key={reward.id}>
                                <Card
                                  sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    ...(reward.reward_image && {
                                      backgroundImage: `url(${CDN_URL}/${reward.reward_image})`,
                                      backgroundSize: 'cover',
                                      backgroundPosition: 'center',
                                      backgroundRepeat: 'no-repeat',
                                    })
                                  }}
                                >
                                  {/* Overlay for better text readability when background image exists */}
                                  {reward.reward_image && (
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        backgroundColor: 'rgba(0, 0, 0, 0.65)',
                                        zIndex: 1,
                                      }}
                                    />
                                  )}
                                  <CardContent
                                    sx={{
                                      flexGrow: 1,
                                      position: 'relative',
                                      zIndex: 2,
                                      ...(reward.reward_image && {
                                        color: 'white',
                                        '& .MuiTypography-root': {
                                          color: 'white',
                                        },
                                        '& .MuiChip-root': {
                                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                          color: 'rgba(0, 0, 0, 0.87)',
                                        },
                                      })
                                    }}
                                  >
                                    <Typography variant="h6" component="h3" gutterBottom>
                                      {reward.name}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        mb: 2,
                                        color: reward.reward_image ? 'rgba(255, 255, 255, 0.9)' : 'text.secondary'
                                      }}
                                    >
                                      {reward.description || 'No description available'}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                      {(() => {
                                        let denominationLabel = null;

                                        if (reward.denominations && reward.denominations.length > 0) {
                                          if (reward.denominations.length === 1) {
                                            const denom = reward.denominations[0];
                                            denominationLabel = formatCurrency(denom.value, denom.currency);
                                          } else {
                                            const values = reward.denominations.map(d => d.value);
                                            const minVal = Math.min(...values);
                                            const maxVal = Math.max(...values);
                                            const currency = reward.denominations[0].currency;
                                            const symbol = getCurrencySymbol(currency);
                                            denominationLabel = `${minVal} - ${maxVal} ${symbol}`;
                                          }
                                        } else if (reward.denomination) {
                                          denominationLabel = formatCurrency(reward.denomination, reward.currency || 'USD');
                                        }

                                        return denominationLabel ? (
                                          <Chip
                                            label={denominationLabel}
                                            size="small"
                                            color="success"
                                            sx={{
                                              ...(reward.reward_image && {
                                                backgroundColor: 'rgba(76, 175, 80, 0.9)',
                                                color: 'white',
                                                fontWeight: 500,
                                              })
                                            }}
                                          />
                                        ) : null;
                                      })()}
                                    </Box>
                                  </CardContent>
                                </Card>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      );
                    }

                    // Special handling for Edenred_PL (map key may be display_name e.g. "Edenred", not "Edenred_PL")
                    if (providerData.provider?.name === 'Edenred_PL') {
                      return (
                        <Box key={providerName} sx={{ mb: 4 }}>
                          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                            {providerName}
                          </Typography>
                          {loadingEdenredBalance ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                              <CircularProgress />
                            </Box>
                          ) : edenredBalanceError ? (
                            <Alert severity="warning">
                              {edenredBalanceError}
                            </Alert>
                          ) : edenredBalance ? (
                            <Paper sx={{ p: 2, backgroundColor: 'success.main', color: 'white', mb: 2 }}>
                              <Typography variant="h5" component="div">
                                {(() => {
                                  // Format balance - adjust based on actual API response structure
                                  const balance = edenredBalance.balance || edenredBalance.amount || 0;
                                  const currency = edenredBalance.currency || 'PLN';
                                  return formatCurrency(balance, currency);
                                })()}
                              </Typography>
                              <Typography variant="body1">
                                Rewards Budget
                              </Typography>
                            </Paper>
                          ) : (
                            <Alert severity="info">
                              Balance information not available
                            </Alert>
                          )}
                          {loadingEdenredUrp ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                              <CircularProgress size={32} />
                            </Box>
                          ) : edenredUrpError ? (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                              {edenredUrpError}
                            </Alert>
                          ) : edenredUrpRows.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                              No Edenred dealer card assignments yet.
                            </Typography>
                          ) : (
                            <TableContainer component={Paper} sx={{ mt: 2 }} variant="outlined">
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Dealer</TableCell>
                                    <TableCell>User</TableCell>
                                    <TableCell>Card requested</TableCell>
                                    <TableCell>Serial numbers</TableCell>
                                    <TableCell>Date</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {edenredUrpRows.map((row) => {
                                    const serialLen = row.serial_numbers?.length ?? 0;
                                    const highlight = row.card_requested > serialLen;
                                    return (
                                      <TableRow
                                        key={row.id}
                                        sx={highlight ? { backgroundColor: '#fff9c4' } : undefined}
                                      >
                                        <TableCell>{row.dealer_name || '—'}</TableCell>
                                        <TableCell>{row.email}</TableCell>
                                        <TableCell>{row.card_requested} / 3</TableCell>
                                        <TableCell sx={{ maxWidth: 360, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                          {(row.serial_numbers && row.serial_numbers.length > 0)
                                            ? row.serial_numbers.join(', ')
                                            : '—'}
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                          {row.modified ? formatDateForDisplay(row.modified, true) : '—'}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          )}
                        </Box>
                      );
                    }

                    // Standard display for PerfectGift and GiftMall
                    return (
                      <Box key={providerName} sx={{ mb: 4 }}>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                          {providerName}
                        </Typography>

                        {/* Rewards Budget for Regular Providers */}
                        <Paper sx={{ p: 2, backgroundColor: 'success.main', color: 'white', mb: 2 }}>
                          <Typography variant="h5" component="div">
                            {loadingBudget ? (
                              <CircularProgress size={24} sx={{ color: 'white' }} />
                            ) : budgetError ? (
                              '---'
                            ) : rewardsBudget ? (
                              (() => {
                                const primaryCurrency = getPrimaryCurrency(providerData.rewards) || 'USD';
                                return formatCurrency(rewardsBudget.balance || 0, primaryCurrency);
                              })()
                            ) : (
                              '---'
                            )}
                          </Typography>
                          <Typography variant="body1">
                            Rewards Budget
                          </Typography>
                        </Paper>

                        <Grid container spacing={3}>
                          {providerData.rewards.map((reward) => (
                            <Grid item xs={12} sm={6} md={4} key={reward.id}>
                              <Card
                                sx={{
                                  height: '100%',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  position: 'relative',
                                  overflow: 'hidden',
                                  ...(reward.reward_image && {
                                    backgroundImage: `url(${CDN_URL}/${reward.reward_image})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    backgroundRepeat: 'no-repeat',
                                  })
                                }}
                              >
                                {/* Overlay for better text readability when background image exists */}
                                {reward.reward_image && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 0,
                                      backgroundColor: 'rgba(0, 0, 0, 0.65)',
                                      zIndex: 1,
                                    }}
                                  />
                                )}
                                <CardContent
                                  sx={{
                                    flexGrow: 1,
                                    position: 'relative',
                                    zIndex: 2,
                                    ...(reward.reward_image && {
                                      color: 'white',
                                      '& .MuiTypography-root': {
                                        color: 'white',
                                      },
                                      '& .MuiChip-root': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                        color: 'rgba(0, 0, 0, 0.87)',
                                      },
                                    })
                                  }}
                                >
                                  <Typography variant="h6" component="h3" gutterBottom>
                                    {reward.name}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      mb: 2,
                                      color: reward.reward_image ? 'rgba(255, 255, 255, 0.9)' : 'text.secondary'
                                    }}
                                  >
                                    {reward.description || 'No description available'}
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                    {(() => {
                                      // Handle both old format (denomination) and new format (denominations array)
                                      let denominationLabel = null;

                                      if (reward.denominations && reward.denominations.length > 0) {
                                        // New format with denominations array
                                        if (reward.denominations.length === 1) {
                                          // Single denomination
                                          const denom = reward.denominations[0];
                                          denominationLabel = formatCurrency(denom.value, denom.currency);
                                        } else {
                                          // Multiple denominations - show range
                                          const values = reward.denominations.map(d => d.value);
                                          const minVal = Math.min(...values);
                                          const maxVal = Math.max(...values);
                                          const currency = reward.denominations[0].currency;
                                          const symbol = getCurrencySymbol(currency);
                                          denominationLabel = `${minVal} - ${maxVal} ${symbol}`;
                                        }
                                      } else if (reward.denomination) {
                                        // Old format with single denomination
                                        denominationLabel = formatCurrency(reward.denomination, reward.currency || 'USD');
                                      }

                                      return denominationLabel ? (
                                        <Chip
                                          label={denominationLabel}
                                          size="small"
                                          color="success"
                                          sx={{
                                            ...(reward.reward_image && {
                                              backgroundColor: 'rgba(76, 175, 80, 0.9)',
                                              color: 'white',
                                              fontWeight: 500,
                                            })
                                          }}
                                        />
                                      ) : null;
                                    })()}
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    );
                  })}
                </>
              )}
            </Paper>
          </Container>
        );

      case 'campaigns':
        if (viewingCampaignChart && selectedCampaignChart) {
          return (
            <CampaignDailySyncChartView
              campaign={selectedCampaignChart}
              days={chartDays}
              onDaysChange={setChartDays}
              onBack={handleCampaignChartClose}
            />
          );
        }
        if (isManager && !viewingCampaignDetail) {
          return (
            <Container maxWidth="lg" sx={{ py: 4, pl: 0.5, pr: 4 }}>
              <Paper sx={{ p: 3, mb: 3, backgroundColor: '#ffffff' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => setSelectedTab('dashboard')}
                    sx={{ mr: 2 }}
                  >
                    Back to Dashboard
                  </Button>
                </Box>
                <Typography variant="h5" gutterBottom>
                  Running Campaigns
                </Typography>
                {loadingDashboardCampaigns ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : dashboardCampaigns.length === 0 ? (
                  <Alert severity="info">
                    No running campaigns found.
                  </Alert>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Image</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Start Date</TableCell>
                          <TableCell>End Date</TableCell>
                          <TableCell>Dealers</TableCell>
                          {dashboardGoalSettings?.show_value_1 && (
                            <TableCell>Sales</TableCell>
                          )}
                          {dashboardGoalSettings?.show_value_2 && (
                            <TableCell></TableCell>
                          )}
                          {dashboardGoalSettings?.show_value_3 && (
                            <TableCell></TableCell>
                          )}
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dashboardCampaigns.map((campaign) => (
                          <TableRow key={campaign.id}>
                            <TableCell>
                              {campaign.campaign_image ? (
                                <Box sx={{ width: 50, height: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                                  <img
                                    src={`${CDN_URL}/${campaign.campaign_image}`}
                                    alt={campaign.name}
                                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: '4px' }}
                                  />
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    width: 50,
                                    height: 50,
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <Typography variant="caption" color="text.secondary">
                                    No Image
                                  </Typography>
                                </Box>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {campaign.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {formatCampaignUtcDateForDisplay(campaign.start_date)}
                            </TableCell>
                            <TableCell>
                              {formatCampaignUtcDateForDisplay(campaign.end_date)}
                            </TableCell>
                            <TableCell>
                              <Chip label={campaign.dealer_count || 0} size="small" color="primary" />
                            </TableCell>
                            {dashboardGoalSettings?.show_value_1 && (
                              <TableCell>
                                {campaign.show_value_1 ? (
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {campaign.value_1_sum || 0}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {campaign.value_1_label || 'Value 1'}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.disabled">
                                    -
                                  </Typography>
                                )}
                              </TableCell>
                            )}
                            {dashboardGoalSettings?.show_value_2 && (
                              <TableCell>
                                {campaign.show_value_2 ? (
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {campaign.value_2_sum || 0}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {campaign.value_2_label || 'Value 2'}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.disabled">
                                    -
                                  </Typography>
                                )}
                              </TableCell>
                            )}
                            {dashboardGoalSettings?.show_value_3 && (
                              <TableCell>
                                {campaign.show_value_3 ? (
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {campaign.value_3_sum || 0}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {campaign.value_3_label || 'Value 3'}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.disabled">
                                    -
                                  </Typography>
                                )}
                              </TableCell>
                            )}
                            <TableCell>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleCampaignDetails(campaign.id)}
                                sx={{
                                  color: companyBrandingColor || '#1976d2',
                                  borderColor: companyBrandingColor || '#1976d2',
                                  '&:hover': {
                                    borderColor: companyBrandingColor || '#1565c0',
                                    backgroundColor: companyBrandingColor ? 'rgba(255, 255, 255, 0.1)' : 'rgba(25, 118, 210, 0.04)',
                                  }
                                }}
                              >
                                View Details
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Container>
          );
        }
        if (viewingCampaignDetail) {
          return (
            <Container maxWidth="lg" sx={{ py: 4, pl: 0.5, pr: 4 }}>
              <Paper sx={{ p: 3, mb: 3, backgroundColor: '#ffffff' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleCampaignDetailClose}
                    sx={{ mr: 2 }}
                  >
                    Back to Campaigns
                  </Button>
                  <Typography variant="h5" component="div">
                    Campaign Details
                  </Typography>
                </Box>

                {campaignDetailError && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {campaignDetailError}
                  </Alert>
                )}

                {loadingCampaignDetail ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : selectedCampaignDetail ? (
                  <Box sx={{ py: 2 }}>
                    <Grid container spacing={3}>
                      {/* Campaign Image */}
                      <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          {selectedCampaignDetail.campaign_image ? (
                            <img
                              src={`${CDN_URL}/${selectedCampaignDetail.campaign_image}`}
                              alt={selectedCampaignDetail.name}
                              style={{
                                width: '100%',
                                maxWidth: '300px',
                                height: 'auto',
                                objectFit: 'contain',
                                borderRadius: '8px',
                                border: '1px solid #e0e0e0'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <Box
                            sx={{
                              width: '100%',
                              maxWidth: '300px',
                              height: '200px',
                              display: selectedCampaignDetail.campaign_image ? 'none' : 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#f5f5f5',
                              borderRadius: '8px',
                              border: '1px solid #e0e0e0',
                              margin: '0 auto'
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              No Image Available
                            </Typography>
                          </Box>

                          {/* Campaign Control Buttons */}
                          {!isManager && (
                            <Box sx={{ mt: 3 }}>
                              {/* Start/Stop Campaign Button */}
                              {selectedCampaignDetail.status === 'DRAFT' ? (
                                <Button
                                  variant="contained"
                                  startIcon={updatingCampaignStatus ? <CircularProgress size={20} /> : <PlayIcon />}
                                  onClick={handleStartCampaign}
                                  disabled={updatingCampaignStatus || syncingPoints || !selectedCampaignDetail.campaign_tag}
                                  fullWidth
                                  sx={{
                                    backgroundColor: '#4caf50',
                                    '&:hover': {
                                      backgroundColor: '#45a049',
                                    },
                                    mb: 2
                                  }}
                                >
                                  {updatingCampaignStatus ? 'Starting Campaign...' : 'Start Campaign'}
                                </Button>
                              ) : selectedCampaignDetail.status === 'RUNNING' ? (
                                <Button
                                  variant="contained"
                                  startIcon={updatingCampaignStatus ? <CircularProgress size={20} /> : <StopIcon />}
                                  onClick={handleStopCampaign}
                                  disabled={updatingCampaignStatus || syncingPoints}
                                  fullWidth
                                  sx={{
                                    backgroundColor: '#f44336',
                                    '&:hover': {
                                      backgroundColor: '#d32f2f',
                                    },
                                    mb: 2
                                  }}
                                >
                                  {updatingCampaignStatus ? 'Stopping Campaign...' : 'Stop Campaign'}
                                </Button>
                              ) : null}

                              {/* Synchronize Points Button */}
                              <Button
                                variant="contained"
                                startIcon={syncingPoints ? <CircularProgress size={20} /> : <SyncIcon />}
                                onClick={handleSynchronizePoints}
                                disabled={syncingPoints || !selectedCampaignDetail.campaign_tag || updatingCampaignStatus}
                                fullWidth
                                sx={{
                                  backgroundColor: companyBrandingColor || '#1976d2',
                                  '&:hover': {
                                    backgroundColor: companyBrandingColor || '#1565c0',
                                  },
                                }}
                              >
                                {syncingPoints ? 'Synchronizing...' : 'Synchronize Points'}
                              </Button>

                              {isTournamentCampaign(selectedCampaignDetail, companyExtraModules) && (
                                <Button
                                  variant="outlined"
                                  startIcon={
                                    applyingLeaderboardBonuses ? (
                                      <CircularProgress size={20} />
                                    ) : (
                                      <LeaderboardIcon />
                                    )
                                  }
                                  onClick={handleApplyLeaderboardBonuses}
                                  disabled={
                                    applyingLeaderboardBonuses
                                    || syncingPoints
                                    || updatingCampaignStatus
                                  }
                                  fullWidth
                                  sx={{ mt: 2 }}
                                >
                                  {applyingLeaderboardBonuses
                                    ? 'Applying bonuses...'
                                    : 'Apply Leaderboard Bonuses'}
                                </Button>
                              )}

                              {syncSuccess && (
                                <Alert severity="success" sx={{ mt: 2 }}>
                                  {syncSuccess}
                                </Alert>
                              )}

                              {syncError && (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                  {syncError}
                                </Alert>
                              )}

                              {leaderboardBonusMessage && (
                                <Alert severity="success" sx={{ mt: 2 }}>
                                  {leaderboardBonusMessage}
                                </Alert>
                              )}

                              {leaderboardBonusError && (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                  {leaderboardBonusError}
                                </Alert>
                              )}
                            </Box>
                          )}
                        </Box>
                      </Grid>

                      {/* Campaign Details */}
                      <Grid item xs={12} md={8}>
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                            {selectedCampaignDetail.name}
                          </Typography>

                          {selectedCampaignDetail.campaign_tag && (
                            <Chip
                              label={selectedCampaignDetail.campaign_tag}
                              color="primary"
                              variant="outlined"
                              sx={{ mb: 2 }}
                            />
                          )}

                          {selectedCampaignDetail.header && (
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: companyBrandingColor || '#1976d2' }}>
                                {selectedCampaignDetail.header}
                              </Typography>
                            </Box>
                          )}

                          {selectedCampaignDetail.description && (
                            <Box sx={{ mb: 3 }}>

                              <Box
                                sx={{
                                  ...quizRichHtmlBlockSx,
                                  '& a': {
                                    color: 'primary.main',
                                    textDecoration: 'underline'
                                  }
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: selectedCampaignDetail.description
                                }}
                              />
                            </Box>
                          )}

                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                            <Chip
                              label={`Type: ${selectedCampaignDetail.type}`}
                              color={selectedCampaignDetail.type === 'POINTS' ? 'primary' : 'secondary'}
                            />
                            <Chip
                              label={`Status: ${selectedCampaignDetail.status}`}
                              color={selectedCampaignDetail.status === 'RUNNING' ? 'success' : selectedCampaignDetail.status === 'DRAFT' ? 'warning' : 'default'}
                            />
                            {selectedCampaignDetail.dealers && selectedCampaignDetail.dealers.length > 0 && (
                              <Chip
                                label={`${selectedCampaignDetail.dealers.length} Dealer${selectedCampaignDetail.dealers.length > 1 ? 's' : ''}`}
                                variant="outlined"
                              />
                            )}
                          </Box>

                          {(selectedCampaignDetail.start_date || selectedCampaignDetail.end_date) && (
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                                Campaign Period
                              </Typography>
                              {selectedCampaignDetail.start_date && (
                                <Typography variant="body2" color="text.secondary">
                                  Start: {formatCampaignUtcDateForDisplay(selectedCampaignDetail.start_date, true)}
                                </Typography>
                              )}
                              {selectedCampaignDetail.end_date && (
                                <Typography variant="body2" color="text.secondary">
                                  End: {formatCampaignUtcDateForDisplay(selectedCampaignDetail.end_date, true)}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      </Grid>

                      {/* Rewards Section - for non-Tournament campaigns */}
                      {selectedCampaignDetail.type !== 'EXTRA' && (
                        <Grid item xs={12}>
                          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                            Connected Rewards
                          </Typography>

                          {selectedCampaignDetail.rewards && selectedCampaignDetail.rewards.length > 0 ? (
                            <Grid container spacing={1.5}>
                              {selectedCampaignDetail.rewards
                                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                                .map((reward) => (
                                  <Grid item xs={6} sm={4} md={3} lg={2} key={reward.id}>
                                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                      <Box sx={{ p: 1.5, textAlign: 'center' }}>
                                        {reward.reward_image ? (
                                          <img
                                            src={`${CDN_URL}/${reward.reward_image}`}
                                            alt={reward.name}
                                            style={{
                                              width: '100px',
                                              height: '70px',
                                              objectFit: 'contain',
                                              borderRadius: '6px',
                                              border: '1px solid #e0e0e0'
                                            }}
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                              e.target.nextSibling.style.display = 'flex';
                                            }}
                                          />
                                        ) : null}
                                        <Box
                                          sx={{
                                            width: '100px',
                                            height: '70px',
                                            display: reward.reward_image ? 'none' : 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: '#f5f5f5',
                                            borderRadius: '6px',
                                            border: '1px solid #e0e0e0',
                                            margin: '0 auto'
                                          }}
                                        >
                                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px' }}>
                                            No Image
                                          </Typography>
                                        </Box>
                                      </Box>
                                      <CardContent sx={{ flexGrow: 1, pt: 0, pb: 1.5, px: 1.5 }}>
                                        <Typography variant="subtitle2" component="div" gutterBottom sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                                          {reward.name}
                                        </Typography>
                                        {reward.description && (
                                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontSize: '0.75rem' }}>
                                            {reward.description}
                                          </Typography>
                                        )}
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                          {(() => {
                                            const denoms = reward.denominations && reward.denominations.length > 0
                                              ? reward.denominations
                                              : (reward.denomination ? [{ value: reward.denomination, currency: reward.currency || 'USD', is_default: true }] : []);
                                            const defaultDenom = denoms.find(d => d.is_default) || denoms[0];

                                            if (denoms.length > 1) {
                                              return (
                                                <Select
                                                  size="small"
                                                  defaultValue={defaultDenom ? `${defaultDenom.value}-${defaultDenom.currency}` : ''}
                                                  sx={{
                                                    fontSize: '0.7rem',
                                                    height: '28px',
                                                    minWidth: '100px',
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                      borderColor: 'primary.main'
                                                    },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                      borderColor: 'primary.main'
                                                    },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                      borderColor: 'primary.main'
                                                    }
                                                  }}
                                                >
                                                  {denoms.map((denom, idx) => (
                                                  <MenuItem key={idx} value={`${denom.value}-${denom.currency}`}>
                                                    {`${denom.value} ${denom.currency}`}
                                                  </MenuItem>
                                                ))}
                                              </Select>
                                            );
                                          } else if (defaultDenom) {
                                            return (
                                              <Chip
                                                label={`${defaultDenom.value} ${defaultDenom.currency}`}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                                sx={{ fontSize: '0.7rem', height: '24px' }}
                                              />
                                            );
                                          }
                                          return null;
                                        })()}
                                        {reward.reward_provider && (
                                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                            {reward.reward_provider.display_name || reward.reward_provider.name}
                                          </Typography>
                                        )}
                                      </Box>
                                    </CardContent>
                                  </Card>
                                </Grid>
                              ))}
                          </Grid>
                        ) : (
                          <Alert severity="info">
                            No rewards connected to this campaign.
                          </Alert>
                        )}
                      </Grid>
                      )}

                      {/* Match Predictor Stats (Admin View) */}
                      {selectedCampaignDetail.type === 'EXTRA' && selectedCampaignDetail.company_extra_module_id && (
                        <Grid item xs={12}>
                          {isTournamentCampaign(selectedCampaignDetail, companyExtraModules) && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
                              <Button
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={handleActiveDealersExportExcel}
                                disabled={activeDealersExporting || loadingCampaignDetail}
                              >
                                {activeDealersExporting ? 'Exporting…' : 'Active Dealers'}
                              </Button>
                              <Button
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={handleTournamentExportExcel}
                                disabled={tournamentExporting || loadingCampaignDetail}
                              >
                                {tournamentExporting ? 'Exporting…' : 'Export to Excel'}
                              </Button>
                            </Box>
                          )}
                          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                            MATCHES PREDICTIONS
                          </Typography>
                          {(() => {
                            const moduleConfig = companyExtraModules?.find(m => m.id === selectedCampaignDetail.company_extra_module_id)?.config || {};
                            const games = moduleConfig.games || [];
                            
                            if (games.length === 0) return <Alert severity="info">No games configured for predictions.</Alert>;
                            
                            return (
                              <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', mb: 3 }}>
                                <Table>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 600 }}>Match</TableCell>
                                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                      <TableCell align="center" sx={{ fontWeight: 600 }}>Score</TableCell>
                                      <TableCell align="center" sx={{ fontWeight: 600 }}>Predictions</TableCell>
                                      <TableCell align="center" sx={{ fontWeight: 600 }}>Correct</TableCell>
                                      <TableCell align="center" sx={{ fontWeight: 600 }}>Action</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {games.map((game, idx) => {
                                      let predictionCount = 0;
                                      if (campaignGoals) {
                                        campaignGoals.forEach(g => {
                                          const gamePred = g.extra?.games?.[idx];
                                          if (hasMatchPrediction(game, gamePred)) {
                                            predictionCount++;
                                          }
                                        });
                                      }
                                      return (
                                        <TableRow key={idx}>
                                          <TableCell>
                                            <Typography variant="body2" fontWeight="medium">
                                              <span style={{ marginRight: '8px' }}>{game.team1_flag || '🇺🇳'}</span>
                                              {game.team1} x {game.team2}
                                              <span style={{ marginLeft: '8px' }}>{game.team2_flag || '🇺🇳'}</span>
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              {game.stage} • {game.venue}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            {(() => {
                                              const formatted = formatMatchDateTime(game.date, game.time, companyTimezone);
                                              return <Typography variant="body2">{formatted.date} {formatted.time}</Typography>;
                                            })()}
                                          </TableCell>
                                          <TableCell align="center">
                                            <Typography variant="body2">
                                              {getOfficialMatchScoreText(game) ?? '-'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align="center">
                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                              <Chip label={predictionCount} size="small" color="primary" variant="outlined" />
                                              {predictionCount > 0 && (
                                                <IconButton
                                                  size="small"
                                                  aria-label="View predictions"
                                                  title="View predictions"
                                                  onClick={() => openMatchPredictionsDialog(game, idx)}
                                                  sx={{ color: companyBrandingColor || '#1976d2' }}
                                                >
                                                  <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                              )}
                                            </Box>
                                          </TableCell>
                                          <TableCell align="center">
                                            <Chip label="0" size="small" color="default" variant="outlined" />
                                          </TableCell>
                                          <TableCell align="center">
                                            <IconButton
                                              size="small"
                                              aria-label="Edit match score"
                                              onClick={() => openMatchScoreDialog(game, idx)}
                                              disabled={Boolean(matchScoreSaving)}
                                            >
                                              <EditIcon fontSize="small" />
                                            </IconButton>
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            );
                          })()}
                          <Dialog
                            open={matchScoreDialogOpen}
                            onClose={() => {
                              if (!matchScoreSaving) closeMatchScoreDialog();
                            }}
                            maxWidth="xs"
                            fullWidth
                          >
                            <DialogTitle>Edit match score</DialogTitle>
                            <DialogContent>
                              {matchScoreDialogGame && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                  <span style={{ marginRight: 6 }}>{matchScoreDialogGame.team1_flag || '🇺🇳'}</span>
                                  {matchScoreDialogGame.team1} vs {matchScoreDialogGame.team2}
                                  <span style={{ marginLeft: 6 }}>{matchScoreDialogGame.team2_flag || '🇺🇳'}</span>
                                </Typography>
                              )}
                              {matchScoreError && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                  {matchScoreError}
                                </Alert>
                              )}
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
                                <TextField
                                  label={matchScoreDialogGame ? `Goals - ${matchScoreDialogGame.team1}` : 'Goals - team 1'}
                                  value={matchScoreTeamA}
                                  onChange={(e) => setMatchScoreTeamA(e.target.value)}
                                  disabled={matchScoreSaving}
                                  fullWidth
                                  inputProps={{ inputMode: 'numeric' }}
                                />
                                <TextField
                                  label={matchScoreDialogGame ? `Goals - ${matchScoreDialogGame.team2}` : 'Goals - team 2'}
                                  value={matchScoreTeamB}
                                  onChange={(e) => setMatchScoreTeamB(e.target.value)}
                                  disabled={matchScoreSaving}
                                  fullWidth
                                  inputProps={{ inputMode: 'numeric' }}
                                />
                              </Box>
                            </DialogContent>
                            <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
                              <Button
                                onClick={handleDeleteMatchScore}
                                color="error"
                                disabled={matchScoreSaving}
                              >
                                Delete the score
                              </Button>
                              <Box sx={{ flexGrow: 1 }} />
                              <Button onClick={closeMatchScoreDialog} disabled={matchScoreSaving}>
                                Cancel
                              </Button>
                              <Button
                                variant="contained"
                                onClick={handleSaveMatchScore}
                                disabled={matchScoreSaving}
                              >
                                Save
                              </Button>
                            </DialogActions>
                          </Dialog>
                          <Dialog
                            open={matchPredictionsDialogOpen}
                            onClose={closeMatchPredictionsDialog}
                            maxWidth="md"
                            fullWidth
                          >
                            <DialogTitle>Match predictions</DialogTitle>
                            <DialogContent>
                              {matchPredictionsDialogGame && (() => {
                                const officialScore = getOfficialMatchScoreText(matchPredictionsDialogGame);
                                return (
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    <span style={{ marginRight: 6 }}>{matchPredictionsDialogGame.team1_flag || '🇺🇳'}</span>
                                    {matchPredictionsDialogGame.team1} vs {matchPredictionsDialogGame.team2}
                                    <span style={{ marginLeft: 6 }}>{matchPredictionsDialogGame.team2_flag || '🇺🇳'}</span>
                                    {officialScore != null && (
                                      <span style={{ marginLeft: 12, fontWeight: 500, color: 'text.primary' }}>
                                        {officialScore}
                                      </span>
                                    )}
                                  </Typography>
                                );
                              })()}
                              {(() => {
                                const dialogGame = matchPredictionsDialogGame;
                                const dialogIdx = matchPredictionsDialogIndex;
                                if (!dialogGame || dialogIdx == null) return null;

                                const showPoints = getOfficialMatchScoreText(dialogGame) != null;
                                const rows = (campaignGoals || [])
                                  .filter((g) => hasMatchPrediction(dialogGame, g.extra?.games?.[dialogIdx]))
                                  .map((g) => {
                                    const predEntry = g.extra?.games?.[dialogIdx];
                                    return {
                                      id: g.id,
                                      dealerName: g.dealer?.name || 'Unknown Dealer',
                                      nickname: g.dealer?.tournament_nickname || '-',
                                      prediction: formatDealerPrediction(dialogGame, predEntry),
                                      points: pointsForPastGame(dialogGame, predEntry),
                                    };
                                  })
                                  .sort((a, b) => a.dealerName.localeCompare(b.dealerName));

                                if (rows.length === 0) {
                                  return <Alert severity="info">No predictions for this match.</Alert>;
                                }

                                return (
                                  <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                                          <TableCell sx={{ fontWeight: 600 }}>Team name</TableCell>
                                          <TableCell sx={{ fontWeight: 600 }}>Prediction</TableCell>
                                          {showPoints && (
                                            <TableCell align="center" sx={{ fontWeight: 600 }}>Points</TableCell>
                                          )}
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {rows.map((row) => (
                                          <TableRow key={row.id}>
                                            <TableCell>
                                              <Typography variant="body2">{row.dealerName}</Typography>
                                            </TableCell>
                                            <TableCell>
                                              <Typography variant="body2">{row.nickname}</Typography>
                                            </TableCell>
                                            <TableCell>
                                              <Typography variant="body2">{row.prediction}</Typography>
                                            </TableCell>
                                            {showPoints && (
                                              <TableCell align="center">
                                                <Typography variant="body2">
                                                  {row.points == null ? '-' : row.points}
                                                </Typography>
                                              </TableCell>
                                            )}
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                );
                              })()}
                            </DialogContent>
                            <DialogActions sx={{ px: 3, pb: 2 }}>
                              <Button onClick={closeMatchPredictionsDialog}>Close</Button>
                            </DialogActions>
                          </Dialog>
                        </Grid>
                      )}


                      {/* Dealer Goals Section */}
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                          <Typography variant="h5" sx={{ fontWeight: 600 }}>
                            Dealer Performance
                          </Typography>
                          {campaignGoals && campaignGoals.length > 0 && (
                            <FormControl sx={{ minWidth: 200 }} size="small">
                              <InputLabel>ASM Owner</InputLabel>
                              <Select
                                value={asmFilter}
                                label="ASM Owner"
                                onChange={(e) => setAsmFilter(e.target.value)}
                              >
                                <MenuItem value="all">All ASM Owners</MenuItem>
                                {getUniqueAsmOwners().map((asmOwner) => (
                                  <MenuItem key={asmOwner} value={asmOwner}>
                                    {asmOwner}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        </Box>

                        {campaignGoals && campaignGoals.length > 0 ? (
                          (() => {
                            const dealerPerformanceRows = getSortedGoals();
                            const dpFooter = getDealerPerformanceFooterStats(dealerPerformanceRows);
                            const footerCellSx = {
                              borderTop: '2px solid',
                              borderColor: 'divider',
                              bgcolor: 'action.hover',
                              fontWeight: 600
                            };
                            return (
                          <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
                            <Table>
                              <TableHead>
                                <TableRow>
                                  <TableCell
                                    onClick={() => handleGoalsSortChange('dealer')}
                                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      Dealer Name
                                      {goalsSortColumn === 'dealer' && (
                                        goalsSortDirection === 'asc' ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />
                                      )}
                                    </Box>
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    onClick={() => handleGoalsSortChange('points')}
                                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                      Points
                                      {goalsSortColumn === 'points' && (
                                        goalsSortDirection === 'asc' ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />
                                      )}
                                    </Box>
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    onClick={() => handleGoalsSortChange('used')}
                                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                      Used
                                      {goalsSortColumn === 'used' && (
                                        goalsSortDirection === 'asc' ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />
                                      )}
                                    </Box>
                                  </TableCell>
                                  {selectedCampaignDetail.show_value_1 && (
                                    <TableCell
                                      align="right"
                                      onClick={() => handleGoalsSortChange('value_1')}
                                      sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                        {selectedCampaignDetail.value_1_label || 'Value 1'}
                                        {goalsSortColumn === 'value_1' && (
                                          goalsSortDirection === 'asc' ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />
                                        )}
                                      </Box>
                                    </TableCell>
                                  )}
                                  {selectedCampaignDetail.show_value_2 && (
                                    <TableCell
                                      align="right"
                                      onClick={() => handleGoalsSortChange('value_2')}
                                      sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                        {selectedCampaignDetail.value_2_label || 'Value 2'}
                                        {goalsSortColumn === 'value_2' && (
                                          goalsSortDirection === 'asc' ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />
                                        )}
                                      </Box>
                                    </TableCell>
                                  )}
                                  {selectedCampaignDetail.show_value_3 && (
                                    <TableCell
                                      align="right"
                                      onClick={() => handleGoalsSortChange('value_3')}
                                      sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                        {selectedCampaignDetail.value_3_label || 'Value 3'}
                                        {goalsSortColumn === 'value_3' && (
                                          goalsSortDirection === 'asc' ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />
                                        )}
                                      </Box>
                                    </TableCell>
                                  )}
                                  {selectedCampaignDetail.show_status && (
                                    <TableCell
                                      align="center"
                                      onClick={() => handleGoalsSortChange('status')}
                                      sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                        Status
                                        {goalsSortColumn === 'status' && (
                                          goalsSortDirection === 'asc' ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />
                                        )}
                                      </Box>
                                    </TableCell>
                                  )}
                                  <TableCell
                                    onClick={() => handleGoalsSortChange('asm_owner')}
                                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      ASM Owner
                                      {goalsSortColumn === 'asm_owner' && (
                                        goalsSortDirection === 'asc' ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />
                                      )}
                                    </Box>
                                  </TableCell>
                                  <TableCell
                                    onClick={() => handleGoalsSortChange('last_login')}
                                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      Last Login
                                      {goalsSortColumn === 'last_login' && (
                                        goalsSortDirection === 'asc' ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />
                                      )}
                                    </Box>
                                  </TableCell>
                                  <TableCell
                                    onClick={() => handleGoalsSortChange('last_points_gain_at')}
                                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      Last Points
                                      {goalsSortColumn === 'last_points_gain_at' && (
                                        goalsSortDirection === 'asc' ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />
                                      )}
                                    </Box>
                                  </TableCell>
                                  <TableCell align="center" sx={{ width: 64 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                      Add
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {dealerPerformanceRows.map((goal) => (
                                  <TableRow key={goal.id}>
                                    <TableCell>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {goal.dealer ? goal.dealer.name : 'Unknown Dealer'}
                                      </Typography>
                                      {goal.dealer?.city ? (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: 'block', mt: 0.25, lineHeight: 1.2 }}
                                        >
                                          {goal.dealer.city}
                                        </Typography>
                                      ) : null}
                                    </TableCell>
                                    <TableCell align="right">
                                      <Chip
                                        label={goalEarnedTotal(goal)}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                      />
                                    </TableCell>
                                    <TableCell align="right">
                                      <Chip
                                        label={goal.used_points || 0}
                                        size="small"
                                        color="secondary"
                                        variant="outlined"
                                      />
                                    </TableCell>
                                    {selectedCampaignDetail.show_value_1 && (
                                      <TableCell align="right">{goal.value_1 || 0}</TableCell>
                                    )}
                                    {selectedCampaignDetail.show_value_2 && (
                                      <TableCell align="right">{goal.value_2 || 0}</TableCell>
                                    )}
                                    {selectedCampaignDetail.show_value_3 && (
                                      <TableCell align="right">{goal.value_3 || 0}</TableCell>
                                    )}
                                    {selectedCampaignDetail.show_status && (
                                      <TableCell align="center">
                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                          <Chip
                                            label="1"
                                            size="small"
                                            color={goal.done_1 ? 'success' : 'default'}
                                            variant={goal.done_1 ? 'filled' : 'outlined'}
                                          />
                                          <Chip
                                            label="2"
                                            size="small"
                                            color={goal.done_2 ? 'success' : 'default'}
                                            variant={goal.done_2 ? 'filled' : 'outlined'}
                                          />
                                          <Chip
                                            label="3"
                                            size="small"
                                            color={goal.done_3 ? 'success' : 'default'}
                                            variant={goal.done_3 ? 'filled' : 'outlined'}
                                          />
                                        </Box>
                                      </TableCell>
                                    )}
                                    <TableCell>
                                      <Typography variant="body2">
                                        {goal.dealer?.asm_owner_name || '-'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2">
                                        {goal.last_login ? formatDateForDisplay(goal.last_login, true) : '-'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2">
                                        {goal.last_points_gain_at ? formatDateForDisplay(goal.last_points_gain_at) : '-'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                      <IconButton
                                        size="small"
                                        color={goalHasManualExtras(goal) ? 'error' : 'primary'}
                                        aria-label="Edit manual points or sales"
                                        onClick={() => openManualAdjustDialog(goal)}
                                      >
                                        <PostAddIcon fontSize="small" />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                              <TableFooter>
                                <TableRow>
                                  <TableCell sx={footerCellSx}>
                                    <Typography variant="body2" fontWeight={600}>
                                      Total
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right" sx={footerCellSx}>
                                    <Chip
                                      label={dpFooter.sumPoints}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell align="right" sx={footerCellSx}>
                                    <Chip
                                      label={dpFooter.sumUsed}
                                      size="small"
                                      color="secondary"
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  {selectedCampaignDetail.show_value_1 && (
                                    <TableCell align="right" sx={footerCellSx}>
                                      {dpFooter.sumValue1}
                                    </TableCell>
                                  )}
                                  {selectedCampaignDetail.show_value_2 && (
                                    <TableCell align="right" sx={footerCellSx}>
                                      {dpFooter.sumValue2}
                                    </TableCell>
                                  )}
                                  {selectedCampaignDetail.show_value_3 && (
                                    <TableCell align="right" sx={footerCellSx}>
                                      {dpFooter.sumValue3}
                                    </TableCell>
                                  )}
                                  {selectedCampaignDetail.show_status && (
                                    <TableCell align="center" sx={footerCellSx}>
                                      <Typography variant="body2" sx={{ textAlign: 'center' }}>
                                        {dpFooter.statusDone1} / {dpFooter.statusDone2} / {dpFooter.statusDone3}
                                      </Typography>
                                    </TableCell>
                                  )}
                                  <TableCell sx={footerCellSx} />
                                  <TableCell sx={footerCellSx}>
                                    <Typography variant="body2">
                                      {dpFooter.dealerCount > 0
                                        ? `${dpFooter.loggedInCount} / ${dpFooter.dealerCount}`
                                        : '—'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={footerCellSx} />
                                  <TableCell align="center" sx={footerCellSx} />
                                </TableRow>
                              </TableFooter>
                            </Table>
                          </TableContainer>
                            );
                          })()
                        ) : (
                          <Alert severity="info">
                            No dealer performance data available. Click "Synchronize Points" to load data.
                          </Alert>
                        )}

                        <Dialog
                          open={manualAdjustOpen}
                          onClose={(_, reason) => {
                            if (manualAdjustSaving && reason === 'backdropClick') return;
                            if (!manualAdjustSaving) closeManualAdjustDialog();
                          }}
                          maxWidth="xs"
                          fullWidth
                        >
                          <DialogTitle>
                            Manual bonus
                            {manualAdjustGoal?.dealer?.name
                              ? ` — ${manualAdjustGoal.dealer.name}`
                              : ''}
                          </DialogTitle>
                          <DialogContent>
                            {manualAdjustError && (
                              <Alert severity="error" sx={{ mb: 2 }}>
                                {manualAdjustError}
                              </Alert>
                            )}
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              Edit manual bonuses only (SMOD totals stay separate and sync as before). Use 0 to clear a manual bonus.
                            </Typography>
                            <TextField
                              margin="dense"
                              fullWidth
                              label="Manual points"
                              type="number"
                              inputProps={{ min: 0, step: 'any' }}
                              value={manualAdjustValues.manual_points}
                              onChange={(e) => handleManualAdjustValueChange('manual_points', e.target.value)}
                              disabled={manualAdjustSaving}
                            />
                            {selectedCampaignDetail?.show_value_1 && (
                              <TextField
                                margin="dense"
                                fullWidth
                                label={`Manual ${selectedCampaignDetail.value_1_label || 'Value 1'}`}
                                type="number"
                                inputProps={{ min: 0, step: 'any' }}
                                value={manualAdjustValues.manual_value_1}
                                onChange={(e) => handleManualAdjustValueChange('manual_value_1', e.target.value)}
                                disabled={manualAdjustSaving}
                              />
                            )}
                            {selectedCampaignDetail?.show_value_2 && (
                              <TextField
                                margin="dense"
                                fullWidth
                                label={`Manual ${selectedCampaignDetail.value_2_label || 'Value 2'}`}
                                type="number"
                                inputProps={{ min: 0, step: 'any' }}
                                value={manualAdjustValues.manual_value_2}
                                onChange={(e) => handleManualAdjustValueChange('manual_value_2', e.target.value)}
                                disabled={manualAdjustSaving}
                              />
                            )}
                            {selectedCampaignDetail?.show_value_3 && (
                              <TextField
                                margin="dense"
                                fullWidth
                                label={`Manual ${selectedCampaignDetail.value_3_label || 'Value 3'}`}
                                type="number"
                                inputProps={{ min: 0, step: 'any' }}
                                value={manualAdjustValues.manual_value_3}
                                onChange={(e) => handleManualAdjustValueChange('manual_value_3', e.target.value)}
                                disabled={manualAdjustSaving}
                              />
                            )}
                          </DialogContent>
                          <DialogActions>
                            <Button onClick={closeManualAdjustDialog} disabled={manualAdjustSaving}>
                              Cancel
                            </Button>
                            <Button
                              variant="contained"
                              onClick={handleSubmitManualAdjust}
                              disabled={manualAdjustSaving}
                            >
                              {manualAdjustSaving ? 'Saving…' : 'Apply'}
                            </Button>
                          </DialogActions>
                        </Dialog>
                      </Grid>

                      {/* Related Quizzes Section */}
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                          <Typography variant="h5" sx={{ fontWeight: 600 }}>
                            Related Quizzes
                          </Typography>
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAddCampaignQuiz}
                            sx={{
                              backgroundColor: companyBrandingColor || '#1976d2',
                              '&:hover': {
                                backgroundColor: companyBrandingColor || '#1565c0',
                              },
                            }}
                          >
                            Add Quiz
                          </Button>
                        </Box>

                        {campaignQuizzesError && (
                          <Alert severity="error" sx={{ mb: 3 }}>
                            {campaignQuizzesError}
                          </Alert>
                        )}

                        {loadingCampaignQuizzes ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                          </Box>
                        ) : campaignQuizzes && campaignQuizzes.length > 0 ? (
                          <Grid container spacing={2}>
                            {campaignQuizzes.map((quiz) => (
                              <Grid item xs={12} key={quiz.id}>
                                <Card>
                                  <CardContent sx={{ pb: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                      <Typography variant="h6" component="div" sx={{ fontWeight: 600, flexGrow: 1 }}>
                                        {quiz.title}
                                      </Typography>
                                      <Chip
                                        label={quiz.active ? 'Active' : 'Inactive'}
                                        size="small"
                                        color={quiz.active ? 'success' : 'default'}
                                        sx={{ ml: 1 }}
                                      />
                                    </Box>
                                    {getQuizPreviewHtml(quiz) && (
                                      <Box
                                        sx={quizRichContentSx}
                                        dangerouslySetInnerHTML={{ __html: getQuizPreviewHtml(quiz) }}
                                      />
                                    )}
                                  </CardContent>
                                  <Divider />
                                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
                                    <Box sx={{
                                      width: { xs: '100%', md: '33.333%' },
                                      display: 'flex',
                                      flexDirection: 'column',
                                      borderRight: { md: 1 },
                                      borderColor: { md: 'divider' }
                                    }}>
                                      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                          <Chip
                                            label={`${quiz.reward || 0} points`}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                          />
                                          <Chip
                                            label={`${quiz.questions?.length || 0} questions`}
                                            size="small"
                                            variant="outlined"
                                          />
                                        </Box>
                                      </CardContent>
                                      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                          <IconButton
                                            size="small"
                                            onClick={() => handleEditCampaignQuiz(quiz)}
                                            title="Edit Quiz"
                                            sx={{ color: 'primary.main' }}
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                          <IconButton
                                            size="small"
                                            onClick={() => handleDeleteCampaignQuiz(quiz.id)}
                                            title="Delete Quiz"
                                            sx={{ color: 'error.main' }}
                                          >
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        </Box>
                                        <Button
                                          size="small"
                                          variant={quiz.active ? 'outlined' : 'contained'}
                                          color={quiz.active ? 'warning' : 'success'}
                                          onClick={() => handleToggleQuizActive(quiz)}
                                        >
                                          {quiz.active ? 'Deactivate' : 'Activate'}
                                        </Button>
                                      </CardActions>
                                    </Box>

                                    {/* Right side - Statistics */}
                                    <Box sx={{
                                      width: { xs: '100%', md: '66.666%' },
                                      display: 'flex',
                                      flexDirection: 'column'
                                    }}>
                                      <CardContent>
                                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                          Statistics
                                        </Typography>

                                        {quizStatsLoading[quiz.id] ? (
                                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                            <CircularProgress size={30} />
                                          </Box>
                                        ) : quizStatistics[quiz.id] ? (
                                          <>
                                            {/* Summary Metrics */}
                                            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                                              <Chip
                                                label={`${quizStatistics[quiz.id].total_attempts} Attempts`}
                                                color="primary"
                                                variant="outlined"
                                              />
                                              <Chip
                                                label={`${quizStatistics[quiz.id].unique_users} Users`}
                                                color="primary"
                                                variant="outlined"
                                              />
                                              <Chip
                                                label={`${quizStatistics[quiz.id].pass_rate}% Pass Rate`}
                                                color={
                                                  quizStatistics[quiz.id].pass_rate >= 70 ? 'success' :
                                                    quizStatistics[quiz.id].pass_rate >= 40 ? 'warning' :
                                                      'error'
                                                }
                                                variant="filled"
                                              />
                                            </Box>

                                            {/* User Completion Table */}
                                            {quizStatistics[quiz.id].user_completions && quizStatistics[quiz.id].user_completions.length > 0 ? (
                                              <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                                  User Completions
                                                </Typography>
                                                <TableContainer sx={{ maxHeight: 300 }}>
                                                  <Table size="small" stickyHeader>
                                                    <TableHead>
                                                      <TableRow>
                                                        <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>Dealer</TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>Score</TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                                      </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                      {quizStatistics[quiz.id].user_completions.map((completion) => (
                                                        <TableRow key={completion.user_id}>
                                                          <TableCell>
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                              {completion.user_name}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                              {completion.user_email}
                                                            </Typography>
                                                          </TableCell>
                                                          <TableCell>
                                                            <Typography variant="body2">
                                                              {completion.dealer_name || 'N/A'}
                                                            </Typography>
                                                            {completion.dealer_city ? (
                                                              <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{ display: 'block', mt: 0.25, lineHeight: 1.2 }}
                                                              >
                                                                {completion.dealer_city}
                                                              </Typography>
                                                            ) : null}
                                                          </TableCell>
                                                          <TableCell>
                                                            <Typography variant="body2">
                                                              {completion.score}/{completion.total_questions}
                                                            </Typography>
                                                          </TableCell>
                                                          <TableCell>
                                                            <Chip
                                                              label={completion.passed ? 'Passed' : 'Failed'}
                                                              size="small"
                                                              color={completion.passed ? 'success' : 'error'}
                                                              variant="filled"
                                                            />
                                                          </TableCell>
                                                        </TableRow>
                                                      ))}
                                                    </TableBody>
                                                  </Table>
                                                </TableContainer>

                                                {/* Pagination */}
                                                <TablePagination
                                                  component="div"
                                                  count={quizStatistics[quiz.id].total_users || 0}
                                                  page={quizStatsPage[quiz.id] || 0}
                                                  onPageChange={(event, newPage) => {
                                                    setQuizStatsPage(prev => ({ ...prev, [quiz.id]: newPage }));
                                                    loadQuizStatistics(quiz.id, newPage + 1, quizStatsRowsPerPage[quiz.id] || 5);
                                                  }}
                                                  rowsPerPage={quizStatsRowsPerPage[quiz.id] || 5}
                                                  onRowsPerPageChange={(event) => {
                                                    const newRowsPerPage = parseInt(event.target.value, 10);
                                                    setQuizStatsRowsPerPage(prev => ({ ...prev, [quiz.id]: newRowsPerPage }));
                                                    setQuizStatsPage(prev => ({ ...prev, [quiz.id]: 0 }));
                                                    loadQuizStatistics(quiz.id, 1, newRowsPerPage);
                                                  }}
                                                  rowsPerPageOptions={[5, 10, 25]}
                                                  sx={{ borderTop: 1, borderColor: 'divider' }}
                                                />
                                              </Box>
                                            ) : (
                                              <Alert severity="info" sx={{ mt: 1 }}>
                                                No one has completed this quiz yet.
                                              </Alert>
                                            )}
                                          </>
                                        ) : (
                                          <Typography variant="body2" color="text.secondary">
                                            Unable to load statistics
                                          </Typography>
                                        )}
                                      </CardContent>
                                    </Box>
                                  </Box>
                                </Card>
                              </Grid>
                            ))}
                          </Grid>
                        ) : (
                          <Alert severity="info">
                            No quizzes have been created for this campaign yet. Click "Add Quiz" to create one.
                          </Alert>
                        )}
                      </Grid>

                      {/* Connected Rewards - for Tournament type, shown above Voucher Requests */}
                      {selectedCampaignDetail.type === 'EXTRA' && (
                        <Grid item xs={12}>
                          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                            Connected Rewards
                          </Typography>

                          {selectedCampaignDetail.rewards && selectedCampaignDetail.rewards.length > 0 ? (
                            <Grid container spacing={1.5}>
                              {selectedCampaignDetail.rewards
                                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                                .map((reward) => (
                                  <Grid item xs={6} sm={4} md={3} lg={2} key={reward.id}>
                                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                      <Box sx={{ p: 1.5, textAlign: 'center' }}>
                                        {reward.reward_image ? (
                                          <img
                                            src={`${CDN_URL}/${reward.reward_image}`}
                                            alt={reward.name}
                                            style={{
                                              width: '100px',
                                              height: '70px',
                                              objectFit: 'contain',
                                              borderRadius: '6px',
                                              border: '1px solid #e0e0e0'
                                            }}
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                              e.target.nextSibling.style.display = 'flex';
                                            }}
                                          />
                                        ) : null}
                                        <Box
                                          sx={{
                                            width: '100px',
                                            height: '70px',
                                            display: reward.reward_image ? 'none' : 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: '#f5f5f5',
                                            borderRadius: '6px',
                                            border: '1px solid #e0e0e0',
                                            margin: '0 auto'
                                          }}
                                        >
                                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px' }}>
                                            No Image
                                          </Typography>
                                        </Box>
                                      </Box>
                                      <CardContent sx={{ flexGrow: 1, pt: 0, pb: 1.5, px: 1.5 }}>
                                        <Typography variant="subtitle2" component="div" gutterBottom sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                                          {reward.name}
                                        </Typography>
                                        {reward.description && (
                                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontSize: '0.75rem' }}>
                                            {reward.description}
                                          </Typography>
                                        )}
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                          {(() => {
                                            const denoms = reward.denominations && reward.denominations.length > 0
                                              ? reward.denominations
                                              : (reward.denomination ? [{ value: reward.denomination, currency: reward.currency || 'USD', is_default: true }] : []);
                                            const defaultDenom = denoms.find(d => d.is_default) || denoms[0];

                                            if (denoms.length > 1) {
                                              return (
                                                <Select
                                                  size="small"
                                                  defaultValue={defaultDenom ? `${defaultDenom.value}-${defaultDenom.currency}` : ''}
                                                  sx={{
                                                    fontSize: '0.7rem',
                                                    height: '28px',
                                                    minWidth: '100px',
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                      borderColor: 'primary.main'
                                                    },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                      borderColor: 'primary.main'
                                                    },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                      borderColor: 'primary.main'
                                                    }
                                                  }}
                                                >
                                                  {denoms.map((denom, idx) => (
                                                    <MenuItem key={idx} value={`${denom.value}-${denom.currency}`}>
                                                      {`${denom.value} ${denom.currency}`}
                                                    </MenuItem>
                                                  ))}
                                                </Select>
                                              );
                                            } else if (defaultDenom) {
                                              return (
                                                <Chip
                                                  label={`${defaultDenom.value} ${defaultDenom.currency}`}
                                                  size="small"
                                                  color="primary"
                                                  variant="outlined"
                                                  sx={{ fontSize: '0.7rem', height: '24px' }}
                                                />
                                              );
                                            }
                                            return null;
                                          })()}
                                          {reward.reward_provider && (
                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                              {reward.reward_provider.display_name || reward.reward_provider.name}
                                            </Typography>
                                          )}
                                        </Box>
                                      </CardContent>
                                    </Card>
                                  </Grid>
                                ))}
                            </Grid>
                          ) : (
                            <Alert severity="info">
                              No rewards connected to this campaign.
                            </Alert>
                          )}
                        </Grid>
                      )}

                      {/* Voucher Requests Section */}
                      <Grid item xs={12}>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                          Voucher Requests
                        </Typography>

                        {voucherRequestsError && (
                          <Alert severity="error" sx={{ mb: 3 }}>
                            {voucherRequestsError}
                          </Alert>
                        )}

                        {loadingVoucherRequests ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                          </Box>
                        ) : voucherRequests && voucherRequests.length > 0 ? (
                          <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
                            <Table>
                              <TableHead>
                                <TableRow>
                                  <TableCell>Request ID</TableCell>
                                  <TableCell>User</TableCell>
                                  <TableCell>Dealer</TableCell>
                                  <TableCell>Reward</TableCell>
                                  <TableCell>Total Cost</TableCell>
                                  <TableCell>Status</TableCell>
                                  <TableCell>Order ID</TableCell>
                                  <TableCell>Created</TableCell>
                                  <TableCell>Vouchers</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {voucherRequests.map((request) => (
                                  <TableRow key={request.id}>
                                    <TableCell>{request.id}</TableCell>
                                    <TableCell>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {request.user_name || 'Unknown'}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {request.user_email || ''}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      {request.dealer_name || 'N/A'}
                                    </TableCell>
                                    <TableCell>
                                      {request.vouchers && request.vouchers.length > 0 ? (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                          {request.vouchers.map((voucher, idx) => (
                                            <Typography key={idx} variant="body2">
                                              {voucher.reward_name || '—'} × {voucher.quantity}
                                            </Typography>
                                          ))}
                                        </Box>
                                      ) : (
                                        <Typography variant="body2" color="text.secondary">
                                          —
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={request.total_cost}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={request.status}
                                        size="small"
                                        color={
                                          request.status === 'RECEIVED' ? 'success' :
                                            request.status === 'ORDERED' ? 'info' :
                                              request.status === 'ERROR' ? 'error' :
                                                'default'
                                        }
                                        variant="filled"
                                      />
                                    </TableCell>
                                    <TableCell>
                                      {request.order_id && request.order_id !== '-1' ? (
                                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                          {request.order_id}
                                        </Typography>
                                      ) : (
                                        <Typography variant="caption" color="text.secondary">
                                          N/A
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="caption">
                                        {formatDateForDisplay(request.created, true)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      {request.vouchers && request.vouchers.length > 0 ? (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                          {request.vouchers.map((voucher, idx) => (
                                            <Typography key={idx} variant="caption">
                                              {voucher.reward_name || '—'} × {voucher.quantity} ({voucher.denomination})
                                            </Typography>
                                          ))}
                                        </Box>
                                      ) : (
                                        <Typography variant="caption" color="text.secondary">
                                          No vouchers
                                        </Typography>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : (
                          <Alert severity="info">
                            No voucher requests for this campaign.
                          </Alert>
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                ) : null}
              </Paper>
            </Container>
          );
        }

        return (
          <Container maxWidth="lg" sx={{ py: 4, pl: 0.5, pr: 4 }}>
            <Paper sx={{ p: 3, mb: 3, backgroundColor: '#ffffff' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                  Campaigns Management
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddCampaignOpen}
                  sx={{
                    backgroundColor: companyBrandingColor || '#1976d2',
                    '&:hover': {
                      backgroundColor: companyBrandingColor || '#1565c0',
                    },
                  }}
                >
                  Add Campaign
                </Button>
              </Box>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Manage campaigns for your company.
              </Typography>

              {/* Search Filter */}
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Search Campaigns"
                  placeholder="Search by name, tag, or description..."
                  variant="outlined"
                  size="small"
                  value={campaignsSearch}
                  onChange={handleCampaignsSearchChange}
                  sx={{ minWidth: 300 }}
                />
                {campaignsSearch && (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setCampaignsSearch('');
                      setCampaignsPage(0);
                    }}
                    size="small"
                  >
                    Clear Search
                  </Button>
                )}
              </Box>

              {campaignsError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {campaignsError}
                </Alert>
              )}

              {loadingCampaigns ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : campaigns.length === 0 ? (
                <Alert severity="info">
                  {campaignsSearch ? 'No campaigns found matching your search criteria.' : 'No campaigns found for your company.'}
                </Alert>
              ) : (
                <>
                  <TableContainer component={Paper} sx={{ mb: 3, backgroundColor: '#ffffff' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Image</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Tag</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Rewards</TableCell>
                          <TableCell>Dealers</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {campaigns.map((campaign) => (
                          <TableRow key={campaign.id}>
                            <TableCell>
                              <Box sx={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                                {campaign.campaign_image ? (
                                  <img
                                    src={`${CDN_URL}/${campaign.campaign_image}`}
                                    alt={campaign.name}
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'contain',
                                      borderRadius: '4px',
                                      border: '1px solid #e0e0e0'
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <Box
                                  sx={{
                                    width: '100%',
                                    height: '100%',
                                    display: campaign.campaign_image ? 'none' : 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: '4px',
                                    border: '1px solid #e0e0e0'
                                  }}
                                >
                                  <Typography variant="caption" color="text.secondary">
                                    No Image
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {campaign.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {campaign.campaign_tag ? (
                                <Chip label={campaign.campaign_tag} size="small" variant="outlined" />
                              ) : 'N/A'}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={campaign.type}
                                size="small"
                                color={campaign.type === 'POINTS' ? 'primary' : campaign.type === 'GOALS' ? 'secondary' : 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={campaign.status}
                                size="small"
                                color={campaign.status === 'RUNNING' ? 'success' : campaign.status === 'DRAFT' ? 'warning' : 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              {campaign.rewards && campaign.rewards.length > 0 ? (
                                <Typography variant="body2">
                                  {campaign.rewards.length} reward{campaign.rewards.length > 1 ? 's' : ''}
                                </Typography>
                              ) : 'N/A'}
                            </TableCell>
                            <TableCell>
                              {campaign.dealers && campaign.dealers.length > 0 ? (
                                <Typography variant="body2">
                                  {campaign.dealers.length} dealer{campaign.dealers.length > 1 ? 's' : ''}
                                </Typography>
                              ) : 'N/A'}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleCampaignChartOpen(campaign)}
                                  sx={{
                                    color: companyBrandingColor || '#1976d2'
                                  }}
                                  title="Sync Chart"
                                >
                                  <ShowChartIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleCampaignDetailOpen(campaign)}
                                  sx={{
                                    color: companyBrandingColor || '#1976d2'
                                  }}
                                  title="View Details"
                                >
                                  <VisibilityIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopyCampaignOpen(campaign)}
                                  sx={{
                                    color: companyBrandingColor || '#1976d2'
                                  }}
                                  title="Copy Campaign"
                                >
                                  <ContentCopyIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditCampaignOpen(campaign)}
                                  sx={{
                                    color: companyBrandingColor || '#1976d2'
                                  }}
                                  title="Edit Campaign"
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteCampaignOpen(campaign)}
                                  sx={{
                                    color: '#d32f2f'
                                  }}
                                  title="Delete Campaign"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={campaignsTotal}
                    rowsPerPage={campaignsRowsPerPage}
                    page={campaignsPage}
                    onPageChange={handleCampaignsPageChange}
                    onRowsPerPageChange={handleCampaignsRowsPerPageChange}
                  />
                </>
              )}
            </Paper>
          </Container>
        );

      case 'leaderboards':
        return (
          <LeaderboardsSection
            campaigns={campaignsWithLeaderboards}
            leaderboardGoals={leaderboardGoals}
            loadingLeaderboardCampaigns={loadingLeaderboardCampaigns}
            loadingLeaderboardGoals={loadingLeaderboardGoals}
            leaderboardSort={leaderboardSort}
            handleLeaderboardSortChange={handleLeaderboardSortChange}
            getSortedLeaderboardGoals={getSortedLeaderboardGoals}
            companyExtraModules={companyExtraModules}
            labels={{
              title: 'Leaderboards',
              description: 'View dealer performance rankings for your active campaigns.',
              noLeaderboards: 'No campaigns with leaderboards enabled. Contact your administrator to enable this feature.',
              dealerName: 'Dealer Name',
              teamName: 'Team name',
              rank: 'Rank',
              points: 'Points',
              status: 'Status',
              unknownDealer: 'Unknown Dealer',
              noPerformanceData: 'No dealer performance data available for this campaign.',
            }}
          />
        );

      case 'bonusTask':
        if (selectedBonusTaskDetail) {
          return (
            <Container maxWidth="lg" sx={{ py: 4, pl: 0.5, pr: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={() => setSelectedBonusTaskDetail(null)}
                >
                  Back
                </Button>
                <Typography variant="h5" sx={{ flexGrow: 1 }}>
                  {selectedBonusTaskDetail.title}
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => handleEditBonusTask(selectedBonusTaskDetail)}
                >
                  Edit
                </Button>
              </Box>

              {bonusTasksError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {bonusTasksError}
                </Alert>
              )}

              <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <Chip
                    label={selectedBonusTaskDetail.active ? 'Active' : 'Inactive'}
                    color={selectedBonusTaskDetail.active ? 'success' : 'default'}
                    size="small"
                  />
                  <Chip
                    label={`${Number(selectedBonusTaskDetail.points) || 0} points`}
                    size="small"
                  />
                  <Chip
                    label={
                      campaigns.find((c) => c.id === selectedBonusTaskDetail.campaign_id)?.name ||
                      'Campaign'
                    }
                    size="small"
                    variant="outlined"
                  />
                </Box>
                {selectedBonusTaskDetail.description && (
                  <Box
                    sx={quizRichContentSx}
                    dangerouslySetInnerHTML={{ __html: selectedBonusTaskDetail.description }}
                  />
                )}
              </Paper>

              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Completed submissions
                </Typography>
                {loadingBonusTaskDetail ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : !(selectedBonusTaskDetail.submissions || []).length ? (
                  <Alert severity="info">No submissions yet.</Alert>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Email</TableCell>
                          <TableCell>Dealer</TableCell>
                          <TableCell>Answer</TableCell>
                          <TableCell>Timestamp</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(selectedBonusTaskDetail.submissions || []).map((submission) => (
                          <TableRow key={submission.id}>
                            <TableCell>{submission.user_email || '—'}</TableCell>
                            <TableCell>{submission.dealer_name || '—'}</TableCell>
                            <TableCell sx={{ maxWidth: 360 }}>
                              <Box
                                sx={{
                                  ...quizRichContentSx,
                                  '& img': { maxWidth: '100%', height: 'auto' },
                                }}
                                dangerouslySetInnerHTML={{ __html: submission.answer || '' }}
                              />
                            </TableCell>
                            <TableCell>{formatDateForDisplay(submission.created)}</TableCell>
                            <TableCell>
                              <Chip
                                label={submission.status}
                                size="small"
                                color={
                                  submission.status === 'ACCEPTED'
                                    ? 'success'
                                    : submission.status === 'REJECTED'
                                      ? 'error'
                                      : 'warning'
                                }
                              />
                            </TableCell>
                            <TableCell>
                              {submission.status === 'PENDING' ? (
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <IconButton
                                    size="small"
                                    color="success"
                                    disabled={reviewingSubmissionId === submission.id}
                                    onClick={() =>
                                      handleReviewBonusTaskSubmission(submission.id, 'accept')
                                    }
                                    title="Accept"
                                  >
                                    <AcceptIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    disabled={reviewingSubmissionId === submission.id}
                                    onClick={() =>
                                      handleReviewBonusTaskSubmission(submission.id, 'reject')
                                    }
                                    title="Reject"
                                  >
                                    <RejectIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  —
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Container>
          );
        }

        return (
          <Container maxWidth="lg" sx={{ py: 4, pl: 0.5, pr: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5">Bonus Task Management</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddBonusTask}
              >
                Add Bonus Task
              </Button>
            </Box>

            {bonusTasksError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {bonusTasksError}
              </Alert>
            )}

            <Paper sx={{ p: 3 }}>
              {loadingBonusTasks ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : bonusTasks.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No bonus tasks found. Create your first bonus task to get started.
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Title</TableCell>
                        <TableCell>Campaign</TableCell>
                        <TableCell>Points</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bonusTasks.map((task) => (
                        <TableRow key={task.id}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {task.title}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {campaigns.find((c) => c.id === task.campaign_id)?.name || '—'}
                          </TableCell>
                          <TableCell>{Number(task.points) || 0}</TableCell>
                          <TableCell>
                            <Chip
                              label={task.active ? 'Active' : 'Inactive'}
                              color={task.active ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{formatDateForDisplay(task.created)}</TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => loadBonusTaskDetail(task.id)}
                              sx={{ mr: 1 }}
                              title="View submissions"
                            >
                              <VisibilityIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleEditBonusTask(task)}
                              sx={{ mr: 1 }}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteBonusTask(task.id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Container>
        );

      case 'quiz':
        return (
          <Container maxWidth="lg" sx={{ py: 4, pl: 0.5, pr: 4 }}>
            {/* Quiz Management Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5">Quiz Management</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddQuiz}
              >
                Add Quiz
              </Button>
            </Box>

            {/* Error and Success Messages */}
            {quizzesError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {quizzesError}
              </Alert>
            )}

            {/* Quiz List */}
            <Paper sx={{ p: 3 }}>
              {loadingQuizzes ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : quizzes.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No quizzes found. Create your first quiz to get started.
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Title</TableCell>
                        <TableCell>Campaign</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Questions</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {quizzes.map((quiz) => (
                        <TableRow key={quiz.id}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {quiz.title}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {campaigns.find(c => c.id === quiz.campaign_id)?.name || 'No Campaign'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={quiz.active ? 'Active' : 'Inactive'}
                              color={quiz.active ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {quiz.questions?.length || 0} questions
                          </TableCell>
                          <TableCell>
                            {formatDateForDisplay(quiz.created)}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleEditQuiz(quiz)}
                              sx={{ mr: 1 }}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteQuiz(quiz.id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Container>
        );

      case 'marketing':
        return (
          <Container maxWidth="lg" sx={{ py: 4, pl: 0.5, pr: 4 }}>
            <Paper sx={{ p: 3, backgroundColor: '#ffffff' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                  Marketing Tools
                </Typography>
                {!isManager && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddMarketingItem}
                    sx={{
                      backgroundColor: companyBrandingColor || '#1976d2',
                      '&:hover': {
                        backgroundColor: companyBrandingColor || '#1565c0',
                      },
                    }}
                  >
                    Add Marketing Item
                  </Button>
                )}
              </Box>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Manage marketing materials about your products, YouTube videos, and downloadable content.
              </Typography>

              {marketingItemsError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {marketingItemsError}
                </Alert>
              )}

              {loadingMarketingItems ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : marketingItems.length === 0 ? (
                <Alert severity="info">
                  No marketing items found. Create your first marketing item to get started.
                </Alert>
              ) : (
                <Grid container spacing={3}>
                  {marketingItems.map((item) => (
                    <Grid item xs={12} sm={6} md={4} key={item.id}>
                      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        {item.image && (
                          <Box
                            sx={{
                              height: 200,
                              backgroundImage: `url(${CDN_URL}/${item.image})`,
                              backgroundSize: 'contain',
                              backgroundPosition: 'center',
                              backgroundRepeat: 'no-repeat',
                              backgroundColor: '#f5f5f5',
                            }}
                          />
                        )}
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" component="h3" gutterBottom>
                              {item.title}
                            </Typography>
                            <Chip
                              label={getMarketingTypeLabel(item.type)}
                              size="small"
                              color="primary"
                            />
                          </Box>

                          {item.description && (
                            <Box
                              sx={{
                                mb: 2,
                                '& p': { margin: 0 },
                                '& ul, & ol': { margin: '8px 0', paddingLeft: '20px' },
                                '& h1, & h2, & h3, & h4, & h5, & h6': { margin: '8px 0 4px 0' }
                              }}
                              dangerouslySetInnerHTML={{ __html: item.description }}
                            />
                          )}

                          {item.type === 'YOUTUBE' && item.youtube_link && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                YouTube: <a href={item.youtube_link} target="_blank" rel="noopener noreferrer">{item.youtube_link}</a>
                              </Typography>
                            </Box>
                          )}

                          {item.type === 'DOWNLOAD' && item.download_file && (
                            <Box sx={{ mb: 2 }}>
                              <Button
                                variant="outlined"
                                size="small"
                                href={`${CDN_URL}/${item.download_file}`}
                                target="_blank"
                                sx={{ textTransform: 'none' }}
                              >
                                Download File
                              </Button>
                            </Box>
                          )}

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                            <Typography variant="caption" color="text.secondary">
                              {formatDateForDisplay(item.created)}
                            </Typography>
                            {!isManager && (
                              <Box>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditMarketingItem(item)}
                                  sx={{ mr: 1 }}
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteMarketingItem(item.id)}
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}

              {/* Marketing Item Dialog */}
              <Dialog
                open={marketingItemDialog}
                onClose={() => setMarketingItemDialog(false)}
                maxWidth="md"
                fullWidth
              >
                <DialogTitle>
                  {editingMarketingItem ? 'Edit Marketing Item' : 'Create New Marketing Item'}
                </DialogTitle>
                <DialogContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    {/* Title */}
                    <TextField
                      fullWidth
                      label="Title"
                      required
                      value={marketingItemFormData.title}
                      onChange={(e) => setMarketingItemFormData(prev => ({ ...prev, title: e.target.value }))}
                      error={!!marketingItemFormErrors.title}
                      helperText={marketingItemFormErrors.title}
                    />

                    {/* Type */}
                    <FormControl fullWidth required>
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={marketingItemFormData.type}
                        onChange={(e) => setMarketingItemFormData(prev => ({ ...prev, type: e.target.value }))}
                        label="Type"
                      >
                        <MenuItem value="IMAGE">Image</MenuItem>
                        <MenuItem value="YOUTUBE">YouTube</MenuItem>
                        <MenuItem value="DOWNLOAD">Download</MenuItem>
                      </Select>
                    </FormControl>

                    {/* Image Upload */}
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Image (Optional)
                      </Typography>
                      <Button
                        variant="outlined"
                        component="label"
                        disabled={uploadingMarketingImage}
                        startIcon={uploadingMarketingImage ? <CircularProgress size={20} /> : null}
                        fullWidth
                      >
                        {uploadingMarketingImage ? 'Uploading...' : 'Upload Image'}
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={handleMarketingImageUpload}
                        />
                      </Button>
                      {marketingItemFormData.image && (
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                          <img
                            src={`${CDN_URL}/${marketingItemFormData.image}`}
                            alt="Marketing preview"
                            style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '4px' }}
                          />
                        </Box>
                      )}
                      {marketingItemFormErrors.image && (
                        <Typography variant="caption" color="error">
                          {marketingItemFormErrors.image}
                        </Typography>
                      )}
                    </Box>

                    {/* YouTube Link (only for YouTube type) */}
                    {marketingItemFormData.type === 'YOUTUBE' && (
                      <TextField
                        fullWidth
                        label="YouTube Link"
                        required
                        value={marketingItemFormData.youtube_link}
                        onChange={(e) => setMarketingItemFormData(prev => ({ ...prev, youtube_link: e.target.value }))}
                        error={!!marketingItemFormErrors.youtube_link}
                        helperText={marketingItemFormErrors.youtube_link}
                        placeholder="https://www.youtube.com/watch?v=..."
                      />
                    )}

                    {/* Download File (only for Download type) */}
                    {marketingItemFormData.type === 'DOWNLOAD' && (
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                          Download File *
                        </Typography>
                        <Button
                          variant="outlined"
                          component="label"
                          disabled={uploadingMarketingFile}
                          startIcon={uploadingMarketingFile ? <CircularProgress size={20} /> : null}
                          fullWidth
                        >
                          {uploadingMarketingFile ? 'Uploading...' : 'Upload File'}
                          <input
                            type="file"
                            hidden
                            onChange={handleMarketingFileUpload}
                          />
                        </Button>
                        {marketingItemFormData.download_file && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            File uploaded: {marketingItemFormData.download_file.split('/').pop()}
                          </Typography>
                        )}
                        {marketingItemFormErrors.download_file && (
                          <Typography variant="caption" color="error">
                            {marketingItemFormErrors.download_file}
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Description with Rich Text Editor */}
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Description (Optional)
                    </Typography>
                    <RichTextEditor
                      value={marketingItemFormData.description}
                      onChange={(value) => setMarketingItemFormData(prev => ({ ...prev, description: value }))}
                      placeholder="Enter description for this marketing item..."
                    />

                    {/* Error Messages */}
                    {marketingItemFormErrors.submit && (
                      <Alert severity="error">
                        {marketingItemFormErrors.submit}
                      </Alert>
                    )}
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setMarketingItemDialog(false)} disabled={submittingMarketingItem}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleMarketingItemSubmit}
                    variant="contained"
                    disabled={submittingMarketingItem}
                  >
                    {submittingMarketingItem ? <CircularProgress size={20} /> : (editingMarketingItem ? 'Update Item' : 'Create Item')}
                  </Button>
                </DialogActions>
              </Dialog>
            </Paper>
          </Container>
        );

      case 'notifications':
        if (viewingTemplates && selectedNotificationSet) {
          return (
            <Container maxWidth="lg" sx={{ py: 4, pl: 0.5, pr: 4 }}>
              <Paper sx={{ p: 3, backgroundColor: '#ffffff' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBackToNotificationSets}
                    sx={{ mr: 2 }}
                  >
                    Back to Notification Sets
                  </Button>
                  <Typography variant="h5" component="div" sx={{ flexGrow: 1 }}>
                    Email Templates - {selectedNotificationSet.name}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddEmailTemplate}
                    sx={{
                      backgroundColor: companyBrandingColor || '#1976d2',
                      '&:hover': {
                        backgroundColor: companyBrandingColor || '#1565c0',
                      },
                    }}
                  >
                    Add Template
                  </Button>
                </Box>

                {emailTemplatesError && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {emailTemplatesError}
                  </Alert>
                )}

                {loadingEmailTemplates ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : emailTemplates.length === 0 ? (
                  <Alert severity="info">
                    No email templates found for this notification set.
                  </Alert>
                ) : (
                  <Grid container spacing={2}>
                    {emailTemplates
                      .sort((a, b) => {
                        // Define the desired order for template types
                        const typeOrder = {
                          'CAMPAIGN_START': 1,
                          'CAMPAIGN_START_ACTIVATION_LINK': 2,
                          'REMINDER_NO_POINTS': 3,
                          'REMINDER_WITH_POINTS': 4,
                          'REMINDER_NEVER_LOGGED_IN': 5,
                          'REMINDER_LOGGED_IN': 6,
                          'REMINDER_LOGGED_IN_2': 7,
                          'REMINDER_TARGET_ACHIEVED': 8,
                          'REMINDER_TARGET_NOT_ACHIEVED': 9,
                          'REMINDER_ALL': 10,
                          'VOUCHER': 11,
                          'TEMP_PASSWORD': 12,
                          'RESET_PASSWORD': 13
                        };
                        return (typeOrder[a.template_type] || 999) - (typeOrder[b.template_type] || 999);
                      })
                      .map((template) => (
                        <Grid item xs={12} sm={6} md={4} key={template.id}>
                          <Card>
                            <CardContent>
                              <Typography variant="h6" gutterBottom>
                                {getTemplateTypeLabel(template.template_type)}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                {template.title}
                              </Typography>
                              {template.subtitle && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                  {template.subtitle}
                                </Typography>
                              )}
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Chip
                                  label={getTemplateTypeLabel(template.template_type)}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                                <Box>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditEmailTemplate(template)}
                                    sx={{ mr: 1 }}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteEmailTemplate(template.id)}
                                    color="error"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                  </Grid>
                )}

                {/* Email Template Dialog */}
                <Dialog
                  open={emailTemplateDialog}
                  onClose={() => setEmailTemplateDialog(false)}
                  maxWidth="md"
                  fullWidth
                >
                  <DialogTitle>
                    {editingEmailTemplate ? 'Edit Email Template' : 'Create New Email Template'}
                  </DialogTitle>
                  <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                      {/* Template Type */}
                      <FormControl fullWidth required>
                        <InputLabel>Template Type</InputLabel>
                        <Select
                          value={emailTemplateFormData.template_type}
                          onChange={(e) => setEmailTemplateFormData(prev => ({ ...prev, template_type: e.target.value }))}
                          label="Template Type"
                        >
                          <MenuItem
                            value="CAMPAIGN_START"
                            disabled={emailTemplates.some(t => t.template_type === 'CAMPAIGN_START' && (!editingEmailTemplate || t.id !== editingEmailTemplate.id))}
                          >
                            Campaign Start {emailTemplates.some(t => t.template_type === 'CAMPAIGN_START' && (!editingEmailTemplate || t.id !== editingEmailTemplate.id)) ? '(Already exists)' : ''}
                          </MenuItem>
                          {selectedNotificationSet.campaign_id && (
                            <MenuItem
                              value="CAMPAIGN_START_ACTIVATION_LINK"
                              disabled={emailTemplates.some(t => t.template_type === 'CAMPAIGN_START_ACTIVATION_LINK' && (!editingEmailTemplate || t.id !== editingEmailTemplate.id))}
                            >
                              Campaign Start with Activation Link {emailTemplates.some(t => t.template_type === 'CAMPAIGN_START_ACTIVATION_LINK' && (!editingEmailTemplate || t.id !== editingEmailTemplate.id)) ? '(Already exists)' : ''}
                            </MenuItem>
                          )}
                          <MenuItem
                            value="REMINDER_NO_POINTS"
                            disabled={emailTemplates.some(t => t.template_type === 'REMINDER_NO_POINTS' && (!editingEmailTemplate || t.id !== editingEmailTemplate.id))}
                          >
                            Reminder (No Points) {emailTemplates.some(t => t.template_type === 'REMINDER_NO_POINTS' && (!editingEmailTemplate || t.id !== editingEmailTemplate.id)) ? '(Already exists)' : ''}
                          </MenuItem>
                          <MenuItem
                            value="REMINDER_WITH_POINTS"
                            disabled={emailTemplates.some(t => t.template_type === 'REMINDER_WITH_POINTS' && (!editingEmailTemplate || t.id !== editingEmailTemplate.id))}
                          >
                            Reminder (With Points) {emailTemplates.some(t => t.template_type === 'REMINDER_WITH_POINTS' && (!editingEmailTemplate || t.id !== editingEmailTemplate.id)) ? '(Already exists)' : ''}
                          </MenuItem>
                          <MenuItem
                            value="REMINDER_NEVER_LOGGED_IN"
                            disabled={emailTemplates.some(t => t.template_type === 'REMINDER_NEVER_LOGGED_IN' && (!editingEmailTemplate || t.id !== editingEmailTemplate.id))}
                          >
                            Reminder (Never logged-in) {emailTemplates.some(t => t.template_type === 'REMINDER_NEVER_LOGGED_IN' && (!editingEmailTemplate || t.id !== editingEmailTemplate.id)) ? '(Already exists)' : ''}
                          </MenuItem>
                          <MenuItem
                            value="REMINDER_LOGGED_IN"
                            disabled={emailTemplates.some(t => t.template_type === 'REMINDER_LOGGED_IN' && (!editingEmailTemplate || t.id !== editingEmailTemplate.id))}
                          >
                            Reminder (Logged-in) {emailTemplates.some(t => t.template_type === 'REMINDER_LOGGED_IN' && (!editingEmailTemplate || t.id !== editingEmailTemplate.id)) ? '(Already exists)' : ''}
                          </MenuItem>
                          <MenuItem
                            value="REMINDER_LOGGED_IN_2"
                            disabled={emailTemplates.some(t => t.template_type === 'REMINDER_LOGGED_IN_2' && (!editingEmailTemplate || t.id !== editingEmailTemplate.id))}
                          >
                            Reminder (Logged-in 2) {emailTemplates.some(t => t.template_type === 'REMINDER_LOGGED_IN_2' && (!editingEmailTemplate || t.id !== editingEmailTemplate.id)) ? '(Already exists)' : ''}
                          </MenuItem>
                          <MenuItem
                            value="REMINDER_TARGET_ACHIEVED"
                            disabled={emailTemplates.some(t => t.template_type === 'REMINDER_TARGET_ACHIEVED' && (!editingEmailTemplate || t.id !== editingEmailTemplate.id))}
                          >
                            Reminder (Target Achieved) {emailTemplates.some(t => t.template_type === 'REMINDER_TARGET_ACHIEVED' && (!editingEmailTemplate || t.id !== editingEmailTemplate.id)) ? '(Already exists)' : ''}
                          </MenuItem>
                          <MenuItem
                            value="REMINDER_TARGET_NOT_ACHIEVED"
                            disabled={emailTemplates.some(t => t.template_type === 'REMINDER_TARGET_NOT_ACHIEVED' && (!editingEmailTemplate || t.id !== editingEmailTemplate.id))}
                          >
                            Reminder (Target NOT Achieved) {emailTemplates.some(t => t.template_type === 'REMINDER_TARGET_NOT_ACHIEVED' && (!editingEmailTemplate || t.id !== editingEmailTemplate.id)) ? '(Already exists)' : ''}
                          </MenuItem>
                          <MenuItem
                            value="REMINDER_ALL"
                            disabled={emailTemplates.some(t => t.template_type === 'REMINDER_ALL' && (!editingEmailTemplate || t.id !== editingEmailTemplate.id))}
                          >
                            Reminder (ALL) {emailTemplates.some(t => t.template_type === 'REMINDER_ALL' && (!editingEmailTemplate || t.id !== editingEmailTemplate.id)) ? '(Already exists)' : ''}
                          </MenuItem>
                          <MenuItem
                            value="VOUCHER"
                            disabled={emailTemplates.some(t => t.template_type === 'VOUCHER' && (!editingEmailTemplate || t.id !== editingEmailTemplate.id))}
                          >
                            Voucher {emailTemplates.some(t => t.template_type === 'VOUCHER' && (!editingEmailTemplate || t.id !== editingEmailTemplate.id)) ? '(Already exists)' : ''}
                          </MenuItem>
                          {!selectedNotificationSet.campaign_id && (
                            <>
                              <MenuItem
                                value="TEMP_PASSWORD"
                                disabled={emailTemplates.some(t => t.template_type === 'TEMP_PASSWORD' && (!editingEmailTemplate || t.id !== editingEmailTemplate.id))}
                              >
                                Temporary Password {emailTemplates.some(t => t.template_type === 'TEMP_PASSWORD' && (!editingEmailTemplate || t.id !== editingEmailTemplate.id)) ? '(Already exists)' : ''}
                              </MenuItem>
                              <MenuItem
                                value="RESET_PASSWORD"
                                disabled={emailTemplates.some(t => t.template_type === 'RESET_PASSWORD' && (!editingEmailTemplate || t.id !== editingEmailTemplate.id))}
                              >
                                Reset Password {emailTemplates.some(t => t.template_type === 'RESET_PASSWORD' && (!editingEmailTemplate || t.id !== editingEmailTemplate.id)) ? '(Already exists)' : ''}
                              </MenuItem>
                            </>
                          )}
                        </Select>
                      </FormControl>

                      {/* Title */}
                      <TextField
                        fullWidth
                        label="Title"
                        required
                        value={emailTemplateFormData.title}
                        onChange={(e) => setEmailTemplateFormData(prev => ({ ...prev, title: e.target.value }))}
                        error={!!emailTemplateFormErrors.title}
                        helperText={emailTemplateFormErrors.title}
                      />

                      {/* Subtitle */}
                      <TextField
                        fullWidth
                        label="Subtitle"
                        value={emailTemplateFormData.subtitle}
                        onChange={(e) => setEmailTemplateFormData(prev => ({ ...prev, subtitle: e.target.value }))}
                        helperText="Optional subtitle for the email"
                      />

                      {/* Content with Rich Text Editor */}
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Content *
                      </Typography>
                      <RichTextEditor
                        value={emailTemplateFormData.content}
                        onChange={(value) => setEmailTemplateFormData(prev => ({ ...prev, content: value }))}
                        placeholder="Enter your email template content here..."
                        error={!!emailTemplateFormErrors.content}
                      />
                      {emailTemplateFormErrors.content && (
                        <Typography variant="caption" color="error">
                          {emailTemplateFormErrors.content}
                        </Typography>
                      )}
                      {emailTemplateFormData.template_type === 'CAMPAIGN_START_ACTIVATION_LINK' && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          For dealers who have never logged in with an activation URL set, use{' '}
                          <code>[your link text]&#123;Url&#125;</code> in the message; each recipient gets their URL from Users → activation link (or CSV import).
                        </Typography>
                      )}

                      {/* Extra Content Below CTA */}
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Extra Content Below CTA (optional)
                      </Typography>
                      <RichTextEditor
                        value={emailTemplateFormData.extra_content}
                        onChange={(value) => setEmailTemplateFormData(prev => ({ ...prev, extra_content: value }))}
                        placeholder="Enter additional content to display below the CTA button..."
                      />

                      {/* Custom CTA Configuration */}
                      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, mt: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          Custom CTA Button
                        </Typography>
                        {emailTemplateFormData.template_type !== 'VOUCHER' && emailTemplateFormData.template_type !== 'RESET_PASSWORD' && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={emailTemplateFormData.no_cta}
                                onChange={(e) => setEmailTemplateFormData(prev => ({
                                  ...prev,
                                  no_cta: e.target.checked,
                                  custom_cta_enabled: e.target.checked ? false : prev.custom_cta_enabled
                                }))}
                              />
                            }
                            label="No CTA"
                          />
                        )}
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={emailTemplateFormData.custom_cta_enabled}
                              disabled={emailTemplateFormData.no_cta}
                              onChange={(e) => setEmailTemplateFormData(prev => ({
                                ...prev,
                                custom_cta_enabled: e.target.checked,
                                no_cta: e.target.checked ? false : prev.no_cta
                              }))}
                            />
                          }
                          label="Enable Custom CTA Button"
                        />

                        {emailTemplateFormData.custom_cta_enabled && !emailTemplateFormData.no_cta && (
                          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                              fullWidth
                              label="Button Title"
                              value={emailTemplateFormData.custom_cta_title}
                              onChange={(e) => setEmailTemplateFormData(prev => ({ ...prev, custom_cta_title: e.target.value }))}
                              placeholder="e.g., View Portal, Reset Password"
                            />

                            {(emailTemplateFormData.template_type === 'VOUCHER' || emailTemplateFormData.template_type === 'RESET_PASSWORD') ? (
                              <Alert severity="info">
                                Button URL is automatically generated for {emailTemplateFormData.template_type === 'VOUCHER' ? 'Voucher' : 'Reset Password'} templates
                              </Alert>
                            ) : (
                              <TextField
                                fullWidth
                                label="Button URL"
                                value={emailTemplateFormData.custom_cta_url}
                                onChange={(e) => setEmailTemplateFormData(prev => ({ ...prev, custom_cta_url: e.target.value }))}
                                placeholder="https://example.com"
                              />
                            )}
                          </Box>
                        )}
                      </Box>

                      {/* Error Messages */}
                      {emailTemplateFormErrors.submit && (
                        <Alert severity="error">
                          {emailTemplateFormErrors.submit}
                        </Alert>
                      )}

                      {/* Success/Error Messages for Test Email */}
                      {communicationError && (
                        <Alert severity="error" onClose={() => setCommunicationError('')}>
                          {communicationError}
                        </Alert>
                      )}
                      {communicationSuccess && (
                        <Alert severity="success" onClose={() => setCommunicationSuccess('')}>
                          {communicationSuccess}
                        </Alert>
                      )}

                      {/* Test Email Section */}
                      <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e0e0e0' }}>
                        <Typography variant="h6" gutterBottom>
                          Test Email
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Send a test email to preview how your template will look.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                          <TextField
                            label="Test email address"
                            value={testEmail}
                            onChange={e => setTestEmail(e.target.value)}
                            size="small"
                            sx={{ minWidth: '250px' }}
                          />
                          <Button
                            variant="outlined"
                            onClick={async () => {
                              const emailOk = /.+@.+\..+/.test(testEmail);
                              if (!emailOk) { setCommunicationError('Enter a valid test email'); return; }
                              if (!emailTemplateFormData.title.trim()) { setCommunicationError('Title is required'); return; }
                              if (!emailTemplateFormData.content || emailTemplateFormData.content.trim() === '') { setCommunicationError('Content is required'); return; }

                              setCommunicationError('');
                              setCommunicationSuccess('');

                              try {
                                let content = emailTemplateFormData.content;
                                let extraContent = emailTemplateFormData.extra_content;
                                if (emailTemplateFormData.template_type === 'CAMPAIGN_START_ACTIVATION_LINK') {
                                  const previewUrl = 'https://example.com/activation-preview';
                                  content = applySpecialUrlPlaceholderForTest(content, previewUrl);
                                  extraContent = applySpecialUrlPlaceholderForTest(extraContent || '', previewUrl);
                                }
                                const requestData = {
                                  to_email: testEmail,
                                  subject: emailTemplateFormData.title,
                                  subtitle: emailTemplateFormData.subtitle,
                                  content,
                                  extra_content: extraContent,
                                  no_cta: emailTemplateFormData.no_cta
                                };

                                // Include custom CTA if enabled
                                if (emailTemplateFormData.custom_cta_enabled && !emailTemplateFormData.no_cta) {
                                  requestData.custom_cta_title = emailTemplateFormData.custom_cta_title;
                                  requestData.custom_cta_url = emailTemplateFormData.custom_cta_url;
                                }

                                // Use notification set's header/footer images when testing
                                if (selectedNotificationSet?.id) {
                                  requestData.notification_set_id = selectedNotificationSet.id;
                                }

                                const result = await apiCall('post', '/api/email/send-test', requestData);

                                if (result.success && result.data.success) {
                                  setCommunicationSuccess('Test email sent successfully!');
                                } else {
                                  setCommunicationError(result.data?.error || result.error || 'Failed to send test email');
                                }
                              } catch (error) {
                                setCommunicationError('Failed to send test email: ' + (error.message || 'Unknown error'));
                              }
                            }}
                          >
                            Send Test
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setEmailTemplateDialog(false)} disabled={submittingEmailTemplate}>
                      Cancel
                    </Button>
                    <Button
                      onClick={handleEmailTemplateSubmit}
                      variant="contained"
                      disabled={submittingEmailTemplate}
                    >
                      {submittingEmailTemplate ? <CircularProgress size={20} /> : (editingEmailTemplate ? 'Update Template' : 'Create Template')}
                    </Button>
                  </DialogActions>
                </Dialog>
              </Paper>
            </Container>
          );
        }

        return (
          <Container maxWidth="lg" sx={{ py: 4, pl: 0.5, pr: 4 }}>
            <Paper sx={{ p: 3, backgroundColor: '#ffffff' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                  Notification Sets
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddNotificationSet}
                  sx={{
                    backgroundColor: companyBrandingColor || '#1976d2',
                    '&:hover': {
                      backgroundColor: companyBrandingColor || '#1565c0',
                    },
                  }}
                >
                  Add Notification Set
                </Button>
              </Box>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Manage notification sets for campaigns and general notifications. Each set contains email templates for different notification types.
              </Typography>

              {notificationSetsError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {notificationSetsError}
                </Alert>
              )}

              {loadingNotificationSets ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : notificationSets.length === 0 ? (
                <Alert severity="info">
                  No notification sets found. Create your first notification set to get started.
                </Alert>
              ) : (
                <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Campaign</TableCell>
                        <TableCell>Templates</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {notificationSets.map((notificationSet) => (
                        <TableRow key={notificationSet.id}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {notificationSet.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={notificationSet.campaign_id ? 'Campaign' : 'General'}
                              color={notificationSet.campaign_id ? 'primary' : 'secondary'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {notificationSet.campaign ? (
                              <Typography variant="body2">
                                {notificationSet.campaign.name}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                N/A
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {notificationSet.email_templates?.length || 0} templates
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {formatDateForDisplay(notificationSet.created)}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton
                                size="small"
                                onClick={() => handleViewTemplates(notificationSet)}
                                sx={{ color: companyBrandingColor || '#1976d2' }}
                                title="View Templates"
                              >
                                <VisibilityIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleEditNotificationSet(notificationSet)}
                                sx={{ color: companyBrandingColor || '#1976d2' }}
                                title="Edit Notification Set"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteNotificationSet(notificationSet.id)}
                                color="error"
                                title="Delete Notification Set"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Notification Set Dialog */}
              <Dialog
                open={notificationSetDialog}
                onClose={() => setNotificationSetDialog(false)}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle>
                  {editingNotificationSet ? 'Edit Notification Set' : 'Create New Notification Set'}
                </DialogTitle>
                <DialogContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    {/* Name */}
                    <TextField
                      fullWidth
                      label="Notification Set Name"
                      required
                      value={notificationSetFormData.name}
                      onChange={(e) => setNotificationSetFormData(prev => ({ ...prev, name: e.target.value }))}
                      error={!!notificationSetFormErrors.name}
                      helperText={notificationSetFormErrors.name}
                      placeholder="e.g., General, Summer Campaign 2024"
                    />

                    {/* Campaign Selection */}
                    <FormControl fullWidth>
                      <InputLabel>Campaign (Optional)</InputLabel>
                      <Select
                        value={notificationSetFormData.campaign_id === null ? '' : notificationSetFormData.campaign_id}
                        onChange={(e) => setNotificationSetFormData(prev => ({ ...prev, campaign_id: e.target.value === '' ? null : e.target.value }))}
                        label="Campaign (Optional)"
                        disabled={editingNotificationSet && editingNotificationSet.campaign_id === null}
                      >
                        <MenuItem
                          value=""
                          disabled={hasGeneralSet && (!editingNotificationSet || editingNotificationSet.campaign_id !== null)}
                        >
                          <em>General (Not linked to any campaign){hasGeneralSet && (!editingNotificationSet || editingNotificationSet.campaign_id !== null) ? ' - Already exists' : ''}</em>
                        </MenuItem>
                        {campaigns && campaigns.length > 0 ? campaigns.map((campaign) => {
                          const campaignHasSet = campaignsWithSets.has(campaign.id);
                          const isCurrentCampaign = editingNotificationSet && editingNotificationSet.campaign_id === campaign.id;
                          const shouldDisable = campaignHasSet && !isCurrentCampaign;

                          return (
                            <MenuItem
                              key={campaign.id}
                              value={campaign.id}
                              disabled={shouldDisable}
                            >
                              {campaign.name}{shouldDisable ? ' (Already has notification set)' : ''}
                            </MenuItem>
                          );
                        }) : (
                          <MenuItem disabled>
                            <em>No campaigns available</em>
                          </MenuItem>
                        )}
                      </Select>
                      <FormHelperText>
                        {editingNotificationSet && editingNotificationSet.campaign_id === null
                          ? 'General notification sets cannot be changed to campaign-specific sets.'
                          : 'Select a campaign to create campaign-specific templates, or leave empty for general templates.'
                        }
                      </FormHelperText>
                    </FormControl>

                    {/* Header Image */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Header Image (Optional)</Typography>
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>
                        When set, overrides the header image from Settings for emails using this notification set.
                      </Typography>
                      <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="notification-set-header-upload"
                        type="file"
                        onChange={handleNotificationSetHeaderImageUpload}
                      />
                      <label htmlFor="notification-set-header-upload">
                        <Button variant="outlined" component="span" size="small" sx={{ mb: 1 }}>
                          Upload Header Image
                        </Button>
                      </label>
                      {notificationSetFormData.header_image && (
                        <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                          <img src={`${CDN_URL}/${notificationSetFormData.header_image}`} alt="Header" style={{ maxWidth: 200, maxHeight: 100 }} />
                          <Button variant="outlined" color="error" size="small" onClick={() => setNotificationSetFormData(prev => ({ ...prev, header_image: '' }))}>
                            Remove
                          </Button>
                        </Box>
                      )}
                    </Box>

                    {/* Footer Image */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Footer Image (Optional)</Typography>
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>
                        When set, overrides the footer image from Settings for emails using this notification set.
                      </Typography>
                      <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="notification-set-footer-upload"
                        type="file"
                        onChange={handleNotificationSetFooterImageUpload}
                      />
                      <label htmlFor="notification-set-footer-upload">
                        <Button variant="outlined" component="span" size="small" sx={{ mb: 1 }}>
                          Upload Footer Image
                        </Button>
                      </label>
                      {notificationSetFormData.footer_image && (
                        <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                          <img src={`${CDN_URL}/${notificationSetFormData.footer_image}`} alt="Footer" style={{ maxWidth: 200, maxHeight: 100 }} />
                          <Button variant="outlined" color="error" size="small" onClick={() => setNotificationSetFormData(prev => ({ ...prev, footer_image: '' }))}>
                            Remove
                          </Button>
                        </Box>
                      )}
                    </Box>

                    {/* Info about template types */}
                    <Alert severity="info">
                      {notificationSetFormData.campaign_id ?
                        'Campaign-specific sets will include: Campaign Start, optional Campaign Start with Activation Link (add manually), Reminder (No Points), Reminder (With Points), Reminder (Never logged-in), Reminder (Logged-in), and Voucher templates. Only one notification set is allowed per campaign.' :
                        `General sets will include all template types including Temporary Password template. Only one General set is allowed per company.${hasGeneralSet && (!editingNotificationSet || editingNotificationSet.campaign_id !== null) ? ' A General set already exists.' : ''}`
                      }
                    </Alert>

                    {/* Error Messages */}
                    {notificationSetFormErrors.submit && (
                      <Alert severity="error">
                        {notificationSetFormErrors.submit}
                      </Alert>
                    )}
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setNotificationSetDialog(false)} disabled={submittingNotificationSet}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleNotificationSetSubmit}
                    variant="contained"
                    disabled={submittingNotificationSet}
                  >
                    {submittingNotificationSet ? <CircularProgress size={20} /> : (editingNotificationSet ? 'Update Set' : 'Create Set')}
                  </Button>
                </DialogActions>
              </Dialog>
            </Paper>
          </Container>
        );

      case 'communication':
        return (
          <Container maxWidth="lg" sx={{ py: 4, pl: 2, pr: 4 }}>
            <Paper sx={{ p: 3, backgroundColor: '#ffffff' }}>
              <Typography variant="h5" gutterBottom>
                Communication
              </Typography>

              {communicationError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCommunicationError('')}>
                  {communicationError}
                </Alert>
              )}
              {communicationSuccess && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCommunicationSuccess('')}>
                  {communicationSuccess}
                </Alert>
              )}

              <Tabs
                value={communicationTab}
                onChange={(e, v) => setCommunicationTab(v)}
                sx={{ mt: 1 }}
              >
                <Tab label="Scheduled" value="scheduled" />
                <Tab label="Instant" value="instant" />
                <Tab label="Special" value="special" />
                <Tab label="History" value="history" />
                <Tab label="Statistics" value="statistics" />
              </Tabs>

              {communicationTab === 'scheduled' && (
                <Box sx={{ mt: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Scheduled Reminders</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Timezone: {companyTimezone}
                    </Typography>
                  </Box>

                  {/* Campaign Filter */}
                  <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <FormControl sx={{ minWidth: 250 }}>
                      <InputLabel>Campaign Filter</InputLabel>
                      <Select
                        value={scheduledRemindersCampaignFilter}
                        label="Campaign Filter"
                        onChange={(e) => setScheduledRemindersCampaignFilter(e.target.value)}
                      >
                        <MenuItem value="ALL">All Campaigns</MenuItem>
                        {campaigns.filter(c => c.status === 'RUNNING').map((campaign) => (
                          <MenuItem key={campaign.id} value={campaign.id.toString()}>
                            {campaign.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={loadAllScheduledReminders}
                      disabled={loadingReminders}
                    >
                      Refresh
                    </Button>
                  </Box>

                  {loadingReminders ? (
                    <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Campaign</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Schedule</TableCell>
                            <TableCell>Next run ({companyTimezone})</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {scheduledReminders.map((reminder) => {
                            const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
                            const typeLabel = getTemplateTypeLabel(reminder.template_type);
                            const freqLabel = getReminderFrequencyLabel(reminder.frequency);
                            const scheduleText = `${days[reminder.day_of_week]} ${String(reminder.hour).padStart(2, '0')}:${String(reminder.minute).padStart(2, '0')} · ${freqLabel}`;

                            // Convert UTC next_run to company timezone
                            const nextRunInCompanyTz = reminder.next_run
                              ? dayjs.tz(reminder.next_run, 'UTC').tz(companyTimezone).format(dateFormatForDayjs)
                              : '-';

                            return (
                              <TableRow key={reminder.id}>
                                <TableCell>{reminder.campaign?.name || 'Unknown'}</TableCell>
                                <TableCell>{typeLabel}</TableCell>
                                <TableCell>{scheduleText}</TableCell>
                                <TableCell>
                                  {nextRunInCompanyTz}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={reminder.is_active ? 'Active' : 'Paused'}
                                    size="small"
                                    color={reminder.is_active ? 'success' : 'default'}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleToggleReminderActive(reminder)}
                                    title={reminder.is_active ? 'Pause' : 'Resume'}
                                  >
                                    {reminder.is_active ? <StopIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenReminderDialog(reminder.campaign_id, reminder)}
                                    title="Edit"
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteReminder(reminder.id)}
                                    title="Delete"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          {scheduledReminders.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={6} align="center">
                                No scheduled reminders. Click "Add Reminder" button for a campaign below.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  {/* Add Reminder buttons per campaign */}
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                      Add Reminders by Campaign
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {campaigns.filter(c => c.status === 'RUNNING' && campaignsWithSets.has(c.id)).map(campaign => (
                        <Button
                          key={campaign.id}
                          variant="outlined"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => handleOpenReminderDialog(campaign.id)}
                        >
                          {campaign.name}
                        </Button>
                      ))}
                      {campaigns.filter(c => c.status === 'RUNNING' && campaignsWithSets.has(c.id)).length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                          No running campaigns with notification sets. Create a campaign and add a notification set to schedule reminders.
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Reminder Dialog */}
                  <Dialog open={reminderDialog} onClose={handleCloseReminderDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>{editingReminder ? 'Edit Reminder' : 'Add Reminder'}</DialogTitle>
                    <DialogContent>
                      {reminderError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          {reminderError}
                        </Alert>
                      )}

                      <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Reminder Type</InputLabel>
                        <Select
                          value={reminderForm.template_type}
                          onChange={(e) => setReminderForm({ ...reminderForm, template_type: e.target.value })}
                          label="Reminder Type"
                        >
                          <MenuItem value="REMINDER_NO_POINTS">Reminder (No Points)</MenuItem>
                          <MenuItem value="REMINDER_WITH_POINTS">Reminder (With Points)</MenuItem>
                          <MenuItem value="REMINDER_NEVER_LOGGED_IN">Reminder (Never logged-in)</MenuItem>
                          <MenuItem value="REMINDER_LOGGED_IN">Reminder (Logged-in)</MenuItem>
                          <MenuItem value="REMINDER_LOGGED_IN_2">Reminder (Logged-in 2)</MenuItem>
                          <MenuItem value="REMINDER_TARGET_ACHIEVED">Reminder (Target Achieved)</MenuItem>
                          <MenuItem value="REMINDER_TARGET_NOT_ACHIEVED">Reminder (Target NOT Achieved)</MenuItem>
                          <MenuItem value="REMINDER_ALL">Reminder (ALL)</MenuItem>
                        </Select>
                      </FormControl>

                      <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Frequency</InputLabel>
                        <Select
                          value={reminderForm.frequency}
                          onChange={(e) => setReminderForm({ ...reminderForm, frequency: e.target.value })}
                          label="Frequency"
                        >
                          <MenuItem value="ONCE">Once</MenuItem>
                          <MenuItem value="WEEKLY">Weekly</MenuItem>
                          <MenuItem value="BIWEEKLY">Biweekly</MenuItem>
                          <MenuItem value="MONTHLY">Monthly</MenuItem>
                        </Select>
                      </FormControl>

                      <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Day of Week</InputLabel>
                        <Select
                          value={reminderForm.day_of_week}
                          onChange={(e) => setReminderForm({ ...reminderForm, day_of_week: e.target.value })}
                          label="Day of Week"
                        >
                          <MenuItem value={0}>Monday</MenuItem>
                          <MenuItem value={1}>Tuesday</MenuItem>
                          <MenuItem value={2}>Wednesday</MenuItem>
                          <MenuItem value={3}>Thursday</MenuItem>
                          <MenuItem value={4}>Friday</MenuItem>
                          <MenuItem value={5}>Saturday</MenuItem>
                          <MenuItem value={6}>Sunday</MenuItem>
                        </Select>
                      </FormControl>

                      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                        <TextField
                          label="Hour (0-23)"
                          type="number"
                          value={reminderForm.hour}
                          onChange={(e) => setReminderForm({ ...reminderForm, hour: parseInt(e.target.value, 10) || 0 })}
                          inputProps={{ min: 0, max: 23 }}
                          fullWidth
                        />
                        <TextField
                          label="Minute (0-59)"
                          type="number"
                          value={reminderForm.minute}
                          onChange={(e) => setReminderForm({ ...reminderForm, minute: parseInt(e.target.value, 10) || 0 })}
                          inputProps={{ min: 0, max: 59 }}
                          fullWidth
                        />
                      </Box>

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={reminderForm.is_active}
                            onChange={(e) => setReminderForm({ ...reminderForm, is_active: e.target.checked })}
                          />
                        }
                        label="Active"
                        sx={{ mt: 2 }}
                      />
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={handleCloseReminderDialog}>Cancel</Button>
                      <Button
                        variant="contained"
                        onClick={handleSaveReminder}
                        disabled={savingReminder}
                      >
                        {savingReminder ? 'Saving...' : 'Save'}
                      </Button>
                    </DialogActions>
                  </Dialog>
                </Box>
              )}

              {communicationTab === 'instant' && (
                <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography variant="h6">Recipients</Typography>
                  <Autocomplete
                    options={communicationCampaigns}
                    value={communicationCampaigns.find(c => String(c.id) === String(selectedCampaignId)) || null}
                    getOptionLabel={(option) => option?.name || ''}
                    onChange={(event, value) => {
                      if (!value) {
                        setSelectedCampaignId('');
                        setCampaignDealers([]);
                        return;
                      }
                      const valueId = String(value.id);
                      setSelectedCampaignId(valueId);
                      handleCampaignSelection(valueId);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Campaign"
                        placeholder="Start typing to search..."
                        helperText={loadingCommunicationCampaigns ? 'Loading campaigns...' : ''}
                      />
                    )}
                    loading={loadingCommunicationCampaigns}
                    noOptionsText={loadingCommunicationCampaigns ? 'Loading campaigns...' : 'No campaigns available'}
                    isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                  />

                  {selectedCampaignId && campaignDealers.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Dealers assigned to this campaign ({campaignDealers.length}):
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2, maxHeight: '240px', overflowY: 'auto' }}>
                        <List dense>
                          {campaignDealers.map((dealer) => (
                            <ListItem key={dealer.id}>
                              <ListItemText primary={dealer.name} />
                            </ListItem>
                          ))}
                        </List>
                      </Paper>
                    </Box>
                  )}

                  {selectedCampaignId && campaignDealers.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      No dealers assigned to this campaign.
                    </Typography>
                  )}

                  <Typography variant="h6" sx={{ mt: 2 }}>Message</Typography>
                  <TextField label="Title" fullWidth value={instantTitle} onChange={e => setInstantTitle(e.target.value)} />
                  <TextField label="Subtitle" fullWidth value={instantSubtitle} onChange={e => setInstantSubtitle(e.target.value)} />
                  <Box sx={{ mt: 2 }}>
                    <RichTextEditor value={instantContent} onChange={setInstantContent} placeholder="Enter email content..." />
                  </Box>

                  <Typography variant="body2" sx={{ mt: 2, mb: 0.5, fontWeight: 500 }}>Extra content below CTA</Typography>
                  <Box>
                    <RichTextEditor value={instantExtraContent} onChange={setInstantExtraContent} placeholder="Enter additional content below CTA button..." />
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1, flexWrap: 'wrap' }}>
                    <TextField label="Test email address" value={testEmail} onChange={e => setTestEmail(e.target.value)} size="small" />
                    <Button variant="outlined" onClick={async () => {
                      const emailOk = /.+@.+\..+/.test(testEmail);
                      if (!emailOk) { setCommunicationError('Enter a valid test email'); return; }
                      if (!instantTitle.trim()) { setCommunicationError('Title is required'); return; }
                      if (!instantContent || instantContent.trim() === '') { setCommunicationError('Content is required'); return; }

                      setCommunicationError('');
                      setCommunicationSuccess('');

                      try {
                        const result = await apiCall('post', '/api/email/send-test', {
                          to_email: testEmail,
                          subject: instantTitle,
                          subtitle: instantSubtitle,
                          content: instantContent,
                          extra_content: instantExtraContent
                        });

                        if (result.success && result.data.success) {
                          setCommunicationSuccess('Test email sent successfully!');
                        } else {
                          setCommunicationError(result.data?.error || result.error || 'Failed to send test email');
                        }
                      } catch (error) {
                        setCommunicationError('Failed to send test email: ' + (error.message || 'Unknown error'));
                      }
                    }}>Send Test</Button>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button variant="contained" onClick={handleOpenSendConfirm} disabled={sendingInstant}>
                      {sendingInstant ? 'Sending...' : 'SEND NOW'}
                    </Button>
                  </Box>
                </Box>
              )}

              {communicationTab === 'special' && (
                <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography variant="h6">Recipients</Typography>
                  <Typography variant="body2" color="text.secondary">
                    CSV columns: <strong>Email</strong>, <strong>Url</strong>. Use <code>[your link text]&#123;Url&#125;</code> in the message body; each recipient gets their URL from the CSV.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                      accept=".csv"
                      style={{ display: 'none' }}
                      id="special-csv-upload"
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        const inputEl = e.target;
                        if (!file || !user?.company_id || !apiCall) {
                          if (inputEl) inputEl.value = '';
                          return;
                        }
                        const reader = new FileReader();
                        reader.onload = async (ev) => {
                          const text = ev.target?.result;
                          if (typeof text !== 'string') {
                            setCommunicationError('Could not read CSV file');
                            if (inputEl) inputEl.value = '';
                            return;
                          }
                          const lines = text.split(/\r?\n/);
                          if (lines.length < 2) {
                            setCommunicationError('CSV file is empty or invalid');
                            if (inputEl) inputEl.value = '';
                            return;
                          }
                          const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
                          const emailIdx = headers.findIndex((h) => h === 'email' || h.includes('email'));
                          const urlIdx = headers.findIndex((h) => h === 'url' || h === 'link');
                          if (emailIdx === -1 || urlIdx === -1) {
                            setCommunicationError('CSV must have Email and Url columns');
                            if (inputEl) inputEl.value = '';
                            return;
                          }
                          const rows = [];
                          for (let i = 1; i < lines.length; i++) {
                            const line = lines[i].trim();
                            if (!line) continue;
                            const values = line.split(',').map((v) => v.trim().replace(/^"|"$/g, ''));
                            const em = values[emailIdx] || '';
                            const u = values[urlIdx] || '';
                            if (em) rows.push({ email: em, url: u });
                          }
                          if (!rows.length) {
                            setCommunicationError('No data rows in CSV');
                            if (inputEl) inputEl.value = '';
                            return;
                          }
                          setLoadingSpecialPreview(true);
                          setCommunicationError('');
                          try {
                            const result = await apiCall('post', '/api/communications/special/preview', {
                              company_id: user.company_id,
                              rows
                            });
                            if (result.success && result.data) {
                              setSpecialAcceptedRecipients(result.data.accepted || []);
                              setSpecialRejectedRows(result.data.rejected || []);
                            } else {
                              setCommunicationError(result.error || 'Failed to validate recipients');
                              setSpecialAcceptedRecipients([]);
                              setSpecialRejectedRows([]);
                            }
                          } catch (err) {
                            console.error(err);
                            setCommunicationError('Failed to validate recipients');
                            setSpecialAcceptedRecipients([]);
                            setSpecialRejectedRows([]);
                          }
                          setLoadingSpecialPreview(false);
                          if (inputEl) inputEl.value = '';
                        };
                        reader.readAsText(file);
                      }}
                    />
                    <label htmlFor="special-csv-upload">
                      <Button variant="outlined" component="span" disabled={loadingSpecialPreview}>
                        {loadingSpecialPreview ? 'Validating…' : 'Upload CSV'}
                      </Button>
                    </label>
                  </Box>

                  {specialRejectedRows.length > 0 && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                        {specialRejectedRows.length} row{specialRejectedRows.length !== 1 ? 's' : ''} not found in database (no matching dealer user for this company)
                      </Typography>
                      <Box component="ul" sx={{ m: '8px 0 0', pl: 2 }}>
                        {specialRejectedRows.map((r, idx) => (
                          <li key={`${r.email}-${idx}`}>
                            <Typography variant="body2" component="span">
                              {r.email}
                              {r.url ? ` — ${r.url}` : ''}
                            </Typography>
                          </li>
                        ))}
                      </Box>
                    </Alert>
                  )}

                  {specialAcceptedRecipients.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Accepted recipients ({specialAcceptedRecipients.length})
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 0, maxHeight: '240px', overflowY: 'auto' }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>Email</TableCell>
                              <TableCell>Url</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {specialAcceptedRecipients.map((r, idx) => (
                              <TableRow key={`${r.email}-${idx}`}>
                                <TableCell>{r.email}</TableCell>
                                <TableCell sx={{ wordBreak: 'break-all' }}>{r.url}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Paper>
                    </Box>
                  )}

                  <Typography variant="h6" sx={{ mt: 2 }}>Message</Typography>
                  <TextField label="Title" fullWidth value={specialTitle} onChange={(e) => setSpecialTitle(e.target.value)} />
                  <TextField label="Subtitle" fullWidth value={specialSubtitle} onChange={(e) => setSpecialSubtitle(e.target.value)} />
                  <Box sx={{ mt: 2 }}>
                    <RichTextEditor value={specialContent} onChange={setSpecialContent} placeholder="Enter email content…" />
                  </Box>

                  <Typography variant="body2" sx={{ mt: 2, mb: 0.5, fontWeight: 500 }}>Extra content below CTA</Typography>
                  <Box>
                    <RichTextEditor value={specialExtraContent} onChange={setSpecialExtraContent} placeholder="Enter additional content below CTA button…" />
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1, flexWrap: 'wrap' }}>
                    <TextField
                      label="Test email address"
                      value={specialTestEmail}
                      onChange={(e) => setSpecialTestEmail(e.target.value)}
                      size="small"
                    />
                    <Button
                      variant="outlined"
                      onClick={async () => {
                        const emailOk = /.+@.+\..+/.test(specialTestEmail);
                        if (!emailOk) { setCommunicationError('Enter a valid test email'); return; }
                        if (!specialTitle.trim()) { setCommunicationError('Title is required'); return; }
                        if (!specialContent || specialContent.trim() === '') { setCommunicationError('Content is required'); return; }
                        const testUrl = specialAcceptedRecipients[0]?.url || '';
                        if (!testUrl) { setCommunicationError('Upload CSV with at least one accepted row to supply a URL for the test email'); return; }

                        setCommunicationError('');
                        setCommunicationSuccess('');

                        try {
                          const result = await apiCall('post', '/api/email/send-test', {
                            to_email: specialTestEmail,
                            subject: specialTitle,
                            subtitle: specialSubtitle,
                            content: applySpecialUrlPlaceholderForTest(specialContent, testUrl),
                            extra_content: applySpecialUrlPlaceholderForTest(specialExtraContent || '', testUrl),
                            use_special_sendgrid_template: true
                          });

                          if (result.success && result.data.success) {
                            setCommunicationSuccess('Test email sent successfully!');
                          } else {
                            setCommunicationError(result.data?.error || result.error || 'Failed to send test email');
                          }
                        } catch (error) {
                          setCommunicationError('Failed to send test email: ' + (error.message || 'Unknown error'));
                        }
                      }}
                    >
                      Send Test
                    </Button>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button variant="contained" onClick={handleOpenSpecialSendConfirm} disabled={sendingSpecial}>
                      {sendingSpecial ? 'Sending…' : 'SEND NOW'}
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Send Confirmation Dialog */}
              <Dialog open={showSendConfirmDialog} onClose={() => setShowSendConfirmDialog(false)}>
                <DialogTitle>Confirm Send</DialogTitle>
                <DialogContent>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    You are about to send this message to:
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 'bold' }}>
                    {recipientCounts.dealer_count} Dealer{recipientCounts.dealer_count !== 1 ? 's' : ''}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 3, fontWeight: 'bold' }}>
                    {recipientCounts.user_count} User{recipientCounts.user_count !== 1 ? 's' : ''}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={sendConfirmChecked}
                        onChange={(e) => setSendConfirmChecked(e.target.checked)}
                      />
                    }
                    label="Yes, send it"
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setShowSendConfirmDialog(false)}>Cancel</Button>
                  <Button
                    variant="contained"
                    onClick={handleSendInstant}
                    disabled={!sendConfirmChecked}
                  >
                    Send
                  </Button>
                </DialogActions>
              </Dialog>

              <Dialog open={showSpecialSendConfirmDialog} onClose={() => setShowSpecialSendConfirmDialog(false)}>
                <DialogTitle>Confirm Send</DialogTitle>
                <DialogContent>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    You are about to send this message to:
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 3, fontWeight: 'bold' }}>
                    {specialAcceptedRecipients.length} User{specialAcceptedRecipients.length !== 1 ? 's' : ''}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={sendSpecialConfirmChecked}
                        onChange={(e) => setSendSpecialConfirmChecked(e.target.checked)}
                      />
                    }
                    label="Yes, send it"
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setShowSpecialSendConfirmDialog(false)}>Cancel</Button>
                  <Button
                    variant="contained"
                    onClick={handleSendSpecial}
                    disabled={!sendSpecialConfirmChecked}
                  >
                    Send
                  </Button>
                </DialogActions>
              </Dialog>

              {communicationTab === 'history' && (
                <Box sx={{ mt: 3 }}>
                  {/* Campaign Filter */}
                  <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <FormControl sx={{ minWidth: 250 }}>
                      <InputLabel>Campaign Filter</InputLabel>
                      <Select
                        value={commHistoryCampaignFilter}
                        label="Campaign Filter"
                        onChange={(e) => setCommHistoryCampaignFilter(e.target.value)}
                      >
                        <MenuItem value="ALL">All Campaigns</MenuItem>
                        {communicationCampaigns.map((campaign) => (
                          <MenuItem key={campaign.id} value={campaign.id.toString()}>
                            {campaign.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={loadCommunicationHistory}
                      disabled={loadingCommHistory}
                    >
                      Refresh
                    </Button>
                  </Box>
                  {loadingCommHistory ? (
                    <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
                  ) : (
                    <>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Date ({companyTimezone})</TableCell>
                              <TableCell>Type</TableCell>
                              <TableCell>Campaign</TableCell>
                              <TableCell>Users</TableCell>
                              <TableCell>Opened</TableCell>
                              <TableCell>Clicked</TableCell>
                              <TableCell>Title</TableCell>
                              <TableCell align="center">Action</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {commHistory.map(row => {
                              const campaign = communicationCampaigns.find(c => c.id === row.campaign_id);
                              return (
                                <TableRow key={row.id}>
                                  <TableCell>{dayjs.tz(row.created, 'UTC').tz(companyTimezone).format(dateFormatForDayjs)}</TableCell>
                                  <TableCell>{row.type}</TableCell>
                                  <TableCell>{campaign ? campaign.name : '-'}</TableCell>
                                  <TableCell>{row.email_count}</TableCell>
                                  <TableCell>{row.unique_opened_count}</TableCell>
                                  <TableCell>{row.unique_clicked_count}</TableCell>
                                  <TableCell>{row.title}</TableCell>
                                  <TableCell align="center">
                                    <Button 
                                      size="small" 
                                      variant="text" 
                                      onClick={() => handleOpenCommDetails(row)}
                                    >
                                      Details
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                            {commHistory.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={8} align="center">No communications yet.</TableCell>
                              </TableRow>
                            )}

                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        component="div"
                        count={commHistoryTotal}
                        page={commHistoryPage}
                        onPageChange={handleCommHistoryChangePage}
                        rowsPerPage={commHistoryRowsPerPage}
                        onRowsPerPageChange={handleCommHistoryChangeRows}
                        rowsPerPageOptions={[10, 25, 50]}
                      />
                    </>
                  )}
                </Box>
              )}

              {communicationTab === 'statistics' && (
                <Box sx={{ mt: 3 }}>
                  {/* Campaign Filter */}
                  <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <FormControl sx={{ minWidth: 250 }}>
                      <InputLabel>Campaign Filter</InputLabel>
                      <Select
                        value={commStatsCampaignFilter}
                        label="Campaign Filter"
                        onChange={(e) => setCommStatsCampaignFilter(e.target.value)}
                      >
                        <MenuItem value="ALL">All Campaigns</MenuItem>
                        {communicationCampaigns.map((campaign) => (
                          <MenuItem key={campaign.id} value={campaign.id.toString()}>
                            {campaign.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={loadCommunicationStatistics}
                      disabled={loadingCommStatistics}
                    >
                      Refresh
                    </Button>
                  </Box>

                  {loadingCommStatistics ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : commStatistics ? (
                    <>
                      {/* Main Statistics Cards */}
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={3}>
                          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">TOTAL COMMUNICATIONS</Typography>
                            <Typography variant="h4">{commStatistics.total_communications}</Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">EMAILS SENT</Typography>
                            <Typography variant="h4">{commStatistics.total_emails_sent}</Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">DELIVERED</Typography>
                            <Typography variant="h4">{commStatistics.total_delivered}</Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">DELIVERY RATE</Typography>
                            <Typography variant="h4" color="success.main">{commStatistics.delivery_rate}%</Typography>
                          </Paper>
                        </Grid>
                      </Grid>

                      {/* Engagement Metrics */}
                      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
                        Engagement Metrics
                      </Typography>
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={4}>
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="caption" color="text.secondary">OPENS</Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                              <Typography variant="h5">{commStatistics.unique_opened}</Typography>
                              <Typography variant="h6" color="primary">{commStatistics.open_rate}%</Typography>
                            </Box>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="caption" color="text.secondary">CLICKS</Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                              <Typography variant="h5">{commStatistics.unique_clicked}</Typography>
                              <Typography variant="h6" color="primary">{commStatistics.click_rate}%</Typography>
                            </Box>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="caption" color="text.secondary">BOUNCE RATE</Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                              <Typography variant="h5">{commStatistics.total_bounced}</Typography>
                              <Typography variant="h6" color="error.main">{commStatistics.bounce_rate}%</Typography>
                            </Box>
                          </Paper>
                        </Grid>
                      </Grid>

                      {/* Detailed Event Counts */}
                      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
                        Detailed Event Counts
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={4} md={3}>
                          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">PROCESSED</Typography>
                            <Typography variant="h6">{commStatistics.total_processed}</Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6} sm={4} md={3}>
                          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">DEFERRED</Typography>
                            <Typography variant="h6">{commStatistics.total_deferred}</Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6} sm={4} md={3}>
                          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">DROPPED</Typography>
                            <Typography variant="h6">{commStatistics.total_dropped}</Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6} sm={4} md={3}>
                          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">BLOCKED</Typography>
                            <Typography variant="h6">{commStatistics.total_blocked}</Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6} sm={4} md={3}>
                          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">SPAM REPORTS</Typography>
                            <Typography variant="h6">{commStatistics.total_spam_reports}</Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6} sm={4} md={3}>
                          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">UNSUBSCRIBED</Typography>
                            <Typography variant="h6">{commStatistics.total_unsubscribed}</Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                    </>
                  ) : (
                    <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                      <Typography color="text.secondary">No statistics available</Typography>
                    </Paper>
                  )}
                </Box>
              )}
            </Paper>
          </Container>
        );

      case 'settings':
        return (
          <Container maxWidth="lg" sx={{ py: 4, pl: 2, pr: 4 }}>
            <Paper sx={{ p: 3, backgroundColor: '#ffffff' }}>
              <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                Company Settings
              </Typography>

              {emailConfigError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEmailConfigError('')}>
                  {emailConfigError}
                </Alert>
              )}

              {emailConfigSuccess && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setEmailConfigSuccess('')}>
                  {emailConfigSuccess}
                </Alert>
              )}

              {/* Company Timezone */}
              <Box sx={{ mb: 4, pb: 3, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6" gutterBottom>
                  Company Timezone
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select your company's timezone for scheduling reminders and notifications
                </Typography>
                <FormControl fullWidth disabled={emailConfigSaving}>
                  <InputLabel>Timezone</InputLabel>
                  <Select
                    value={companyTimezone}
                    onChange={(e) => setCompanyTimezone(e.target.value)}
                    label="Timezone"
                  >
                    <MenuItem value="UTC">UTC (Coordinated Universal Time)</MenuItem>
                    <MenuItem value="Europe/Warsaw">Europe/Warsaw (Central European Time)</MenuItem>
                    <MenuItem value="Europe/London">Europe/London (GMT/BST)</MenuItem>
                    <MenuItem value="America/New_York">America/New_York (Eastern Time)</MenuItem>
                    <MenuItem value="America/Chicago">America/Chicago (Central Time)</MenuItem>
                    <MenuItem value="America/Denver">America/Denver (Mountain Time)</MenuItem>
                    <MenuItem value="America/Los_Angeles">America/Los_Angeles (Pacific Time)</MenuItem>
                    <MenuItem value="Asia/Tokyo">Asia/Tokyo (Japan Standard Time)</MenuItem>
                    <MenuItem value="Asia/Shanghai">Asia/Shanghai (China Standard Time)</MenuItem>
                    <MenuItem value="Asia/Singapore">Asia/Singapore (Singapore Time)</MenuItem>
                    <MenuItem value="Australia/Sydney">Australia/Sydney (Australian Eastern Time)</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ mb: 4, pb: 3, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6" gutterBottom>
                  Dealer dashboard welcome
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Heading shown on the dealer portal home page. Leave empty to use the default translation for the company language.
                </Typography>
                <TextField
                  label="Welcome message"
                  value={dealerDashboardWelcome}
                  onChange={(e) => setDealerDashboardWelcome(e.target.value)}
                  disabled={emailConfigSaving}
                  fullWidth
                  multiline
                  minRows={2}
                  placeholder="Welcome to the contest platform!"
                />
              </Box>

              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Email Configuration
              </Typography>

              {/* Email Header Image Upload */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Default Email Header Image
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Upload an image to be used as the header in email communications
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="email-header-upload"
                    type="file"
                    onChange={handleEmailHeaderImageUpload}
                    disabled={emailConfigLoading || emailConfigSaving}
                  />
                  <label htmlFor="email-header-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      disabled={emailConfigLoading || emailConfigSaving}
                      startIcon={emailConfigLoading ? <CircularProgress size={20} /> : null}
                    >
                      {emailConfigLoading ? 'Uploading...' : 'Upload Header Image'}
                    </Button>
                  </label>
                </Box>

                {emailConfig.email_header_image && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                      Current Header Image:
                    </Typography>
                    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, display: 'inline-block' }}>
                      <img
                        src={`${CDN_URL}/${emailConfig.email_header_image}`}
                        alt="Email header preview"
                        style={{
                          maxWidth: '400px',
                          maxHeight: '150px',
                          objectFit: 'contain'
                        }}
                        onError={(e) => {
                          console.error('Failed to load header image');
                          e.target.style.display = 'none';
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Box>

              {/* CTA Button Configuration */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  CTA Button
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Configure the call-to-action button for email communications
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
                  <TextField
                    label="Button Title"
                    value={emailConfig.button_title}
                    onChange={(e) => setEmailConfig(prev => ({ ...prev, button_title: e.target.value }))}
                    disabled={emailConfigSaving}
                    fullWidth
                    placeholder="Log in"
                  />
                  <TextField
                    label="Button Url"
                    value={emailConfig.button_url}
                    onChange={(e) => setEmailConfig(prev => ({ ...prev, button_url: e.target.value }))}
                    disabled={emailConfigSaving}
                    fullWidth
                    placeholder="https://example.com"
                  />
                </Box>
              </Box>

              {/* Email Footer Image Upload */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Default Email Footer Image
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Upload an image to be used as the footer in email communications
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="email-footer-upload"
                    type="file"
                    onChange={handleEmailFooterImageUpload}
                    disabled={emailConfigLoading || emailConfigSaving}
                  />
                  <label htmlFor="email-footer-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      disabled={emailConfigLoading || emailConfigSaving}
                      startIcon={emailConfigLoading ? <CircularProgress size={20} /> : null}
                    >
                      {emailConfigLoading ? 'Uploading...' : 'Upload Footer Image'}
                    </Button>
                  </label>
                </Box>

                {emailConfig.email_footer_image && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                      Current Footer Image:
                    </Typography>
                    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, display: 'inline-block' }}>
                      <img
                        src={`${CDN_URL}/${emailConfig.email_footer_image}`}
                        alt="Email footer preview"
                        style={{
                          maxWidth: '400px',
                          maxHeight: '150px',
                          objectFit: 'contain'
                        }}
                        onError={(e) => {
                          console.error('Failed to load footer image');
                          e.target.style.display = 'none';
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Email Footer Rich Text Editor */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Default Email Footer
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Enter the default footer text to appear in email communications
                </Typography>

                <RichTextEditor
                  value={emailConfig.email_footer}
                  onChange={(value) => setEmailConfig(prev => ({ ...prev, email_footer: value }))}
                  placeholder="Enter email footer content..."
                  readOnly={emailConfigSaving}
                />
              </Box>

              {/* Email From Name */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Email From Name
                </Typography>

                <TextField
                  label="Sender Name"
                  value={emailConfig.sender_name}
                  onChange={(e) => setEmailConfig(prev => ({ ...prev, sender_name: e.target.value }))}
                  disabled={emailConfigSaving}
                  fullWidth
                  placeholder="SMOD"
                  helperText="The name that will appear as the sender in recipient's inbox"
                />
              </Box>

              {/* Temporary Password Prefix */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Temporary Password Prefix
                </Typography>

                <TextField
                  label="Temporary Password Prefix"
                  value={emailConfig.temp_password_prefix}
                  onChange={(e) => setEmailConfig(prev => ({ ...prev, temp_password_prefix: e.target.value }))}
                  disabled={emailConfigSaving}
                  fullWidth
                  placeholder="Password:"
                  helperText="Text that appears before the temporary password in emails (e.g., 'Password:', 'Hasło:', 'Your password:')"
                />
              </Box>

              {/* Formal Documents */}
              <Box sx={{ mb: 4, pb: 3, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6" gutterBottom>
                  Formal Documents
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Terms and Conditions and Privacy Policy. Dealers must accept these before accessing the portal. Upload a file or enter a URL for each. Optionally add extra confirmation text below to require an additional checkbox on the dealer acceptance dialog.
                </Typography>

                {/* Terms and Conditions */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom fontWeight={500}>
                    Terms and Conditions
                  </Typography>
                  <RadioGroup
                    row
                    value={formalDocuments.terms.type}
                    onChange={(e) => setFormalDocuments(prev => ({
                      ...prev,
                      terms: { ...prev.terms, type: e.target.value, url: e.target.value === 'url' ? prev.terms.url : '', file: e.target.value === 'file' ? prev.terms.file : '' }
                    }))}
                    sx={{ mb: 1 }}
                  >
                    <FormControlLabel value="url" control={<Radio />} label="Enter URL" disabled={emailConfigSaving} />
                    <FormControlLabel value="file" control={<Radio />} label="Upload file" disabled={emailConfigSaving} />
                  </RadioGroup>
                  {formalDocuments.terms.type === 'url' ? (
                    <TextField
                      label="Terms and Conditions URL"
                      value={formalDocuments.terms.url}
                      onChange={(e) => setFormalDocuments(prev => ({ ...prev, terms: { ...prev.terms, url: e.target.value } }))}
                      disabled={emailConfigSaving}
                      fullWidth
                      placeholder="https://example.com/terms"
                    />
                  ) : (
                    <Box>
                      <input
                        accept=".pdf,.doc,.docx,.txt,.html"
                        style={{ display: 'none' }}
                        id="terms-upload"
                        type="file"
                        onChange={(e) => handleFormalDocumentUpload('terms', e)}
                        disabled={emailConfigSaving || uploadingFormalDoc.terms}
                      />
                      <label htmlFor="terms-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          disabled={emailConfigSaving || uploadingFormalDoc.terms}
                          startIcon={uploadingFormalDoc.terms ? <CircularProgress size={20} /> : null}
                        >
                          {uploadingFormalDoc.terms ? 'Uploading...' : 'Upload Terms'}
                        </Button>
                      </label>
                      {formalDocuments.terms.file && (
                        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                          Current: {formalDocuments.terms.file.split('/').pop()}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>

                {/* Privacy Policy */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" gutterBottom fontWeight={500}>
                    Privacy Policy
                  </Typography>
                  <RadioGroup
                    row
                    value={formalDocuments.privacy.type}
                    onChange={(e) => setFormalDocuments(prev => ({
                      ...prev,
                      privacy: { ...prev.privacy, type: e.target.value, url: e.target.value === 'url' ? prev.privacy.url : '', file: e.target.value === 'file' ? prev.privacy.file : '' }
                    }))}
                    sx={{ mb: 1 }}
                  >
                    <FormControlLabel value="url" control={<Radio />} label="Enter URL" disabled={emailConfigSaving} />
                    <FormControlLabel value="file" control={<Radio />} label="Upload file" disabled={emailConfigSaving} />
                  </RadioGroup>
                  {formalDocuments.privacy.type === 'url' ? (
                    <TextField
                      label="Privacy Policy URL"
                      value={formalDocuments.privacy.url}
                      onChange={(e) => setFormalDocuments(prev => ({ ...prev, privacy: { ...prev.privacy, url: e.target.value } }))}
                      disabled={emailConfigSaving}
                      fullWidth
                      placeholder="https://example.com/privacy"
                    />
                  ) : (
                    <Box>
                      <input
                        accept=".pdf,.doc,.docx,.txt,.html"
                        style={{ display: 'none' }}
                        id="privacy-upload"
                        type="file"
                        onChange={(e) => handleFormalDocumentUpload('privacy', e)}
                        disabled={emailConfigSaving || uploadingFormalDoc.privacy}
                      />
                      <label htmlFor="privacy-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          disabled={emailConfigSaving || uploadingFormalDoc.privacy}
                          startIcon={uploadingFormalDoc.privacy ? <CircularProgress size={20} /> : null}
                        >
                          {uploadingFormalDoc.privacy ? 'Uploading...' : 'Upload Privacy Policy'}
                        </Button>
                      </label>
                      {formalDocuments.privacy.file && (
                        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                          Current: {formalDocuments.privacy.file.split('/').pop()}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>

                <Box sx={{ mt: 2 }}>
                  <TextField
                    label="Extra confirmation text"
                    value={formalDocuments.extra_confirmation_text}
                    onChange={(e) => setFormalDocuments(prev => ({ ...prev, extra_confirmation_text: e.target.value }))}
                    disabled={emailConfigSaving}
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder="Optional. If set, dealers must check a box with this text before entering the portal."
                    helperText="Non-empty text adds a required checkbox (no link) to the dealer formal documents popup, in addition to Terms and Privacy."
                  />
                </Box>
              </Box>

              {/* Dealer portal support email */}
              <Box sx={{ mb: 4, pb: 3, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6" gutterBottom>
                  Support email
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  If set, dealers see a Support line with this address in the sidebar (below Terms and Privacy). Leave empty to hide it.
                </Typography>
                <TextField
                  label="Support email"
                  type="email"
                  value={dealerSupportEmail}
                  onChange={(e) => setDealerSupportEmail(e.target.value)}
                  disabled={emailConfigSaving}
                  fullWidth
                  placeholder="support@example.com"
                />
              </Box>

              {/* Save Button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleSaveEmailConfig}
                  disabled={emailConfigLoading || emailConfigSaving}
                  startIcon={emailConfigSaving ? <CircularProgress size={20} /> : null}
                >
                  {emailConfigSaving ? 'Saving...' : 'Save Configuration'}
                </Button>
              </Box>
            </Paper>
          </Container>
        );

      default:
        return null;
    }
  };

  // Show loading while authentication is being checked
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Drawer
        variant="permanent"
        sx={{
          width: currentDrawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: currentDrawerWidth,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: companyBrandingColor || 'default',
            color: companyBrandingColor ? 'white' : 'inherit',
          },
        }}
      >
        <Box sx={{ overflow: 'auto', flexGrow: 1 }}>
          {/* Company Branding Image */}
          {companyBrandingImage && !isNarrow && (
            <Box sx={{
              pt: 2,
              pb: 2,
              px: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderBottom: companyBrandingColor ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.12)'
            }}>
              <img
                src={companyBrandingImage}
                alt="Company Logo"
                style={{
                  maxWidth: '100%',
                  maxHeight: '80px',
                  objectFit: 'contain'
                }}
              />
            </Box>
          )}
          {companyBrandingImage && isNarrow && (
            <Box sx={{
              pt: 1,
              pb: 1,
              px: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderBottom: companyBrandingColor ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.12)'
            }}>
              <img
                src={companyBrandingImage}
                alt="Company Logo"
                style={{
                  maxWidth: '40px',
                  maxHeight: '40px',
                  objectFit: 'contain'
                }}
              />
            </Box>
          )}
          <List>
            {menuItems.map((item) => (
              <ListItem key={item.key} disablePadding>
                <ListItemButton
                  selected={selectedTab === item.key}
                  onClick={() => handleMenuItemClick(item.key)}
                  sx={{
                    minHeight: 48,
                    justifyContent: isNarrow ? 'center' : 'initial',
                    px: 2.5,
                    color: companyBrandingColor ? 'white' : 'inherit',
                    '&:hover': {
                      backgroundColor: companyBrandingColor ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                    },
                    '&.Mui-selected': {
                      backgroundColor: companyBrandingColor ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.08)',
                      '&:hover': {
                        backgroundColor: companyBrandingColor ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.12)',
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: isNarrow ? 'auto' : 3,
                      justifyContent: 'center',
                      color: companyBrandingColor ? 'white' : 'inherit',
                    }}
                  >
                    <item.icon />
                  </ListItemIcon>
                  {!isNarrow && <ListItemText primary={item.label} />}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Welcome message and Logout button at the bottom */}
        <Box sx={{
          mt: 'auto',
          borderTop: companyBrandingColor ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.12)',
          p: 2
        }}>
          {!isNarrow && (
            <Typography variant="body2" sx={{
              mb: 2,
              textAlign: 'center',
              color: companyBrandingColor ? 'white' : 'inherit'
            }}>
              Welcome, {isImpersonating ? originalUser?.name : user?.name}
            </Typography>
          )}
          {isImpersonating && (
            <Box sx={{
              mb: 2,
              p: 1,
              backgroundColor: 'warning.main',
              borderRadius: 1,
              textAlign: 'center'
            }}>
              <Typography variant="caption" sx={{
                color: 'warning.contrastText',
                fontWeight: 'bold'
              }}>
                Impersonating: {user?.name}
              </Typography>
              {!isNarrow && (
                <Box sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    color="warning"
                    onClick={async () => {
                      const result = await stopImpersonation();
                      if (result.success) {
                        navigate('/admin');
                      }
                    }}
                    sx={{
                      fontSize: '0.7rem',
                      minHeight: 'auto',
                      py: 0.5,
                      px: 1
                    }}
                  >
                    Stop Impersonating
                  </Button>
                </Box>
              )}
            </Box>
          )}
          {!isMobile && (
            <Box sx={{ display: 'flex', justifyContent: isNarrow ? 'center' : 'flex-end', px: 1, pb: 1 }}>
              <IconButton
                onClick={toggleSidebar}
                size="small"
                aria-label={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
                sx={{ color: companyBrandingColor ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary' }}
              >
                {sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
              </IconButton>
            </Box>
          )}
          <Button
            fullWidth
            variant="outlined"
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
            sx={{
              justifyContent: isNarrow ? 'center' : 'flex-start',
              minHeight: 48,
              color: companyBrandingColor ? 'white' : 'primary.main',
              borderColor: companyBrandingColor ? 'rgba(255, 255, 255, 0.5)' : 'primary.main',
              '&:hover': {
                borderColor: companyBrandingColor ? 'white' : 'primary.dark',
                backgroundColor: companyBrandingColor ? 'rgba(255, 255, 255, 0.1)' : 'rgba(25, 118, 210, 0.04)',
              },
            }}
          >
            {isNarrow ? <LogoutIcon /> : 'Logout'}
          </Button>
        </Box>
      </Drawer>

      <Box component="main" sx={{
        flexGrow: 1,
        bgcolor: '#f4f5f6'
      }}>
        {renderContent()}
      </Box>

      {/* Bonus Task Dialog */}
      <Dialog
        open={bonusTaskDialog}
        onClose={() => setBonusTaskDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingBonusTask ? 'Edit Bonus Task' : 'Create Bonus Task'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(bonusTaskFormData.active)}
                  onChange={(e) =>
                    setBonusTaskFormData((prev) => ({ ...prev, active: e.target.checked }))
                  }
                />
              }
              label="Active"
            />
            <TextField
              fullWidth
              label="Title"
              required
              value={bonusTaskFormData.title}
              onChange={(e) =>
                setBonusTaskFormData((prev) => ({ ...prev, title: e.target.value }))
              }
              error={!!bonusTaskFormErrors.title}
              helperText={bonusTaskFormErrors.title}
            />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Description
            </Typography>
            <RichTextEditor
              value={bonusTaskFormData.description}
              onChange={(value) =>
                setBonusTaskFormData((prev) => ({ ...prev, description: value }))
              }
              placeholder="Enter bonus task description..."
            />
            <TextField
              fullWidth
              label="Points"
              type="number"
              required
              value={bonusTaskFormData.points}
              onChange={(e) =>
                setBonusTaskFormData((prev) => ({
                  ...prev,
                  points: e.target.value === '' ? '' : Number(e.target.value),
                }))
              }
              error={!!bonusTaskFormErrors.points}
              helperText={
                bonusTaskFormErrors.points ||
                'Points awarded when an admin accepts the dealer submission'
              }
              inputProps={{ min: 0 }}
            />
            <FormControl fullWidth required error={!!bonusTaskFormErrors.campaign_id}>
              <InputLabel id="bonus-task-campaign-label">Campaign</InputLabel>
              <Select
                labelId="bonus-task-campaign-label"
                value={bonusTaskFormData.campaign_id || ''}
                onChange={(e) =>
                  setBonusTaskFormData((prev) => ({ ...prev, campaign_id: e.target.value }))
                }
                label="Campaign"
              >
                {campaigns.map((campaign) => (
                  <MenuItem key={campaign.id} value={campaign.id}>
                    {campaign.name} ({campaign.status})
                  </MenuItem>
                ))}
              </Select>
              {bonusTaskFormErrors.campaign_id && (
                <FormHelperText>{bonusTaskFormErrors.campaign_id}</FormHelperText>
              )}
            </FormControl>
            {bonusTaskFormErrors.submit && (
              <Alert severity="error">{bonusTaskFormErrors.submit}</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBonusTaskDialog(false)} disabled={submittingBonusTask}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleBonusTaskSubmit}
            disabled={submittingBonusTask}
          >
            {submittingBonusTask ? <CircularProgress size={20} /> : editingBonusTask ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quiz Dialog */}
      <Dialog
        open={quizDialog}
        onClose={() => setQuizDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingQuiz ? 'Edit Quiz' : 'Create New Quiz'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            {/* Quiz Title */}
            <TextField
              fullWidth
              label="Quiz Title"
              required
              value={quizFormData.title}
              onChange={(e) => setQuizFormData(prev => ({ ...prev, title: e.target.value }))}
              error={!!quizFormErrors.title}
              helperText={quizFormErrors.title}
            />

            {/* Quiz Description */}
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Quiz Description (Optional)
            </Typography>
            <RichTextEditor
              value={quizFormData.description}
              onChange={(value) => setQuizFormData(prev => ({ ...prev, description: value }))}
              placeholder="Enter quiz description here..."
              style={{
                marginBottom: '16px'
              }}
            />

            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Short description (optional)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              If set, this text is shown on campaign quiz cards instead of the full description. The full description still appears when taking the quiz.
            </Typography>
            <RichTextEditor
              value={quizFormData.short_description}
              onChange={(value) => setQuizFormData(prev => ({ ...prev, short_description: value }))}
              placeholder="Brief summary for campaign lists…"
              style={{
                marginBottom: '16px'
              }}
            />

            {/* Campaign Selection */}
            <FormControl fullWidth required error={!!quizFormErrors.campaign_id}>
              <InputLabel id="quiz-campaign-label">Campaign</InputLabel>
              <Select
                labelId="quiz-campaign-label"
                value={quizFormData.campaign_id || ''}
                onChange={(e) => setQuizFormData(prev => ({ ...prev, campaign_id: e.target.value }))}
                label="Campaign"
              >
                {campaigns.map((campaign) => (
                  <MenuItem key={campaign.id} value={campaign.id}>
                    {campaign.name} ({campaign.status})
                  </MenuItem>
                ))}
              </Select>
              {quizFormErrors.campaign_id && (
                <FormHelperText>{quizFormErrors.campaign_id}</FormHelperText>
              )}
              {!quizFormErrors.campaign_id && (
                <FormHelperText>Select the campaign this quiz belongs to</FormHelperText>
              )}
            </FormControl>

            {/* Reward Field */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
              <TextField
                sx={{ flex: '1 1 200px' }}
                label="Reward (points)"
                type="number"
                value={quizFormData.reward}
                onChange={(e) => setQuizFormData(prev => ({ ...prev, reward: parseInt(e.target.value) || 0 }))}
                error={!!quizFormErrors.reward}
                helperText={
                  quizFormErrors.reward || (
                    quizFormData.reward_per_question
                      ? "Points awarded per correctly answered question (retakes replace the previous credit)"
                      : "Number of points to award for completing this quiz"
                  )
                }
                inputProps={{ min: 0 }}
              />
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Checkbox
                    checked={quizFormData.reward_per_question}
                    onChange={(e) => setQuizFormData(prev => ({ ...prev, reward_per_question: e.target.checked }))}
                  />
                }
                label="per question"
              />
            </Box>

            {/* Show correct answers */}
            <FormControl fullWidth>
              <InputLabel id="quiz-show-correct-answers-label">Show correct answers</InputLabel>
              <Select
                labelId="quiz-show-correct-answers-label"
                value={quizFormData.show_correct_answers}
                onChange={(e) => setQuizFormData(prev => ({ ...prev, show_correct_answers: e.target.value }))}
                label="Show correct answers"
              >
                <MenuItem value="ALWAYS">Always</MenuItem>
                <MenuItem value="AFTER_SECOND_ATTEMPT">After 2nd attempt</MenuItem>
                <MenuItem value="NEVER">After last attempt</MenuItem>
              </Select>
              <FormHelperText>When dealers see the correct answers after completing a quiz. &quot;After last attempt&quot; shows answers only on the 3rd (final) attempt.</FormHelperText>
            </FormControl>

            {/* Active + points requirement */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={quizFormData.active}
                    onChange={(e) => setQuizFormData(prev => ({ ...prev, active: e.target.checked }))}
                  />
                }
                label="Active Quiz"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={quizFormData.only_with_points}
                    onChange={(e) => setQuizFormData(prev => ({ ...prev, only_with_points: e.target.checked }))}
                  />
                }
                label="Award quiz points only after recorded sales (campaign values)"
              />
            </Box>

            <LocalizationProvider dateAdapter={AdapterDayjs} timezone={companyTimezone || 'UTC'}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={quizFormData.auto_on_off}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setQuizFormData((prev) => ({
                          ...prev,
                          auto_on_off: checked,
                          ...(!checked ? { auto_active_at: null, auto_inactive_at: null } : {})
                        }));
                      }}
                    />
                  }
                  label="Auto On/Off"
                />
                {quizFormData.auto_on_off ? (
                  <>
                    <Typography variant="caption" color="text.secondary">
                      Date and time use company timezone ({companyTimezone || 'UTC'}). Scheduled sync updates quiz active state from this window for RUNNING campaigns.
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <DateTimePicker
                          label="Active from"
                          value={quizFormData.auto_active_at}
                          onChange={(newValue) => setQuizFormData((prev) => ({ ...prev, auto_active_at: newValue }))}
                          sx={{ width: '100%' }}
                          slotProps={{ textField: { fullWidth: true } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <DateTimePicker
                          label="Active until"
                          value={quizFormData.auto_inactive_at}
                          onChange={(newValue) => setQuizFormData((prev) => ({ ...prev, auto_inactive_at: newValue }))}
                          sx={{ width: '100%' }}
                          slotProps={{ textField: { fullWidth: true } }}
                        />
                      </Grid>
                    </Grid>
                  </>
                ) : null}
              </Box>
            </LocalizationProvider>

            {/* Questions Section */}
            <Typography variant="h6" sx={{ mt: 2 }}>
              Questions
            </Typography>

            {quizFormData.questions.map((question, index) => (
              <Card key={index} sx={{ p: 2, border: '1px solid #e0e0e0' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1">Question {index + 1}</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => moveQuestionUp(index)}
                      disabled={index === 0}
                      title="Move question up"
                    >
                      <ArrowUpIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => moveQuestionDown(index)}
                      disabled={index === quizFormData.questions.length - 1}
                      title="Move question down"
                    >
                      <ArrowDownIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => removeQuestion(index)}
                      color="error"
                      title="Delete question"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>

                {/* Question Text with Rich Text Editor */}
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Question Text
                </Typography>
                <RichTextEditor
                  value={question.question}
                  onChange={(value) => updateQuestion(index, 'question', value)}
                  placeholder="Enter your question here..."
                  style={{
                    marginBottom: '16px'
                  }}
                />

                {/* Answer Options */}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Answer A"
                      value={question.answer_a}
                      onChange={(e) => updateQuestion(index, 'answer_a', e.target.value)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Answer B"
                      value={question.answer_b}
                      onChange={(e) => updateQuestion(index, 'answer_b', e.target.value)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Answer C (optional)"
                      value={question.answer_c}
                      onChange={(e) => updateQuestion(index, 'answer_c', e.target.value)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Answer D (optional)"
                      value={question.answer_d}
                      onChange={(e) => updateQuestion(index, 'answer_d', e.target.value)}
                      size="small"
                    />
                  </Grid>
                </Grid>

                {/* Correct Answer Selection */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    Correct Answer
                  </Typography>
                  <RadioGroup
                    row
                    value={question.correct_answer}
                    onChange={(e) => updateQuestion(index, 'correct_answer', e.target.value)}
                  >
                    <FormControlLabel value="A" control={<Radio />} label="A" />
                    <FormControlLabel value="B" control={<Radio />} label="B" />
                    {(question.answer_c || '').trim() ? (
                      <FormControlLabel value="C" control={<Radio />} label="C" />
                    ) : null}
                    {(question.answer_d || '').trim() ? (
                      <FormControlLabel value="D" control={<Radio />} label="D" />
                    ) : null}
                  </RadioGroup>
                </Box>
              </Card>
            ))}

            {/* Add Question Button */}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addQuestion}
              sx={{ alignSelf: 'flex-start' }}
            >
              Add Question
            </Button>

            {/* Error Messages */}
            {quizFormErrors.submit && (
              <Alert severity="error">
                {quizFormErrors.submit}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuizDialog(false)} disabled={submittingQuiz}>
            Cancel
          </Button>
          <Button
            onClick={handleQuizSubmit}
            variant="contained"
            disabled={submittingQuiz}
          >
            {submittingQuiz ? <CircularProgress size={20} /> : (editingQuiz ? 'Update Quiz' : 'Create Quiz')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Campaign Dialog */}
      <Dialog
        open={addCampaignOpen}
        onClose={handleAddCampaignClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{editingCampaign ? 'Edit Campaign' : 'Add New Campaign'}</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDayjs} timezone={companyTimezone || 'UTC'}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <TextField
                fullWidth
                label="Campaign Name"
                required
                value={campaignFormData.name}
                onChange={(e) => handleCampaignFormChange('name', e.target.value)}
                error={!!campaignFormErrors.name}
                helperText={campaignFormErrors.name}
              />

              <TextField
                fullWidth
                label="Campaign Header"
                placeholder="Enter campaign header text..."
                value={campaignFormData.header}
                onChange={(e) => handleCampaignFormChange('header', e.target.value)}
                error={!!campaignFormErrors.header}
                helperText={campaignFormErrors.header}
              />

              <Typography variant="caption" color="text.secondary">
                Start and end use company timezone ({companyTimezone || 'UTC'}).
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <DateTimePicker
                    label="Start Date"
                    value={campaignFormData.start_date}
                    onChange={(newValue) => handleCampaignFormChange('start_date', newValue)}
                    sx={{ width: '100%' }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!campaignFormErrors.start_date,
                        helperText: campaignFormErrors.start_date
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DateTimePicker
                    label="End Date"
                    value={campaignFormData.end_date}
                    onChange={(newValue) => handleCampaignFormChange('end_date', newValue)}
                    sx={{ width: '100%' }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!campaignFormErrors.end_date,
                        helperText: campaignFormErrors.end_date
                      }
                    }}
                  />
                </Grid>
              </Grid>

              <TextField
                fullWidth
                label="Campaign Tag"
                placeholder="e.g., #spring2024, #promo"
                value={campaignFormData.campaign_tag}
                onChange={(e) => handleCampaignFormChange('campaign_tag', e.target.value)}
                error={!!campaignFormErrors.campaign_tag}
                helperText={campaignFormErrors.campaign_tag}
              />

              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Description
                </Typography>
                <RichTextEditor
                  value={campaignFormData.description || ''}
                  onChange={(value) => handleCampaignFormChange('description', value)}
                  placeholder="Describe your campaign goals and rules..."
                  error={!!campaignFormErrors.description}
                />
                {campaignFormErrors.description && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {campaignFormErrors.description}
                  </Typography>
                )}
              </Box>

              {/* Campaign Image Upload */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                  Campaign Image
                </Typography>

                {/* Upload Button */}
                <Box sx={{ mb: 2 }}>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="campaign-image-upload"
                    type="file"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        try {
                          console.log('Starting campaign image upload...', file.name);

                          // Upload file to backend
                          const uploadFormData = new FormData();
                          uploadFormData.append('file', file);

                          const response = await fetch(`${API_URL}/api/upload/campaign-image`, {
                            method: 'POST',
                            headers: {
                              'Authorization': `Bearer ${token}`,
                            },
                            body: uploadFormData,
                          });

                          console.log('Response status:', response.status);

                          if (response.ok) {
                            const result = await response.json();
                            console.log('✅ Campaign image upload result:', result);

                            setCampaignFormData(prev => ({
                              ...prev,
                              campaign_image: result.filename,
                              campaign_image_url: result.public_url
                            }));
                          } else {
                            console.log('❌ Response not ok, status:', response.status);
                            const errorData = await response.json();
                            console.log('❌ Error data:', errorData);
                            setCampaignFormErrors({ submit: errorData.detail || 'Failed to upload campaign image' });
                          }
                        } catch (error) {
                          console.log('❌ Campaign image upload error:', error);
                          setCampaignFormErrors({ submit: 'Error uploading campaign image: ' + error.message });
                        }
                      }
                    }}
                  />
                  <label htmlFor="campaign-image-upload">
                    <Button variant="outlined" component="span" sx={{ mr: 2 }}>
                      Upload Campaign Image
                    </Button>
                  </label>
                </Box>

                {/* Manual Image Path Input */}
                <TextField
                  fullWidth
                  label="Or enter image path manually"
                  value={campaignFormData.campaign_image || ''}
                  onChange={(e) => {
                    const imagePath = e.target.value;
                    setCampaignFormData(prev => ({
                      ...prev,
                      campaign_image: imagePath,
                      campaign_image_url: imagePath ? `${CDN_URL}/${imagePath}` : ''
                    }));
                  }}
                  placeholder="e.g., campaigns/campaign-image.jpg"
                  helperText="Enter the full path to reuse an existing image"
                />

                {/* Image Preview */}
                {campaignFormData.campaign_image_url && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                      Preview:
                    </Typography>
                    <Box
                      component="img"
                      src={campaignFormData.campaign_image_url}
                      alt="Campaign preview"
                      sx={{
                        width: 200,
                        height: 120,
                        objectFit: 'cover',
                        border: '1px solid #ddd',
                        borderRadius: 1,
                        display: 'block'
                      }}
                      onError={(e) => {
                        console.log('❌ Image preview error:', e);
                        e.target.style.display = 'none';
                      }}
                    />
                  </Box>
                )}
              </Box>

              <FormControl fullWidth required error={!!campaignFormErrors.type}>
                <InputLabel>Campaign Type</InputLabel>
                <Select
                  value={campaignFormData.type}
                  onChange={(e) => handleCampaignFormChange('type', e.target.value)}
                  label="Campaign Type"
                >
                  <MenuItem value="POINTS">Points Campaign</MenuItem>
                  <MenuItem value="TARGET">Target Campaign</MenuItem>
                  {companyExtraModules.length > 0 && (
                    <MenuItem value="EXTRA">Extra Campaign</MenuItem>
                  )}
                </Select>
                {campaignFormErrors.type && (
                  <FormHelperText>{campaignFormErrors.type}</FormHelperText>
                )}
              </FormControl>

              {campaignFormData.type === 'EXTRA' && (
                <FormControl fullWidth required error={!!campaignFormErrors.company_extra_module_id}>
                  <InputLabel>Extra Module</InputLabel>
                  <Select
                    value={campaignFormData.company_extra_module_id === null ? '' : campaignFormData.company_extra_module_id}
                    onChange={(e) => handleCampaignFormChange('company_extra_module_id', e.target.value)}
                    label="Extra Module"
                  >
                    {companyExtraModules.map((module) => (
                      <MenuItem key={module.id} value={module.id}>
                        {module.extra_module?.name || `Module #${module.id}`}
                      </MenuItem>
                    ))}
                  </Select>
                  {campaignFormErrors.company_extra_module_id && (
                    <FormHelperText>{campaignFormErrors.company_extra_module_id}</FormHelperText>
                  )}
                </FormControl>
              )}

              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={campaignFormData.status}
                  onChange={(e) => handleCampaignFormChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="DRAFT">Draft</MenuItem>
                  <MenuItem value="RUNNING">Running</MenuItem>
                  <MenuItem value="FINISHED">Finished</MenuItem>
                </Select>
              </FormControl>

              <Autocomplete
                multiple
                options={availableRewards}
                getOptionLabel={(option) => option.name}
                value={availableRewards.filter(reward =>
                  campaignFormData.reward_ids.includes(reward.id)
                )}
                onChange={(_, newValue) => {
                  handleCampaignFormChange('reward_ids', newValue.map(v => v.id));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Campaign Rewards"
                    placeholder="Select rewards for this campaign"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option.name}
                      size="small"
                      {...getTagProps({ index })}
                    />
                  ))
                }
              />

              <Box>
                <Autocomplete
                  multiple
                  options={(() => {
                    // Filter dealers to only show those who have this campaign's tag in their campaigns array
                    if (!campaignFormData.campaign_tag) {
                      return dealersForCampaigns; // Show all if no tag is set
                    }
                    return dealersForCampaigns.filter(dealer =>
                      dealer.campaigns &&
                      Array.isArray(dealer.campaigns) &&
                      dealer.campaigns.includes(campaignFormData.campaign_tag)
                    );
                  })()}
                  getOptionLabel={(option) => option.name}
                  value={(() => {
                    // Filter to only show dealers that match the campaign tag
                    const filteredDealers = !campaignFormData.campaign_tag
                      ? dealersForCampaigns
                      : dealersForCampaigns.filter(dealer =>
                        dealer.campaigns &&
                        Array.isArray(dealer.campaigns) &&
                        dealer.campaigns.includes(campaignFormData.campaign_tag)
                      );
                    return filteredDealers.filter(dealer =>
                      campaignFormData.dealer_ids.includes(dealer.id)
                    );
                  })()}
                  onChange={(_, newValue) => {
                    handleCampaignFormChange('dealer_ids', newValue.map(v => v.id));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Campaign Dealers"
                      placeholder="Select dealers for this campaign"
                      helperText={campaignFormData.campaign_tag ? `Only showing dealers with campaign tag: ${campaignFormData.campaign_tag}` : "Select a campaign tag to filter dealers"}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option.name}
                        size="small"
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                />
                <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      // Filter dealers before adding all
                      const filteredDealers = !campaignFormData.campaign_tag
                        ? dealersForCampaigns
                        : dealersForCampaigns.filter(dealer =>
                          dealer.campaigns &&
                          Array.isArray(dealer.campaigns) &&
                          dealer.campaigns.includes(campaignFormData.campaign_tag)
                        );
                      handleCampaignFormChange('dealer_ids', filteredDealers.map(d => d.id));
                    }}
                    disabled={dealersForCampaigns.length === 0}
                  >
                    Add All Dealers
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      handleCampaignFormChange('dealer_ids', []);
                    }}
                    disabled={campaignFormData.dealer_ids.length === 0}
                  >
                    Clear All
                  </Button>
                </Box>
              </Box>

              {/* Dealer Performance Table Configuration */}
              <Typography variant="h6" sx={{ mt: 3, mb: 2, fontWeight: 500 }}>
                Dealer Performance Table Configuration
              </Typography>

              {/* Column Visibility Settings */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
                  Show Columns
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={campaignFormData.show_value_1}
                          onChange={(e) => handleCampaignFormChange('show_value_1', e.target.checked)}
                        />
                      }
                      label="Value 1"
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={campaignFormData.show_value_2}
                          onChange={(e) => handleCampaignFormChange('show_value_2', e.target.checked)}
                        />
                      }
                      label="Value 2"
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={campaignFormData.show_value_3}
                          onChange={(e) => handleCampaignFormChange('show_value_3', e.target.checked)}
                        />
                      }
                      label="Value 3"
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={campaignFormData.show_status}
                          onChange={(e) => handleCampaignFormChange('show_status', e.target.checked)}
                        />
                      }
                      label="Status"
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Custom Column Labels */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
                  Custom Column Labels
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Value 1 Label"
                      placeholder="e.g., Sales PRO"
                      value={campaignFormData.value_1_label}
                      onChange={(e) => handleCampaignFormChange('value_1_label', e.target.value)}
                      disabled={!campaignFormData.show_value_1}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Value 2 Label"
                      placeholder="e.g., Installations"
                      value={campaignFormData.value_2_label}
                      onChange={(e) => handleCampaignFormChange('value_2_label', e.target.value)}
                      disabled={!campaignFormData.show_value_2}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Value 3 Label"
                      placeholder="e.g., Customer Reviews"
                      value={campaignFormData.value_3_label}
                      onChange={(e) => handleCampaignFormChange('value_3_label', e.target.value)}
                      disabled={!campaignFormData.show_value_3}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ mt: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={campaignFormData.show_in_leaderboards}
                      onChange={(e) => handleCampaignFormChange('show_in_leaderboards', e.target.checked)}
                    />
                  }
                  label="Show in Leaderboards for all dealers"
                />
              </Box>

              {campaignFormErrors.submit && (
                <Alert severity="error">
                  {campaignFormErrors.submit}
                </Alert>
              )}
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddCampaignClose} disabled={submittingCampaign}>
            Cancel
          </Button>
          <Button
            onClick={handleCampaignSubmit}
            variant="contained"
            disabled={submittingCampaign}
          >
            {submittingCampaign ? <CircularProgress size={20} /> : (editingCampaign ? 'Update Campaign' : 'Create Campaign')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Dealers from Salesforce Confirmation Dialog */}
      <Dialog
        open={importDealersDialogOpen}
        onClose={() => setImportDealersDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Import Dealers from Salesforce
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Do you want to proceed with the import? This will fetch dealers from Salesforce and add them to your company.
          </Typography>
          {importDealersCampaign?.trim() && (
            <Typography variant="body2" color="text.secondary">
              Campaign filter: {importDealersCampaign.trim()}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDealersDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmImportDealers}
            variant="contained"
            disabled={importingDealers}
            startIcon={importingDealers ? <CircularProgress size={20} /> : <ImportIcon />}
          >
            {importingDealers ? 'Importing...' : 'Proceed with Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update dealers from Salesforce */}
      <Dialog
        open={updateDealersDialogOpen}
        onClose={() => !updatingDealers && setUpdateDealersDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Update dealers from Salesforce
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Fetches dealer data from Salesforce and updates only dealers whose portal user email matches the Salesforce contact email.
            Rows without an email in the API response are skipped.
          </Typography>
          {importDealersCampaign?.trim() && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Campaign filter (SMOD request): {importDealersCampaign.trim()}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
          <Button onClick={() => setUpdateDealersDialogOpen(false)} disabled={updatingDealers}>
            Cancel
          </Button>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 'auto' }}>
            <Button
              variant="contained"
              onClick={() => handleUpdateDealersFromSalesforce('all')}
              disabled={updatingDealers}
            >
              Update All
            </Button>
            <Button
              variant="outlined"
              onClick={() => handleUpdateDealersFromSalesforce('campaigns_only')}
              disabled={updatingDealers}
            >
              Update Campaign Tags
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Start Campaign Confirmation Dialog */}
      <Dialog
        open={startCampaignDialogOpen}
        onClose={handleCancelStartCampaign}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Start Campaign Confirmation
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            You are about to start the campaign "{selectedCampaignDetail?.name}". This will change the campaign status to RUNNING.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={sendStartCampaignEmails}
                onChange={(e) => setSendStartCampaignEmails(e.target.checked)}
                color="primary"
              />
            }
            label="Send Start Campaign emails to all dealers"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelStartCampaign}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmStartCampaign}
            variant="contained"
            disabled={updatingCampaignStatus}
            startIcon={updatingCampaignStatus ? <CircularProgress size={20} /> : null}
            sx={{
              backgroundColor: '#4caf50',
              '&:hover': {
                backgroundColor: '#45a049',
              },
            }}
          >
            {updatingCampaignStatus ? 'Starting...' : 'START'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Dealers Added - Email Confirmation Dialog (Edit Campaign, RUNNING, new dealers) */}
      <Dialog
        open={newDealersEmailDialogOpen}
        onClose={handleCancelNewDealersEmailDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>New Dealers Added</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            The following dealers have been added to this campaign:
          </Typography>
          <List dense sx={{ mb: 2 }}>
            {pendingNewDealerIds.map((dealerId) => {
              const dealer = dealersForCampaigns.find(d => d.id === dealerId);
              return (
                <ListItem key={dealerId}>
                  <ListItemText primary={dealer ? dealer.name : `Dealer #${dealerId}`} />
                </ListItem>
              );
            })}
          </List>
          <FormControlLabel
            control={
              <Checkbox
                checked={sendEmailsToNewDealers}
                onChange={(e) => setSendEmailsToNewDealers(e.target.checked)}
                color="primary"
              />
            }
            label="Send Start Campaign emails to these new dealers"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelNewDealersEmailDialog}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmNewDealersEmailDialog}
            variant="contained"
            disabled={submittingCampaign}
            startIcon={submittingCampaign ? <CircularProgress size={20} /> : null}
          >
            {submittingCampaign ? 'Updating...' : 'Update Campaign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Campaign Confirmation Dialog */}
      {campaignToDelete && (
        <DeleteCampaignDialog
          open={deleteCampaignDialog}
          onClose={handleDeleteCampaignClose}
          campaign={campaignToDelete}
          onSuccess={handleDeleteCampaignSuccess}
        />
      )}

      {/* Tremendous Campaign Setup Dialog */}
      <Dialog
        open={showTremendousCampaignDialog}
        onClose={() => {
          setShowTremendousCampaignDialog(false);
          setTremendousCampaignName('');
          setTremendousCampaignDescription('');
          setTremendousCampaignProducts([]);
          setTremendousProductsError('');
          setError('');
          productsLoadAttemptedRef.current = false;
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Tremendous Campaign Setup</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Campaign Name"
            type="text"
            fullWidth
            variant="outlined"
            value={tremendousCampaignName}
            onChange={(e) => setTremendousCampaignName(e.target.value)}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            type="text"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={tremendousCampaignDescription}
            onChange={(e) => setTremendousCampaignDescription(e.target.value)}
            sx={{ mb: 2 }}
            helperText="Optional: A description of the campaign's purpose"
          />
          {loadingTremendousProducts ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : tremendousProducts.length > 0 ? (
            <Box>
              <Autocomplete
                multiple
                options={tremendousProducts}
                getOptionLabel={(option) => option.name || option.id || ''}
                value={tremendousProducts.filter(p => tremendousCampaignProducts.includes(p.id))}
                onChange={(event, newValue) => {
                  setTremendousCampaignProducts(newValue.map(p => p.id));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="dense"
                    label="Products"
                    variant="outlined"
                    helperText={`Optional: Select products available in this campaign (${tremendousCampaignProducts.length} selected)`}
                    sx={{ mb: 2 }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id}>
                    <Box>
                      <Typography variant="body1">{option.name || option.id}</Typography>
                      {option.category && (
                        <Typography variant="caption" color="text.secondary">
                          {option.category}
                          {option.subcategory && ` • ${option.subcategory}`}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
              />
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleAddVISACards}
                  sx={{ flex: 1 }}
                >
                  VISA cards
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleAddAllGiftCards}
                  sx={{ flex: 1 }}
                >
                  All gift cards
                </Button>
              </Box>
              {tremendousCampaignProducts.length > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {tremendousCampaignProducts.length} product{tremendousCampaignProducts.length !== 1 ? 's' : ''} selected
                </Typography>
              )}
            </Box>
          ) : (
            <Box>
              {tremendousProductsError && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {tremendousProductsError}
                </Alert>
              )}
              <TextField
                margin="dense"
                label="Product IDs"
                type="text"
                fullWidth
                variant="outlined"
                value={tremendousCampaignProducts.join(', ')}
                onChange={(e) => {
                  const ids = e.target.value.split(',').map(p => p.trim()).filter(p => p);
                  setTremendousCampaignProducts(ids);
                }}
                sx={{ mb: 2 }}
                helperText="Optional: Comma-separated list of product IDs (e.g., PRODUCT1, PRODUCT2, PRODUCT3)"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowTremendousCampaignDialog(false);
              setTremendousCampaignName('');
              setTremendousCampaignDescription('');
              setTremendousCampaignProducts([]);
              setTremendousProductsError('');
              setError('');
              productsLoadAttemptedRef.current = false;
            }}
            disabled={creatingTremendousCampaign}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateTremendousCampaign}
            variant="contained"
            disabled={creatingTremendousCampaign || !tremendousCampaignName.trim()}
            startIcon={creatingTremendousCampaign ? <CircularProgress size={20} /> : null}
          >
            {creatingTremendousCampaign ? 'Creating...' : 'Create Campaign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Communication Details Dialog */}
      <Dialog
        open={commDetailsDialogOpen}
        onClose={() => setCommDetailsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Communication Details: {selectedCommForDetails?.title}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl size="small" sx={{ width: 250 }}>
                <InputLabel>Event Type Filter</InputLabel>
                <Select
                  value={commEventsFilter}
                  label="Event Type Filter"
                  onChange={(e) => {
                    setCommEventsFilter(e.target.value);
                    setCommEventsPage(0);
                  }}
                >
                  <MenuItem value="ALL">All Events</MenuItem>
                  <MenuItem value="PROCESSED">Processed</MenuItem>
                  <MenuItem value="DELIVERED">Delivered</MenuItem>
                  <MenuItem value="OPEN">Open</MenuItem>
                  <MenuItem value="CLICK">Click</MenuItem>
                  <MenuItem value="BOUNCE">Bounce</MenuItem>
                  <MenuItem value="DEFERRED">Deferred</MenuItem>
                  <MenuItem value="DROPPED">Dropped</MenuItem>
                  <MenuItem value="BLOCKED">Blocked</MenuItem>
                  <MenuItem value="SPAMREPORT">Spam Report</MenuItem>
                  <MenuItem value="UNSUBSCRIBE">Unsubscribe</MenuItem>
                </Select>
              </FormControl>
              <TextField 
                size="small"
                label="Search by Email"
                variant="outlined"
                value={commEventsSearch}
                onChange={(e) => {
                  setCommEventsSearch(e.target.value);
                  setCommEventsPage(0);
                }}
                sx={{ width: 250 }}
              />
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={commEventsLatestOnly} 
                    onChange={(e) => {
                      setCommEventsLatestOnly(e.target.checked);
                      setCommEventsPage(0);
                    }}
                    color="primary"
                  />
                }
                label="Latest Stats"
              />
            </Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => loadCommunicationEvents()}
              disabled={loadingCommEvents}
            >
              Refresh
            </Button>
          </Box>

          {loadingCommEvents ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sortDirection={commEventsSortBy === 'timestamp' ? commEventsSortOrder : false}>
                        <TableSortLabel
                          active={commEventsSortBy === 'timestamp'}
                          direction={commEventsSortBy === 'timestamp' ? commEventsSortOrder : 'asc'}
                          onClick={() => handleCommEventsSort('timestamp')}
                        >
                          Timestamp
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={commEventsSortBy === 'email' ? commEventsSortOrder : false}>
                        <TableSortLabel
                          active={commEventsSortBy === 'email'}
                          direction={commEventsSortBy === 'email' ? commEventsSortOrder : 'asc'}
                          onClick={() => handleCommEventsSort('email')}
                        >
                          Email
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Template Type</TableCell>
                      <TableCell sortDirection={commEventsSortBy === 'event_type' ? commEventsSortOrder : false}>
                        <TableSortLabel
                          active={commEventsSortBy === 'event_type'}
                          direction={commEventsSortBy === 'event_type' ? commEventsSortOrder : 'asc'}
                          onClick={() => handleCommEventsSort('event_type')}
                        >
                          Event Type
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={commEventsSortBy === 'reason' || commEventsSortBy === 'url' ? commEventsSortOrder : false}>
                        <TableSortLabel
                          active={commEventsSortBy === 'reason' || commEventsSortBy === 'url'}
                          direction={commEventsSortBy === 'reason' || commEventsSortBy === 'url' ? commEventsSortOrder : 'asc'}
                          onClick={() => handleCommEventsSort('reason')}
                        >
                          Reason / URL
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {commEvents.length > 0 ? commEvents.map((event) => (
                      <TableRow key={event.id}>
                        <TableCell>{dayjs.tz(event.timestamp, 'UTC').tz(companyTimezone).format(dateFormatForDayjs)}</TableCell>
                        <TableCell>{event.email}</TableCell>
                        <TableCell>
                          {(event.effective_template_type || selectedCommForDetails?.template_type)
                            ? getTemplateTypeLabel(event.effective_template_type || selectedCommForDetails.template_type)
                            : '—'}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={event.event_type.toUpperCase()} 
                            size="small" 
                            color={
                              event.event_type === 'delivered' || event.event_type === 'open' || event.event_type === 'click' ? 'success' :
                              event.event_type === 'processed' ? 'info' :
                              event.event_type === 'bounce' || event.event_type === 'dropped' || event.event_type === 'blocked' ? 'error' : 'default'
                            }
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 300 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexGrow: 1 }}>
                              {event.event_type === 'click' ? (
                                <a href={event.url} target="_blank" rel="noopener noreferrer">{event.url}</a>
                              ) : (
                                event.reason || '-'
                              )}
                            </Box>
                            {(event.url || event.reason) && (
                              <IconButton 
                                size="small" 
                                onClick={() => {
                                  const textToCopy = event.event_type === 'click' ? event.url : event.reason;
                                  if (textToCopy) {
                                    navigator.clipboard.writeText(textToCopy);
                                  }
                                }}
                                title="Copy to clipboard"
                                sx={{ color: companyBrandingColor || '#1976d2' }}
                              >
                                <ContentCopyIcon sx={{ fontSize: '1rem' }} />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center">No events found.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={commEventsTotal}
                page={commEventsPage}
                onPageChange={handleCommEventsChangePage}
                rowsPerPage={commEventsRowsPerPage}
                onRowsPerPageChange={handleCommEventsChangeRows}
                rowsPerPageOptions={[10, 25, 50, 100, 200]}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>



    </Box>
  );
};

export default Manufacturer; 